import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { db, auth } from "../../backend/firebaseConfig";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, setDoc, onSnapshot, getDoc, collection, getDocs, updateDoc, deleteField, runTransaction, Timestamp } from "firebase/firestore";
import Modal from "react-modal";
import { Link, useNavigate, useLocation } from "react-router-dom";
import ServicesList from "../../components/UI/organisms/list/ServicesList";
import PatientInsuranceForm from "./PatientInsuranceForm";
import { addNotification } from "../../components/functions/notifyComp";
import { PLACEHOLDERS } from "../../utils/placeholders"; 
import { redirectIfNotLoggedIn } from "../../components/functions/redirectIfNotLoggedIn";
import {
  CHAIR_SWAPS_COLLECTION,
  DEFAULT_CHAIR_SETTINGS,
  subscribeToChairSettings,
  shouldDefaultUnavailableByLegacySetup,
  isSlotEffectivelyUnavailableForDentist,
  isChairCapacityConfigured,
  isDentistOnRoster,
  normalizeChairSwaps,
  getSwappedInBenchUids,
  getBenchSwapSlots,
  getBenchPartnerRosterUidsForDay,
  getLinkedRosterForBenchSlot,
  getLinkedRosterUidsForBenchRange,
  getRosterPartnerBenchUidsForDay,
  parseSlotLabelToRange,
  getBenchSwapCoverageRanges,
  buildStandardHalfHourSlotLabels,
} from "../../utils/chairScheduling";
import {
  applyTimelineShiftUpdatesToRows,
  appointmentBelongsToDateDoc,
  computeCompressibleTimelineShiftUpdates,
  computeDayTimelineCompressionShiftUpdates,
  forEachTimelineGapSlot,
  getAppointmentDisplayRangeMinutes,
  isCompressiblePendingStatus,
  mergeContiguousBookedTimelineRows,
  normalizeAppointmentDateKey,
  parseUnavailableLabelsToMinuteRanges,
  withAppointmentDocDate,
} from "../../utils/appointmentTimeDisplay";
import {
  applyCompressibleTimelineShiftsAndNotify,
} from "../../utils/appointmentTimeAdjustmentNotifications";
import {
  areAllChairsOccupiedAtRange,
  assignAppointmentsToChairsForBooking,
  dentistHasPlottedSchedule,
  getChairBlockSeverityAtRange,
  getChairBlockSeverityForChair,
  getDentistScheduleEntry,
  getDentistScheduleUnavailableRanges,
  getPlottedScheduleRangesForDentist,
  isChairOccupiedAtRange,
  isRangeWithinPlottedSchedule,
  isAnyChairFreeAtRange,
  isChairScheduleActiveForDate,
  isDentistSchedulePlottingEnabled,
  resolveChairCountForDate,
  subscribeToChairScheduleForDate,
  subscribeToDentistSchedulesForDate,
  planPatientBookingOnChairPlot,
  validatePatientBookingDurationOnChairPlot,
} from "../../utils/dentistSchedulePlots";

// Imports MUI Components
import { 
  Box,
  Button,
  Stack,
  Typography,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails, 
  Divider,
  AvatarGroup,
  Avatar,
  Pagination,
  Tooltip,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  ListItemText,
  Checkbox,
  OutlinedInput,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  ListSubheader,
  Badge,
  Snackbar,
  Alert
} from "@mui/material"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import CloseIcon from "@mui/icons-material/Close"
import CheckIcon from "@mui/icons-material/Check";
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DemoContainer, DemoItem } from '@mui/x-date-pickers/internals/demo';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { DayCalendarSkeleton } from '@mui/x-date-pickers/DayCalendarSkeleton';
import Drawer from '@mui/material/Drawer';
import { useTheme } from "@mui/material/styles";
import SplashScreen from "../../components/UI/atoms/splash/SplashScreen";
import MedicalHistory from "../../components/UI/organisms/history/MedicalHistory"
import MobileStepper from '@mui/material/MobileStepper';
import Paper from '@mui/material/Paper';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';


  
import Header from "../../components/UI/organisms/header/logged-in/HeaderLoggedIn"
import Text from "../../components/UI/atoms/text/Text";
import Icon from "../../components/UI/atoms/icon/Icon";
import AlertShortMessage from "../../components/UI/molecules/alert/AlertShortMessage";

import "./styles.css"
import ButtonComp from "../../components/UI/atoms/button/Button";
import { hover } from "framer-motion";
import { CheckBox } from "@mui/icons-material";

Modal.setAppElement("#root");

function formatDateMMDDYYYY(dateStr) {
  if (!dateStr) return "";
  const [year, month, day] = dateStr.split("-");
  return `${month}-${day}-${year}`;
}

function formatDateLong(dateStr) {
  if (!dateStr) return "";
  const [year, month, day] = dateStr.split("-");
  const date = new Date(year, month - 1, day);
  const monthNames = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];
  return `${monthNames[date.getMonth()]} ${day}, ${year}`;
}

/** Patient-friendly clock label: strip leading zeros on the hour (e.g. 09:30 -> 9:30). */
function formatClockTimeNoLeadingHourZero(timePart) {
  if (timePart == null) return "";
  const s = String(timePart).trim();
  if (!s) return "";
  const m = s.match(/^0*(\d{1,2}):(\d{2})(?:\s*(AM|PM))?$/i);
  if (!m) return s;
  const h = Number(m[1]);
  const mm = m[2];
  const ap = m[3] ? ` ${m[3].toUpperCase()}` : "";
  return `${h}:${mm}${ap}`;
}

/** Normalize a single clock for modal display: 24h "13:30" → "1:30 PM"; keeps existing AM/PM strings. */
function formatModalClockToDisplay(timePart) {
  if (timePart == null) return "";
  const s = String(timePart).trim();
  if (!s) return "";
  if (/\b(AM|PM)\b/i.test(s)) {
    return formatClockTimeNoLeadingHourZero(s);
  }
  const m24 = s.match(/^(\d{1,2}):(\d{2})$/);
  if (m24) {
    let h = Number(m24[1]);
    const mm = m24[2];
    if (!Number.isFinite(h) || h > 23 || Number(mm) > 59) return formatClockTimeNoLeadingHourZero(s);
    const ampm = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 || 12;
    return `${h12}:${mm} ${ampm}`;
  }
  return formatClockTimeNoLeadingHourZero(s);
}

function formatExistingAppointmentModalTime(existing) {
  if (!existing) return "N/A";
  if (existing.startTime && existing.endTime) {
    return `${formatModalClockToDisplay(existing.startTime)} - ${formatModalClockToDisplay(
      existing.endTime
    )}`;
  }
  if (existing.time) {
    const combined = String(existing.time).trim();
    const parts = combined.split(/\s*-\s*/);
    if (parts.length === 2) {
      return `${formatModalClockToDisplay(parts[0])} - ${formatModalClockToDisplay(parts[1])}`;
    }
    return formatModalClockToDisplay(combined);
  }
  return "N/A";
}

function getLocalDateKey(date = new Date()) {
  const localDate = new Date(date);
  localDate.setHours(0, 0, 0, 0);
  const year = localDate.getFullYear();
  const month = String(localDate.getMonth() + 1).padStart(2, "0");
  const day = String(localDate.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

const BOOKING_DENTIST_UNSELECTABLE_TEXT = "#8A8278";
const BOOKING_DENTIST_UNSELECTABLE_BG = "rgba(89, 84, 74, 0.08)";

const BOOKING_DENTIST_MENU_ITEM_DISABLED_SX = {
  backgroundColor: BOOKING_DENTIST_UNSELECTABLE_BG,
  cursor: "not-allowed",
  "&.Mui-disabled": {
    opacity: 1,
    color: BOOKING_DENTIST_UNSELECTABLE_TEXT,
    WebkitTextFillColor: BOOKING_DENTIST_UNSELECTABLE_TEXT,
  },
};

const BOOKING_DENTIST_MENU_DISABLED_LIST_SX = {
  opacity: 1,
  color: BOOKING_DENTIST_UNSELECTABLE_TEXT,
  backgroundColor: BOOKING_DENTIST_UNSELECTABLE_BG,
};

function normalizeFirestoreDateKey(raw) {
  return normalizeAppointmentDateKey(raw);
}

const buildNotificationWindow = (dateLabel, startTime, endTime) => {
  const safeStart = startTime || "N/A";
  const safeEnd = endTime || "N/A";
  return `${dateLabel} (${safeStart} - ${safeEnd})`;
};

function formatContactInfo(userDetails) {
  if (!userDetails) return "";
  
  // If both email and contact number are provided, show email (priority)
  if (userDetails.email && userDetails.contactNumber) {
    return userDetails.email;
  }
  
  // If only email is provided, show email
  if (userDetails.email) {
    return userDetails.email;
  }
  
  // If only contact number is provided, format it in Philippine format
  if (userDetails.contactNumber) {
    const contact = userDetails.contactNumber.toString();
    // Convert 09XXXXXXXXX to 0962-233-9466 format
    if (contact.length === 11 && contact.startsWith('09')) {
      return `${contact.slice(0, 4)}-${contact.slice(4, 7)}-${contact.slice(7)}`;
    }
    return contact; // Return as-is if format is unexpected
  }
  
  return "No contact info";
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};


const steps = [
  {
    label: 'Review Appointment Details',
    description: `For each ad campaign that you create, you can control how much
              you're willing to spend on clicks and conversions, which networks
              and geographical locations you want your ads to show on, and more.`,
  },
  {
    label: 'Add Insurance',
    description:
      'An ad group contains one or more ads which target a shared set of keywords.',
  },
  {
    label: 'Terms of Service Agreement',
    description: `Try out different ad text to see what brings in the most customers,
              and learn how to enhance your ads using features like ad extensions.
              If you run into any problems with your ads, find out how to tell if
              they're running and how to resolve approval issues.`,
  },
];

const label = [
  'Review Details',
  'Insurance',
  'Terms of Service',
];

const names = [
  'Oliver Hansen',
  'Van Henry',
  'April Tucker',
  'Ralph Hubbard',
  'Omar Alexander',
  'Carlos Abbott',
  'Miriam Wagner',
  'Bradley Wilkerson',
  'Virginia Andrews',
  'Kelly Snyder',
];

const sports = [
  'Football',
  'Soccer',
  'Basketball',
  'Food',
];

// Default services list (fallback)
export const servicesList = [
  // BASIC SERVICES
  { name: "Oral Prophylaxis (Cleaning)", price: "₱1,000-3,000", category: "Basic Services" },
  { name: "Temporary Filling (pasta)", price: "₱900", category: "Basic Services" },
  { name: "Permanent Filling", price: "₱1,000/surface", category: "Basic Services" },
  { name: "Fluoride Treatment", price: "₱1,500", category: "Basic Services" },
  { name: "Consultation", price: "₱500", category: "Basic Services" },

  // DENTURE (POSTISO)
  { name: "Partial Denture (acrylic)", price: "starts at ₱5,000", category: "Denture" },
  { name: "Partial Denture (flexible)", price: "₱10,000/quadrant", category: "Denture" },
  { name: "Full Denture (acrylic)", price: "₱15,000/arch", category: "Denture" },
  { name: "Full Denture (high impact)", price: "₱25,000/arch", category: "Denture" },
  { name: "Full Denture (DG-CRYL)", price: "₱25,000/arch", category: "Denture" },
  { name: "Reline (pahugot)", price: "₱2,500", category: "Denture Repair" },
  { name: "Rebase (replace whole base sa postiso)", price: "₱4,000", category: "Denture Repair" },

  // EXTRACTION & SURGERY
  { name: "Simple tooth extraction", price: "₱1,500/tooth", category: "Extraction & Surgery" },
  { name: "Complicated Extraction", price: "₱2,000-3,500/tooth", category: "Extraction & Surgery" },
  { name: "Erupted Wisdom Tooth", price: "₱10,000-20,000/tooth", category: "Extraction & Surgery" },
  { name: "Gingivectomy", price: "₱2,500/area, ₱10,000/arch", category: "Extraction & Surgery" },
  { name: "Operculectomy", price: "₱2,500-5,000", category: "Extraction & Surgery" },

  // ORTHODONTICS (BRACES)
  { name: "Conventional/standard", price: "₱65,000-95,000", category: "Orthodontics" },
  { name: "Conventional ceramic", price: "₱80,000", category: "Orthodontics" },
  { name: "Self Ligating Braces", price: "₱90,000-95,000", category: "Orthodontics" },
  { name: "Q Aligner (mild)", price: "₱170,000", category: "Orthodontics" },
  { name: "Q Aligner (moderate)", price: "₱200,000", category: "Orthodontics" },
  { name: "Q Aligner (severe)", price: "₱220,000", category: "Orthodontics" },
  { name: "Bracket/Buccal Tube", price: "₱1,500", category: "Orthodontics" },
  { name: "Replacement (recement)", price: "₱800", category: "Orthodontics" },
  { name: "Replacement (bracket)", price: "₱500/bracket", category: "Orthodontics" },
  { name: "Removal of brackets", price: "₱1,500/arch", category: "Orthodontics" },

  // COSMETIC
  { name: "Composite Veneers", price: "₱4,500/tooth", category: "Cosmetic" },
  { name: "Emax Veneer/Crown", price: "₱16,000/tooth", category: "Cosmetic" },
  { name: "Zirconia Veneer/Crown", price: "₱16,000/tooth", category: "Cosmetic" },
  { name: "Porcelain fused to metal", price: "₱10,000/tooth", category: "Cosmetic" },
  { name: "Porcelain fused to zirconia", price: "₱16,000/tooth", category: "Cosmetic" },
  { name: "Acrylic Crown (plastic)", price: "₱6,000/tooth", category: "Cosmetic" },
  { name: "Cosmetic per side", price: "₱3,500/side", category: "Cosmetic" },

  // RETAINER
  { name: "Essix", price: "₱6,500/arch", category: "Retainer" },
  { name: "Hawley's", price: "₱6,500/arch", category: "Retainer" },
  { name: "Lingual Retainer", price: "₱6,500/arch", category: "Retainer" },

  // TMD TREATMENT
  { name: "EF", price: "₱30,000 (mild case)", category: "TMD Treatment" },
  { name: "SPLINT", price: "₱15,000", category: "TMD Treatment" },
  { name: "Teeth Whitening", price: "₱12,000", category: "TMD Treatment" },
];




function getRandomNumber(min, max) {
  return Math.round(Math.random() * (max - min) + min);
}

const initialValue = dayjs('2022-04-17');


function fakeFetch(date, { signal }) {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      const daysInMonth = date.daysInMonth();
      const daysToHighlight = [1, 2, 3].map(() => getRandomNumber(1, daysInMonth));

      resolve({ daysToHighlight });
    }, 500);

    signal.onabort = () => {
      clearTimeout(timeout);
      reject(new DOMException('aborted', 'AbortError'));
    };
  });
}

/** Pending / new / reserved — shared UI grouping (counts, icons). */
function isPendingLikeStatus(status = "") {
  const n = (status || "").toString().trim().toLowerCase();
  return n === "pending" || n === "new" || n === "reserved";
}

function isCompletedLikeStatus(status = "") {
  const n = (status || "").toString().trim().toLowerCase();
  return n === "completed";
}

const PatientAppointmentBooking = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());

  const requestAbortController = React.useRef(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [highlightedDays, setHighlightedDays] = React.useState([1, 2, 15]);
  
  const [selectedDateAppointment, setSelectedDateAppointment] = useState(dayjs());
  const [showBookingSummary, setShowBookingSummary] = useState(false);
  const [showExistingAppointmentModal, setShowExistingAppointmentModal] = useState(false);
  const [existingAppointment, setExistingAppointment] = useState(null);
  
  // Sync selectedDate with selectedDateAppointment
  useEffect(() => {
    if (selectedDateAppointment) {
      setSelectedDate(selectedDateAppointment.toDate());
    }
  }, [selectedDateAppointment]);
  
  // Custom onChange handler to prevent past date selection but allow navigation
  const handleDateChange = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Only set the date if it's not in the past
    if (date >= today) {
      setSelectedDate(date);
      setSelectedDateAppointment(dayjs(date));
      setSelectedTimeSlot(null);
    }
  };

  // Handler for StaticDatePicker onChange (receives dayjs object)
  const handleDatePickerChange = (date) => {
    if (!date) return;
    
    const today = dayjs().startOf('day');
    const selectedDate = date.startOf('day');
    
    // Only set the date if it's not in the past
    if (selectedDate.isSame(today) || selectedDate.isAfter(today)) {
      setSelectedDateAppointment(date);
      setSelectedTimeSlot(null);
    } else {
      // If trying to select a past date, reset to today
      setSelectedDateAppointment(today);
      setSelectedTimeSlot(null);
    }
  };
  const [selectedServices, setSelectedServices] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [bookingStatus, setBookingStatus] = useState("");
  const [showBookingSnackbar, setShowBookingSnackbar] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success"); // "success" or "error"
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const previousSelectedSlotIdRef = useRef(null);
  const [dentists, setDentists] = useState([]);
  const [closedDays, setClosedDays] = useState([]);
  const [unavailableTimeSlots, setUnavailableTimeSlots] = useState([]);
  const [hasSelectedDentistUnavailableRecord, setHasSelectedDentistUnavailableRecord] = useState(false);
  const [selectedDentist, setSelectedDentist] = useState(null);
  const [chairSettings, setChairSettings] = useState({ ...DEFAULT_CHAIR_SETTINGS });
  const [chairScheduleForDate, setChairScheduleForDate] = useState(null);
  const [unavailableSlotsByDentistForDate, setUnavailableSlotsByDentistForDate] = useState({});
  const [chairSwapsBySlotForDate, setChairSwapsBySlotForDate] = useState({});
  const [dentistSchedulesForDate, setDentistSchedulesForDate] = useState({});
  const [timeSlots, setTimeSlots] = useState([]);
  const [showInsuranceModal, setShowInsuranceModal] = useState(false);
  const [insuranceAgreement, setInsuranceAgreement] = useState(false);
  const [insuranceNone, setInsuranceNone] = useState(false);
  const [insuranceChoice, setInsuranceChoice] = useState(null);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsAgreement, setTermsAgreement] = useState(false);
  const [showInsuranceForm, setShowInsuranceForm] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [tempInsuranceDetails, setTempInsuranceDetails] = useState(null); // New state for temporary insurance details
  const [isBookingLoading, setIsBookingLoading] = useState(false);
  const [isYesButtonLoading, setIsYesButtonLoading] = useState(false);
  const [isNoButtonLoading, setIsNoButtonLoading] = useState(false);
  const [isProceedButtonLoading, setIsProceedButtonLoading] = useState(false);
  const [isTermsAgreeLoading, setIsTermsAgreeLoading] = useState(false);

  const [showCustomAlert, setShowCustomAlert] = useState(false);
  const [showInsuranceAlert, setShowInsuranceAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertTitle, setAlertTitle] = useState("");
  const [alertImage, setAlertImage] = useState("");
const [alertButton, setAlertButton] = useState("");
const [alertButtonType, setAlertButtonType] = useState("false");
const [alertLink, setAlertLink] = useState("");
  const [showMedicalHistoryModal, setShowMedicalHistoryModal] = useState(false);
const [showMedicalHistory, setShowMedicalHistory] = useState(false);
const medicalHistoryRef = useRef(null);
  const [showInsuranceFormModal, setShowInsuranceFormModal] = useState(false);
  const [serverTime, setServerTime] = useState(new Date());

  // Shared day-slot builder (9:00 AM - 6:00 PM, 30-minute intervals) used by
  // chair-aware visibility checks and fallback unavailable initialization.
  const buildClinicDaySlots = () => {
    const allTimeSlots = [];
    for (let hour = 9; hour < 18; hour++) {
      for (let minutes = 0; minutes < 60; minutes += 30) {
        const startMinutes = hour * 60 + minutes;
        const endMinutes = startMinutes + 30;
        if (endMinutes > 18 * 60) continue;

        const startHours = Math.floor(startMinutes / 60);
        const startMins = startMinutes % 60;
        const startAmpm = startHours >= 12 ? "PM" : "AM";
        const startDisplayHours = startHours % 12 || 12;
        const startTimeStr = `${startDisplayHours}:${startMins
          .toString()
          .padStart(2, "0")} ${startAmpm}`;

        const endHours = Math.floor(endMinutes / 60);
        const endMins = endMinutes % 60;
        const endAmpm = endHours >= 12 ? "PM" : "AM";
        const endDisplayHours = endHours % 12 || 12;
        const endTimeStr = `${endDisplayHours}:${endMins
          .toString()
          .padStart(2, "0")} ${endAmpm}`;

        allTimeSlots.push(`${startTimeStr} - ${endTimeStr}`);
      }
    }
    return allTimeSlots;
  };

const [isHovered, setIsHovered] = useState(false);

  const [showTooltipConfirm, setShowTooltipConfirm] = React.useState(false);

  const [name, setName] = useState(""); 
  const [companyName, setCompanyName] = useState(""); 
  const [contactNumber, setContactNumber] = useState(""); 
  const [hmoCard, setHmoCard] = useState(""); 
  const [hmoAccountNumber, setHmoAccountNumber] = useState(""); 
  const [validGovernmentID, setValidGovernmentID] = useState(""); 
  const [validGovernmentIDNumber, setValidGovernmentIDNumber] = useState(""); 
  const [birthdate, setBirthdate] = useState(""); 
  const [relationship, setRelationship] = useState(""); 

  // Validation states
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  
  // Loading states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGoingBack, setIsGoingBack] = useState(false);

  // Auto-populate insurance form fields from user's personal information
  useEffect(() => {
    if (userDetails) {
      // Populate patient name if not already filled (first + middle + last name)
      if (!name) {
        let fullName = "";
        if (userDetails.firstName || userDetails.lastName) {
          const nameParts = [
            userDetails.firstName,
            userDetails.middleName,
            userDetails.lastName
          ].filter(Boolean); // Remove empty/null/undefined values
          fullName = nameParts.join(" ").trim();
        } else {
          fullName = userDetails.name || "";
        }
        if (fullName) setName(fullName);
      }
      
      // Populate contact number only if user has one and field is not already filled
      if (!contactNumber && userDetails.contactNumber) {
        setContactNumber(userDetails.contactNumber);
      }
      
      // Populate birthdate if not already filled (convert from YYYY-MM-DD to MM-DD-YYYY)
      if (!birthdate) {
        const bdate = userDetails.birthDate || userDetails.birthdate || "";
        if (bdate && bdate.match(/^\d{4}-\d{2}-\d{2}$/)) {
          // Convert from YYYY-MM-DD to MM-DD-YYYY
          const [year, month, day] = bdate.split("-");
          setBirthdate(`${month}-${day}-${year}`);
        } else if (bdate) {
          // If already in MM-DD-YYYY format or other format, use as is
          setBirthdate(bdate);
        }
      }
    }
  }, [userDetails]);

  // Validation function
  const validateField = (fieldName, value) => {
    switch (fieldName) {
      case 'name':
        if (!value.trim()) return 'Patient name is required';
        return '';
      case 'companyName':
        if (!value.trim()) return 'Insurance company is required';
        return '';
      case 'contactNumber':
        if (!value.trim()) return 'Mobile number is required';
        // Remove all non-digit characters for validation
        const digitsOnly = value.replace(/\D/g, '');
        // Check if it starts with '09' first
        if (digitsOnly.length > 0 && !digitsOnly.startsWith('09')) {
          return 'Mobile number must start with \'09\'';
        }
        // Must be exactly 11 digits
        if (digitsOnly.length > 0 && digitsOnly.length !== 11) {
          return 'Mobile number must be 11 digits (e.g., 09XXXXXXXXX)';
        }
        return '';
      case 'hmoCard':
        if (!value.trim()) return 'HMO card is required';
        return '';
      case 'hmoAccountNumber':
        if (!value.trim()) return 'HMO account number is required';
        return '';
      case 'validGovernmentID':
        if (!value.trim()) return 'Government ID type is required';
        return '';
      case 'validGovernmentIDNumber':
        if (!value.trim()) return 'Government ID number is required';
        return '';
      case 'birthdate':
        if (!value.trim()) return 'Birthdate is required';
        // Validate MM-DD-YYYY format
        const dateRegex = /^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])-(\d{4})$/;
        if (!dateRegex.test(value)) {
          return 'Birthdate must be in MM-DD-YYYY format';
        }
        // Parse MM-DD-YYYY to check if it's a valid date and not in the future
        const parts = value.split('-');
        const month = parseInt(parts[0], 10);
        const day = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);
        const dateObj = new Date(year, month - 1, day);
        
        // Check if the date is valid (handles invalid dates like 02-31-2025)
        if (dateObj.getMonth() + 1 !== month || dateObj.getDate() !== day || dateObj.getFullYear() !== year) {
          return 'Please enter a valid date';
        }
        
        if (dateObj > new Date()) {
          return 'Birthdate must not be in the future';
        }
        return '';
      case 'relationship':
        if (!value.trim()) return 'Relationship is required';
        return '';
      default:
        return '';
    }
  };

  // Handle field blur (when user leaves a field)
  const handleBlur = (fieldName) => {
    setTouched(prev => ({ ...prev, [fieldName]: true }));
    const value = getFieldValue(fieldName);
    const error = validateField(fieldName, value);
    setErrors(prev => ({ ...prev, [fieldName]: error }));
  };

  // Handle field change
  const handleFieldChange = (fieldName, value) => {
    // Update the corresponding state
    switch (fieldName) {
      case 'name':
        if (/^[a-zA-Z\s]*$/.test(value)) {
          setName(value.replace(/\b\w/g, (char) => char.toUpperCase()));
        }
        break;
      case 'companyName':
        setCompanyName(value);
        break;
      case 'contactNumber':
        // Only allow digits - mobile number must be 11 digits starting with '09'
        if (/^\d*$/.test(value)) {
          // Limit to 11 digits
          const digitsOnly = value.replace(/\D/g, '').slice(0, 11);
          setContactNumber(digitsOnly);
        }
        break;
      case 'hmoCard':
        // Allow all characters, special characters, and numbers (max 50)
        setHmoCard(value);
        break;
      case 'hmoAccountNumber':
        // Allow all characters, special characters, and numbers (max 50)
        setHmoAccountNumber(value);
        break;
      case 'validGovernmentID':
        setValidGovernmentID(value);
        break;
      case 'validGovernmentIDNumber':
        // Allow all characters including special characters, letters, numbers, spaces, etc.
        // Government ID numbers can have various formats like:
        // - Numbers only: 123456789
        // - Letters and numbers: ABC123456
        // - With dashes: 123-456-789
        // - With spaces: 123 456 789
        // - With special characters: 123/456/789, 123.456.789, etc.
        setValidGovernmentIDNumber(value);
        break;
      case 'birthdate':
        // Value is already formatted as MM-DD-YYYY from the input's onChange
        setBirthdate(value);
        break;
      case 'relationship':
        setRelationship(value);
        break;
      default:
        break;
    }
    
    // Clear error if field is touched and valid
    if (touched[fieldName]) {
      const error = validateField(fieldName, value);
      setErrors(prev => ({ ...prev, [fieldName]: error }));
    }
  };

  // Helper function to get field value
  const getFieldValue = (fieldName) => {
    switch (fieldName) {
      case 'name': return name;
      case 'companyName': return companyName;
      case 'contactNumber': return contactNumber;
      case 'hmoCard': return hmoCard;
      case 'hmoAccountNumber': return hmoAccountNumber;
      case 'validGovernmentID': return validGovernmentID;
      case 'validGovernmentIDNumber': return validGovernmentIDNumber;
      case 'birthdate': return birthdate;
      case 'relationship': return relationship;
      default: return '';
    }
  };

  // Function to handle form submission
  const handleSubmit = async (e) => {
    // e.preventDefault();
    
    setIsSubmitting(true);
    
    // Add a small delay to show loading state
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Mark all fields as touched
    setTouched({
      name: true,
      companyName: true,
      contactNumber: true,
      hmoCard: true,
      hmoAccountNumber: true,
      validGovernmentID: true,
      validGovernmentIDNumber: true,
      birthdate: true,
      relationship: true
    });

    // Validate all fields
    const newErrors = {};
    const fields = ['name', 'companyName', 'contactNumber', 'hmoCard', 'hmoAccountNumber', 'validGovernmentID', 'validGovernmentIDNumber', 'birthdate', 'relationship'];
    
    fields.forEach(field => {
      const value = getFieldValue(field);
      const error = validateField(field, value);
      if (error) {
        newErrors[field] = error;
      }
    });

    setErrors(newErrors);

    // Check if there are any validation errors
    if (Object.keys(newErrors).length > 0) {
      setIsSubmitting(false);
      // Scroll to first error field
      const firstErrorField = Object.keys(newErrors)[0];
      const errorElement = document.querySelector(`[name="${firstErrorField}"], input[placeholder*="${firstErrorField}"], select[value*="${firstErrorField}"]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        errorElement.focus();
      }
      return false; // Return false to indicate validation failed
    }

    // Ensure all fields are trimmed and properly formatted before saving
    const formData = {
      name: name.trim(),
      companyName: companyName.trim(),
      contactNumber: contactNumber.trim(),
      hmoCard: hmoCard.trim(),
      hmoAccountNumber: hmoAccountNumber.trim(),
      validGovernmentID: validGovernmentID.trim(),
      validGovernmentIDNumber: validGovernmentIDNumber.trim(),
      birthdate: birthdate.trim(),
      relationship: relationship.trim(),
    };
    handleInsuranceFormSubmit(formData);
    setIsSubmitting(false);
    return true; // Return true to indicate validation passed
  };

  // Function to handle go back
  // const handleGoBack = async () => {
  //   setIsGoingBack(true);
    
  //   // Add a small delay to show loading state
  //   await new Promise(resolve => setTimeout(resolve, 500));
    
  //   onClose(true);
  //   setIsGoingBack(false);
  // };

const theme = useTheme();
  const [activeStep, setActiveStep] = React.useState(0);
  const maxSteps = steps.length;

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const navigate = useNavigate();
  const location = useLocation();

  const [accOpen, setAccOpen] = useState({
    dentist: true,
    service: true,
    date: true,
    time: true,
  });
  const toggleAcc = (key) => (_e, isExpanded) =>
    setAccOpen((prev) => ({ ...prev, [key]: isExpanded }));
  
  // Ref to track logout state to prevent race conditions
  const isLoggingOut = useRef(false);

  // Fetch server time on component mount and periodically
  useEffect(() => {
    const fetchServerTime = async () => {
      try {
        // Write a temporary document with server timestamp
        const tempDocRef = doc(db, "_serverTime", "temp");
        await setDoc(tempDocRef, {
          timestamp: Timestamp.now()
        });
        
        // Read it back to get server time
        const tempDoc = await getDoc(tempDocRef);
        if (tempDoc.exists()) {
          const serverTimestamp = tempDoc.data().timestamp.toDate();
          setServerTime(serverTimestamp);
        }
      } catch (error) {
        console.error("Error fetching server time:", error);
        // Fallback to client time if server time fetch fails
        setServerTime(new Date());
      }
    };

    fetchServerTime();
    // Update server time every minute
    const interval = setInterval(fetchServerTime, 60000);

    return () => clearInterval(interval);
  }, []);

  // MASTER auth listener - consolidated
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user ? user : null);
      
      if (user) {
        // Reset logout flag when user logs back in
        isLoggingOut.current = false;
        
        // Fetch user details for sidebar
        const userRef = doc(db, "users", user.uid);
        const snapshot = await getDoc(userRef);
        if (snapshot.exists()) {
          const userData = snapshot.data();
          if (userData.userType === "Patient" || userData.role === "Patient") {
            setUserDetails({ id: user.uid, ...userData });
          }
        } else {
          try {
            await signOut(auth);
          } catch (error) {
            if (error?.code !== "auth/no-current-user") {
              console.error('Error signing out removed user:', error);
            }
          }
          navigate('/sign-in', {
            state: {
              from: location.pathname,
              message: 'Your session has ended. Please sign in again.'
            },
            replace: true
          });
        }
      } else {
        setUserDetails(null);
        setShowMedicalHistoryModal(false);
        navigate('/sign-in', {
          state: {
            from: location.pathname,
            message: 'You have been signed out. Please sign in again.'
          },
          replace: true
        });
      }
    });
    return () => unsubscribe();
  }, []);

  // Fetch closed days on component mount with real-time updates
  useEffect(() => {
    const unsubscribe = fetchClosedDays();
    
    // Cleanup function to unsubscribe when component unmounts
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  // Subscribe to chair scheduling settings (Appointment Chair Conflict Prevention)
  useEffect(() => {
    const unsubscribe = subscribeToChairSettings((next) => {
      setChairSettings(next || { ...DEFAULT_CHAIR_SETTINGS });
    });
    return () => unsubscribe && unsubscribe();
  }, []);

  // Real-time map of unavailableTimeSlots for the currently selected appointment date,
  // keyed by dentist UID. Powers chair-aware filtering of the dentist dropdown so
  // bench dentists "swapped in" by Manage Calendar still appear available to patients.
  useEffect(() => {
    if (!selectedDateAppointment) {
      setUnavailableSlotsByDentistForDate({});
      return undefined;
    }
    let dateObj;
    try {
      dateObj =
        selectedDateAppointment && typeof selectedDateAppointment.toDate === "function"
          ? selectedDateAppointment.toDate()
          : new Date(selectedDateAppointment);
    } catch (_) {
      setUnavailableSlotsByDentistForDate({});
      return undefined;
    }
    if (!dateObj || Number.isNaN(dateObj.getTime())) {
      setUnavailableSlotsByDentistForDate({});
      return undefined;
    }
    const dateStr = getLocalDateKey(dateObj);
    const ref = doc(db, "unavailableTimeSlots", dateStr);
    const unsubscribe = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          const raw = snap.data() || {};
          const next = {};
          Object.entries(raw).forEach(([uid, value]) => {
            if (Array.isArray(value)) next[uid] = value;
          });
          setUnavailableSlotsByDentistForDate(next);
        } else {
          setUnavailableSlotsByDentistForDate({});
        }
      },
      () => setUnavailableSlotsByDentistForDate({})
    );
    return () => unsubscribe();
  }, [selectedDateAppointment]);

  useEffect(() => {
    if (!selectedDateAppointment) {
      setChairSwapsBySlotForDate({});
      return undefined;
    }
    let dateObj;
    try {
      dateObj =
        selectedDateAppointment && typeof selectedDateAppointment.toDate === "function"
          ? selectedDateAppointment.toDate()
          : new Date(selectedDateAppointment);
    } catch (_) {
      setChairSwapsBySlotForDate({});
      return undefined;
    }
    if (!dateObj || Number.isNaN(dateObj.getTime())) {
      setChairSwapsBySlotForDate({});
      return undefined;
    }
    const dateStr = getLocalDateKey(dateObj);
    const ref = doc(db, CHAIR_SWAPS_COLLECTION, dateStr);
    const unsubscribe = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setChairSwapsBySlotForDate({});
          return;
        }
        setChairSwapsBySlotForDate(normalizeChairSwaps(snap.data() || {}));
      },
      () => setChairSwapsBySlotForDate({})
    );
    return () => unsubscribe();
  }, [selectedDateAppointment]);

  const bookingVisibleDentists = useMemo(() => {
    const safeDentists = Array.isArray(dentists) ? dentists : [];
    if (!isChairCapacityConfigured(chairSettings)) return safeDentists;
    const rosterSet = new Set(
      Array.isArray(chairSettings?.rosterDentistIds) ? chairSettings.rosterDentistIds : []
    );
    const swappedBenchSet = new Set(
      getSwappedInBenchUids({ swapsBySlot: chairSwapsBySlotForDate })
    );
    return safeDentists.filter(
      (dentist) => rosterSet.has(dentist.id) || swappedBenchSet.has(dentist.id)
    );
  }, [dentists, chairSettings, chairSwapsBySlotForDate]);

  const selectedBookingDateKey = useMemo(() => {
    if (!selectedDateAppointment) return null;
    try {
      const dateObj =
        selectedDateAppointment &&
        typeof selectedDateAppointment.toDate === "function"
          ? selectedDateAppointment.toDate()
          : selectedDateAppointment;
      return getLocalDateKey(dateObj);
    } catch {
      return null;
    }
  }, [selectedDateAppointment]);

  const [dentistPlotLoadedForDateKey, setDentistPlotLoadedForDateKey] = useState(null);
  const activeDentistPlotDateKeyRef = useRef(null);
  const dentistPlotRequestIdRef = useRef(0);

  const dentistPlotsReadyForSelectedDate =
    Boolean(selectedBookingDateKey) &&
    dentistPlotLoadedForDateKey === selectedBookingDateKey;

  const effectiveChairCount = useMemo(
    () =>
      resolveChairCountForDate({
        dateChairSchedule: chairScheduleForDate,
        globalSettings: chairSettings,
      }),
    [chairScheduleForDate, chairSettings]
  );

  /** When true, only dentists with a plotted schedule on the selected date are selectable. */
  const requireDentistPlotForBooking = useMemo(
    () =>
      effectiveChairCount > 0 ||
      isDentistSchedulePlottingEnabled(chairSettings) ||
      isChairCapacityConfigured(chairSettings),
    [effectiveChairCount, chairSettings]
  );

  const isPlottingEnabledForDate =
    requireDentistPlotForBooking && dentistPlotsReadyForSelectedDate;

  const isDentistSelectableForBooking = useCallback(
    (dentistId) => {
      if (!dentistId) return false;
      // Once this date's plot doc has loaded, only plotted dentists are selectable.
      if (dentistPlotsReadyForSelectedDate) {
        return dentistHasPlottedSchedule(dentistSchedulesForDate, dentistId);
      }
      if (!requireDentistPlotForBooking) return true;
      return false;
    },
    [
      requireDentistPlotForBooking,
      dentistPlotsReadyForSelectedDate,
      dentistSchedulesForDate,
    ]
  );

  const bookingVisibleDentistsSorted = useMemo(() => {
    return [...bookingVisibleDentists].sort((a, b) => {
      const aSelectable = isDentistSelectableForBooking(a.id);
      const bSelectable = isDentistSelectableForBooking(b.id);
      if (aSelectable === bSelectable) return 0;
      return aSelectable ? -1 : 1;
    });
  }, [bookingVisibleDentists, isDentistSelectableForBooking]);

  useEffect(() => {
    if (!selectedDentist) return;
    const dentist = bookingVisibleDentists.find(
      (entry) => entry.name === selectedDentist
    );
    if (!dentist || !isDentistSelectableForBooking(dentist.id)) {
      setSelectedDentist(null);
      setSelectedTimeSlot(null);
    }
  }, [
    selectedDentist,
    bookingVisibleDentists,
    isDentistSelectableForBooking,
    selectedBookingDateKey,
  ]);

  useEffect(() => {
    setSelectedDentist(null);
    setSelectedTimeSlot(null);
  }, [selectedBookingDateKey]);

  // State to store unsubscribe functions
  const [unsubscribers, setUnsubscribers] = useState([]);

  // Real-time cleanup of past appointments
  useEffect(() => {
    if (!currentUser) return;
    
    const appointmentsCollection = collection(db, "appointments");
    
    const unsubscribe = onSnapshot(appointmentsCollection, async (snapshot) => {
      const todayStr = getLocalDateKey(new Date());
      
      // Check for past appointments and clean them up in real-time
      const appointmentsToDelete = {};
      snapshot.forEach((docSnapshot) => {
        const dateStr = docSnapshot.id;
        
        // Skip if the date is today or in the future
        if (dateStr >= todayStr) {
          return;
        }
        
        const data = docSnapshot.data();
        if (!data || Object.keys(data).length === 0) {
          return;
        }
        
        // Find past appointments with Pending or Confirmed status
        Object.entries(data).forEach(([appointmentId, appointment]) => {
          if (appointment && (appointment.status === "Pending" || appointment.status === "Confirmed" || 
              appointment.status === "pending" || appointment.status === "confirmed")) {
            if (!appointmentsToDelete[dateStr]) {
              appointmentsToDelete[dateStr] = [];
            }
            appointmentsToDelete[dateStr].push(appointmentId);
          }
        });
      });
      
      // Delete past appointments if any were found
      if (Object.keys(appointmentsToDelete).length > 0) {
        for (const [dateStr, appointmentIds] of Object.entries(appointmentsToDelete)) {
          const dateDocRef = doc(db, "appointments", dateStr);
          const updates = {};
          appointmentIds.forEach(appointmentId => {
            updates[appointmentId] = deleteField();
          });
          
          try {
            await updateDoc(dateDocRef, updates);
            console.log(`Real-time cleanup: Deleted ${appointmentIds.length} past appointment(s) from ${dateStr}`);
          } catch (error) {
            console.error(`Error deleting past appointments from ${dateStr}:`, error);
          }
        }
      }
    });

    return () => unsubscribe();
  }, [currentUser]);

  // Fetch appointments for the selected date
  useEffect(() => {
    if (!selectedDateAppointment || !currentUser) return;
    
    const unsubscribe = fetchAppointmentsForMonth();
    if (unsubscribe) {
      setUnsubscribers(prev => [...prev, unsubscribe]);
    }
    
    return () => {
      if (unsubscribe) {
        unsubscribe();
        setUnsubscribers(prev => prev.filter(u => u !== unsubscribe));
      }
    };
  }, [selectedDateAppointment, currentUser]);

  useEffect(() => {
    const redirect = redirectIfNotLoggedIn(setShowCustomAlert);
    return () => {
      redirect && redirect();
    };
  }, []);

  // Fetch dentists
  useEffect(() => {
    if (!currentUser) return;
    
    const unsubscribe = fetchDentists();
    if (unsubscribe) {
      setUnsubscribers(prev => [...prev, unsubscribe]);
    }
    
    return () => {
      if (unsubscribe) {
        unsubscribe();
        setUnsubscribers(prev => prev.filter(u => u !== unsubscribe));
      }
    };
  }, [currentUser]);

  // Don't set first dentist as default - let user select

  // Fetch appointments for the selected date
  // Fetch appointments for the entire month (for calendar badges)
  const fetchAppointmentsForMonth = () => {
    if (!selectedDateAppointment || !currentUser) return null;
    
    const currentDate = selectedDateAppointment.toDate();
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    // Get start and end of month
    const startOfMonth = new Date(year, month, 1);
    const endOfMonth = new Date(year, month + 1, 0);
    
    // Calculate the first visible day on the calendar (might be from previous month)
    const firstVisibleDay = new Date(startOfMonth);
    const dayOfWeek = startOfMonth.getDay(); // 0 = Sunday, 6 = Saturday
    firstVisibleDay.setDate(startOfMonth.getDate() - dayOfWeek);
    
    // Calculate the last visible day on the calendar (might be from next month)
    const lastVisibleDay = new Date(endOfMonth);
    const lastDayOfWeek = endOfMonth.getDay();
    const daysToAdd = lastDayOfWeek === 6 ? 0 : 6 - lastDayOfWeek;
    lastVisibleDay.setDate(endOfMonth.getDate() + daysToAdd);
    
    const unsubscribes = [];
    
    // Fetch appointments for each visible day on the calendar
    const currentDay = new Date(firstVisibleDay);
    while (currentDay <= lastVisibleDay) {
      const formattedDate = getLocalDateKey(currentDay);

      const appointmentsRef = doc(db, "appointments", formattedDate);

      let isActive = true; // Track if listener is still active

      const unsubscribe = onSnapshot(
        appointmentsRef,
        (snapshot) => {
          if (!currentUser || !isActive) return;

          try {
            const data = snapshot.data();
            const fetched = data
              ? Object.entries(data).map(([id, value]) => ({
                  id,
                  ...value,
                  date: formattedDate, // Ensure date is set
                  uid: value.patientId || value.uid,
                  email: value.patientEmail || value.email,
                }))
              : [];

            // Use a more reliable state update approach
            setAppointments((prev) => {
              if (!isActive) return prev; // Don't update if unmounting

              // Create a new array without appointments for this date
              const withoutThisDate = prev.filter((appt) => appt.date !== formattedDate);
              // Add the new appointments for this date
              const updated = [...withoutThisDate, ...fetched];

              return updated;
            });
          } catch (error) {
            if (isActive) {
              console.error(`Error fetching appointments for ${formattedDate}:`, error);
            }
          }
        },
        (error) => {
          if (currentUser && !isLoggingOut.current && isActive) {
            console.error(`Error in appointments listener for ${formattedDate}:`, error);
          }
        }
      );

      unsubscribes.push(() => {
        isActive = false; // Mark as inactive before unsubscribing
        unsubscribe();
      });

      // Move to next day
      currentDay.setDate(currentDay.getDate() + 1);
    }
    
    return () => {
      unsubscribes.forEach(unsub => unsub());
    };
  };

  // Removed: Duplicate listener - user details now handled in master listener above

  // Cleanup effect when component unmounts
  useEffect(() => {
    return () => {
      // Clean up all listeners when component unmounts
      unsubscribers.forEach(unsubscribe => {
        if (unsubscribe) {
          unsubscribe();
        }
      });
    };
  }, [unsubscribers]);

  // Timeline compression persists after booking (below); optimistic UI updates run in submitAppointment.

  // Auto-cancel pending appointments in past time slots
  useEffect(() => {
    if (!currentUser || !appointments || appointments.length === 0 || !serverTime) return;
    
    const now = serverTime; // Use server time instead of client time
    const todayStr = getLocalDateKey(now);
    const currentTimeInMinutes = now.getHours() * 60 + now.getMinutes();
    
    // Filter appointments for today with "Pending" status
    const pendingAppointmentsToday = appointments.filter(apt => {
      const aptDateStr = getLocalDateKey(new Date(apt.date));
      return aptDateStr === todayStr && apt.status === "Pending";
    });
    
    // Check each pending appointment if it's in the past
    pendingAppointmentsToday.forEach(async (apt) => {
      if (!apt.startTime) return;
      
      // Get start time in minutes
      const startTimeInMinutes = timeToMinutes(apt.startTime);
      
      // If appointment start time is in the past, delete it
      if (startTimeInMinutes <= currentTimeInMinutes) {
        try {
          const dateDocRef = doc(db, "appointments", todayStr);
          await updateDoc(dateDocRef, {
            [`${apt.id}`]: deleteField()
          });
          console.log(`Auto-deleted past pending appointment: ${apt.id}`);
        } catch (error) {
          console.error(`Error auto-deleting appointment ${apt.id}:`, error);
        }
      }
    });
  }, [appointments, currentUser, serverTime]);

  // Fetch unavailable time slots when selectedDate changes with real-time updates
  useEffect(() => {
    if (!selectedDateAppointment || !selectedDentist) {
      setUnavailableTimeSlots([]);
      setHasSelectedDentistUnavailableRecord(false);
      return;
    }
    
    const dateStr = getLocalDateKey(selectedDateAppointment.toDate());

    // If the date is in closedDays, mark all time slots as unavailable
    if (closedDays.includes(dateStr)) {
      setUnavailableTimeSlots(buildClinicDaySlots());
      setHasSelectedDentistUnavailableRecord(false);
      return; // Exit early, don't set up listener
    }
    
    // Find the dentist ID
    const dentist = dentists.find(d => d.name === selectedDentist);
    if (!dentist) {
      setHasSelectedDentistUnavailableRecord(false);
      return;
    }
    
    const timeSlotsRef = doc(db, "unavailableTimeSlots", dateStr);
    let isActive = true; // Track if listener is still active
    
    const unsubscribe = onSnapshot(
      timeSlotsRef, 
      (snapshot) => {
        if (!isActive) return;
        
        if (snapshot.exists()) {
          const data = snapshot.data();
          const dentistSlots = data[dentist.id];
          // If dentistSlots is undefined, check if it's a new dentist
          if (dentistSlots === undefined) {
            setHasSelectedDentistUnavailableRecord(false);
            // Check if dentist is new - if so, show all slots as unavailable
            // Otherwise, show all slots as available (empty array)
            getDoc(doc(db, "users", dentist.id)).then(dentistDoc => {
              if (dentistDoc.exists()) {
                const dentistData = dentistDoc.data();
                const shouldDefaultUnavailable = shouldDefaultUnavailableByLegacySetup({
                  dentistUid: dentist.id,
                  settings: chairSettings,
                  hasScheduleSetup: dentistData.hasScheduleSetup,
                });
                const isBenchInChairMode =
                  isChairCapacityConfigured(chairSettings) &&
                  !isDentistOnRoster(dentist.id, chairSettings);
                if (shouldDefaultUnavailable) {
                  if (isActive) {
                    setUnavailableTimeSlots(
                      isBenchInChairMode ? [] : buildClinicDaySlots()
                    );
                  }
                } else {
                  // Existing dentist: all slots available
                  if (isActive) {
                    setUnavailableTimeSlots([]);
                  }
                }
              } else {
                // Dentist not found: default to all available
                if (isActive) {
                  setUnavailableTimeSlots([]);
                }
              }
            }).catch(() => {
              // On error, default to all available
              if (isActive) {
                setUnavailableTimeSlots([]);
              }
            });
          } else {
            // Dentist has data in Firestore, use it
            if (isActive) {
              setUnavailableTimeSlots(dentistSlots);
              setHasSelectedDentistUnavailableRecord(true);
            }
          }
        } else {
          setHasSelectedDentistUnavailableRecord(false);
          // No document exists - check if it's a new dentist
          getDoc(doc(db, "users", dentist.id)).then(dentistDoc => {
            if (dentistDoc.exists()) {
              const dentistData = dentistDoc.data();
              const shouldDefaultUnavailable = shouldDefaultUnavailableByLegacySetup({
                dentistUid: dentist.id,
                settings: chairSettings,
                hasScheduleSetup: dentistData.hasScheduleSetup,
              });
              const isBenchInChairMode =
                isChairCapacityConfigured(chairSettings) &&
                !isDentistOnRoster(dentist.id, chairSettings);
              if (shouldDefaultUnavailable) {
                if (isActive) {
                  setUnavailableTimeSlots(
                    isBenchInChairMode ? [] : buildClinicDaySlots()
                  );
                }
              } else {
                // Existing dentist: all slots available
                if (isActive) {
                  setUnavailableTimeSlots([]);
                }
              }
            } else {
              // Dentist not found: default to all available
              if (isActive) {
                setUnavailableTimeSlots([]);
              }
            }
          }).catch(() => {
            // On error, default to all available
            if (isActive) {
              setUnavailableTimeSlots([]);
            }
          });
        }
      }, 
      (error) => {
        if (isActive) {
          if (error?.code !== "permission-denied") {
            console.error("Error listening to unavailable time slots:", error);
          }
          setUnavailableTimeSlots([]);
          setHasSelectedDentistUnavailableRecord(false);
        }
      }
    );
    
    return () => {
      isActive = false;
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [selectedDateAppointment, selectedDentist, dentists, closedDays, chairSettings]);

  useEffect(() => {
    if (!selectedBookingDateKey) {
      activeDentistPlotDateKeyRef.current = null;
      setDentistSchedulesForDate({});
      setDentistPlotLoadedForDateKey(null);
      return undefined;
    }

    const dateStr = selectedBookingDateKey;
    const requestId = dentistPlotRequestIdRef.current + 1;
    dentistPlotRequestIdRef.current = requestId;
    activeDentistPlotDateKeyRef.current = dateStr;
    setDentistSchedulesForDate({});
    setDentistPlotLoadedForDateKey(null);

    return subscribeToDentistSchedulesForDate(dateStr, (plotData) => {
      if (dentistPlotRequestIdRef.current !== requestId) return;
      if (activeDentistPlotDateKeyRef.current !== dateStr) return;
      setDentistSchedulesForDate(plotData);
      setDentistPlotLoadedForDateKey(dateStr);
    });
  }, [selectedBookingDateKey]);

  useEffect(() => {
    if (!selectedDateAppointment) {
      setChairScheduleForDate(null);
      return undefined;
    }
    let dateObj;
    try {
      dateObj =
        selectedDateAppointment &&
        typeof selectedDateAppointment.toDate === "function"
          ? selectedDateAppointment.toDate()
          : selectedDateAppointment;
    } catch {
      setChairScheduleForDate(null);
      return undefined;
    }
    const dateStr = getLocalDateKey(dateObj);
    return subscribeToChairScheduleForDate(dateStr, setChairScheduleForDate);
  }, [selectedDateAppointment]);

  const selectedDentistUid = useMemo(() => {
    const dentist = dentists.find((d) => d.name === selectedDentist);
    return dentist?.id || null;
  }, [dentists, selectedDentist]);

  const selectedDentistHasPlottedSchedule = useMemo(() => {
    if (!selectedDentistUid) return false;
    if (dentistPlotsReadyForSelectedDate) {
      return dentistHasPlottedSchedule(dentistSchedulesForDate, selectedDentistUid);
    }
    if (!requireDentistPlotForBooking) return true;
    return false;
  }, [
    requireDentistPlotForBooking,
    dentistPlotsReadyForSelectedDate,
    dentistSchedulesForDate,
    selectedDentistUid,
  ]);

  // Update time slots when dependencies change
  useEffect(() => {
    const updateTimeSlots = async () => {
      if (selectedDateAppointment && appointments.length >= 0 && dentists.length > 0) {
        const slots = await generateTimeSlots();
        setTimeSlots(slots);
      }
    };
    
    updateTimeSlots();
  }, [
    selectedDateAppointment,
    appointments,
    dentists,
    selectedDentist,
    unavailableTimeSlots,
    unavailableSlotsByDentistForDate,
    chairSwapsBySlotForDate,
    hasSelectedDentistUnavailableRecord,
    chairSettings,
    closedDays,
    dentistSchedulesForDate,
    chairScheduleForDate,
    effectiveChairCount,
    requireDentistPlotForBooking,
    dentistPlotsReadyForSelectedDate,
    dentistPlotLoadedForDateKey,
  ]);

  // If availability changed and the selected slot is no longer bookable,
  // clear the stale selection so patient cannot proceed with it.
  useEffect(() => {
    if (!selectedTimeSlot) return;
    const currentSlot = timeSlots.find((slot) => slot.id === selectedTimeSlot.id);
    const isStillBookable =
      !!currentSlot &&
      currentSlot.isAvailable === true &&
      currentSlot.isUnavailable !== true;
    if (!isStillBookable) {
      setSelectedTimeSlot(null);
    }
  }, [timeSlots, selectedTimeSlot]);

  // Changing the chosen time slot invalidates service durations; start fresh.
  useEffect(() => {
    const id = selectedTimeSlot?.id ?? null;
    const prev = previousSelectedSlotIdRef.current;
    if (prev !== null && id !== prev) {
      setSelectedServices([]);
    }
    previousSelectedSlotIdRef.current = id;
  }, [selectedTimeSlot]);

  // Fetch dentists from Firebase
  const fetchDentists = () => {
    if (!currentUser) return null;
    
    const usersRef = collection(db, "users");
    
    const unsubscribe = onSnapshot(usersRef, (snapshot) => {
      // Check if user is still authenticated before processing
      if (!currentUser) {
        return;
      }
      
      const allDentists = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        // Exclude soft-deleted accounts so deleted dentists are removed from
        // booking dropdown options.
        const isDeleted =
          data.accountDeleted === true ||
          data.accountDeleted === "true" ||
          (typeof data.accountStatus === "string" &&
            data.accountStatus.toLowerCase() === "deleted");
        if (isDeleted) return;
        // Include all dentists regardless of email verification status
        // (they can sign in with mobile number even if email is not verified)
        if (data.userType === "DentistOwner" || data.role === "DentistOwner") {
          allDentists.push({
            id: doc.id,
            name: `Dr. ${data.firstName} ${data.lastName}`,
          });
        } else if (data.userType === "AssociateDentist" || data.role === "AssociateDentist") {
          allDentists.push({
            id: doc.id,
            name: `Dr. ${data.firstName} ${data.lastName}`,
          });
        }
      });
      
      // Sort dentists by role first (DentistOwner first), then alphabetically by name
      allDentists.sort((a, b) => {
        // Get the role from the original data by finding the dentist in the snapshot
        let roleA = 2, roleB = 2; // Default to 2 for unknown roles
        
        snapshot.forEach((doc) => {
          const data = doc.data();
          if (doc.id === a.id) {
            if (data.userType === "DentistOwner" || data.role === "DentistOwner") {
              roleA = 0;
            } else if (data.userType === "AssociateDentist" || data.role === "AssociateDentist") {
              roleA = 1;
            }
          }
          if (doc.id === b.id) {
            if (data.userType === "DentistOwner" || data.role === "DentistOwner") {
              roleB = 0;
            } else if (data.userType === "AssociateDentist" || data.role === "AssociateDentist") {
              roleB = 1;
            }
          }
        });
        
        if (roleA !== roleB) {
          return roleA - roleB;
        }
        
        // If roles are the same, sort alphabetically by name
        return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
      });
      
      setDentists(allDentists);
    }, (error) => {
      // Only log error if user is still authenticated and not logging out
      if (currentUser && !isLoggingOut.current) {
        console.error("Error fetching dentists:", error);
        setDentists([]);
      }
    });
    
    return unsubscribe;
  };

  const toggleService = (service) => {
    setSelectedServices((prevSelected) =>
      prevSelected.includes(service)
        ? prevSelected.filter((s) => s !== service)
        : [...prevSelected, service]
    );
  };

  // Convert time string to minutes (handles both 12-hour and 24-hour formats)
  const timeToMinutes = (timeStr) => {
    if (!timeStr) return 0;
    
    // If in 12-hour format (H:MM AM/PM), convert to 24-hour first
    if (timeStr.match(/^\d{1,2}:\d{2}\s?(AM|PM)$/i)) {
      const [time, period] = timeStr.split(/\s?(AM|PM)$/i);
      const [hours, minutes] = time.split(':').map(Number);
      let hour24 = hours;
      
      if (period.toUpperCase() === 'PM' && hour24 !== 12) {
        hour24 += 12;
      } else if (period.toUpperCase() === 'AM' && hour24 === 12) {
        hour24 = 0;
      }
      
      return hour24 * 60 + minutes;
    }
    
    // If in 24-hour format (HH:MM), convert directly
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Convert minutes to time string
  const minutesToTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  const formatTime = (minutes) => {
    const hours24 = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const date = new Date(0, 0, 0, hours24, mins, 0);
    let hours = date.getHours();
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    if (hours === 0) hours = 12;
    const formattedMinutes = mins < 10 ? `0${mins}` : mins;
    return `${hours}:${formattedMinutes} ${ampm}`;
  };

  // Parse service durations from formats like: 30, "30", "30 min", "1hr",
  // "1.5 hours", or "1 hr 30 min" and return minutes.
  const parseServiceDurationMinutes = (durationValue) => {
    if (typeof durationValue === "number" && Number.isFinite(durationValue)) {
      return durationValue > 0 ? durationValue : 0;
    }
    if (typeof durationValue !== "string") return 0;

    const raw = durationValue.trim().toLowerCase();
    if (!raw) return 0;

    let total = 0;

    const hourMatches = [...raw.matchAll(/(\d+(?:\.\d+)?)\s*(h|hr|hrs|hour|hours)\b/g)];
    hourMatches.forEach((m) => {
      const hours = Number(m[1]);
      if (Number.isFinite(hours) && hours > 0) total += hours * 60;
    });

    const minuteMatches = [...raw.matchAll(/(\d+(?:\.\d+)?)\s*(m|min|mins|minute|minutes)\b/g)];
    minuteMatches.forEach((m) => {
      const mins = Number(m[1]);
      if (Number.isFinite(mins) && mins > 0) total += mins;
    });

    if (total > 0) return Math.round(total);

    const fallback = Number(raw.replace(/[^\d.]/g, ""));
    if (Number.isFinite(fallback) && fallback > 0) return Math.round(fallback);

    return 0;
  };

  const getServiceDurationMinutes = (service) => {
    if (!service || service.duration == null) return 0;
    return parseServiceDurationMinutes(service.duration);
  };

  /** Firestore may store services as strings or { name, price } objects (same as ManageAppointment). */
  const formatServiceForPatientDisplay = (service) => {
    if (service == null) return "";
    if (typeof service === "string") return service;
    if (typeof service === "object") {
      const n = service.name ?? service.serviceName;
      if (typeof n === "string") return n;
      if (n != null) return String(n);
    }
    return String(service);
  };

  // Updated: Generate time slots for the selected dentist (with merged appointment blocks like ManageAppointment.jsx)
  const generateTimeSlots = async () => {
    if (!serverTime) return []; // Wait for server time to be available
    
    const officeStartTime = 9 * 60; // 9:00 AM in minutes
    const officeEndTime = 18 * 60; // 6:00 PM in minutes
    const slotInterval = 30; // 30-minute intervals
    const slots = [];

    // Check if selected date is today using server time
    const now = serverTime; // Use server time instead of client time
    const selectedDate = selectedDateAppointment.toDate();
    const isToday = selectedDate.getFullYear() === now.getFullYear() &&
                    selectedDate.getMonth() === now.getMonth() &&
                    selectedDate.getDate() === now.getDate();
    
    // Get current time in minutes (if today) using server time
    const currentTimeInMinutes = isToday ? (now.getHours() * 60 + now.getMinutes()) : 0;

    if (!selectedDentist) {
      // If no dentist selected, show empty slots
      for (let currentTime = officeStartTime; currentTime < officeEndTime; currentTime += slotInterval) {
        const end = currentTime + slotInterval;
        const startHours = Math.floor(currentTime / 60);
        const startMinutes = currentTime % 60;
        const startAmpm = startHours >= 12 ? 'PM' : 'AM';
        const startDisplayHours = startHours % 12 || 12;
        const startTimeStr = `${startDisplayHours}:${startMinutes.toString().padStart(2, '0')} ${startAmpm}`;
        
        const endHours = Math.floor(end / 60);
        const endMinutes = end % 60;
        const endAmpm = endHours >= 12 ? 'PM' : 'AM';
        const endDisplayHours = endHours % 12 || 12;
        const endTimeStr = `${endDisplayHours}:${endMinutes.toString().padStart(2, '0')} ${endAmpm}`;
        
        const timeStr = `${startTimeStr} - ${endTimeStr}`;
        
        // Check if this time slot is in the past (start time has passed)
        const isPastTime = isToday && currentTime <= currentTimeInMinutes;
        
        slots.push({
          id: `${currentTime}-${end}`,
          start: currentTime,
          end: end,
          timeStr: timeStr,
          display: timeStr,
          isAvailable: false,
          isUnavailable: isPastTime,
          slotStatus: isPastTime ? "past" : "no-dentist",
          appointment: null
        });
      }
      return slots;
    }

    let bookingDay;
    try {
      bookingDay =
        selectedDateAppointment && typeof selectedDateAppointment.toDate === "function"
          ? selectedDateAppointment.toDate()
          : new Date(selectedDateAppointment);
    } catch (_) {
      return slots;
    }
    const dateStr = getLocalDateKey(bookingDay);
    
    // Get dentist object
    const dentist = dentists.find(d => d.name === selectedDentist);
    if (!dentist) return slots;

    if (dentistPlotsReadyForSelectedDate) {
      if (!dentistHasPlottedSchedule(dentistSchedulesForDate, dentist.id)) {
        return [];
      }
    } else if (requireDentistPlotForBooking) {
      return [];
    }

    const dentistSlotsFromMap = unavailableSlotsByDentistForDate[dentist.id];
    const hasUnavailabilityRecord = Array.isArray(dentistSlotsFromMap)
      ? true
      : hasSelectedDentistUnavailableRecord;
    const dentistUnavailableSlots = Array.isArray(dentistSlotsFromMap)
      ? dentistSlotsFromMap
      : unavailableTimeSlots;
    const recognizedDaySlots = buildClinicDaySlots();
    const isDentistUnavailableForSlot = (slotLabel) =>
      isSlotEffectivelyUnavailableForDentist({
        slot: slotLabel,
        dentistUid: dentist.id,
        dentistUnavailableSlots,
        hasUnavailabilityRecord,
        settings: chairSettings,
        recognizedSlots: [slotLabel, ...recognizedDaySlots],
        timeToMinutes,
      });
    const unavailableRangesForBooking = parseUnavailableLabelsToMinuteRanges(
      dentistUnavailableSlots,
      timeToMinutes
    );

    // In chair mode, bench dentists are visible only when explicitly swapped in
    // via chairSwaps and should expose only their swapped slots.
    const inChairModeBooking = isChairCapacityConfigured(chairSettings);
    const isBenchDentist =
      inChairModeBooking && !isDentistOnRoster(dentist.id, chairSettings);
    const benchSwapSlots = isBenchDentist
      ? getBenchSwapSlots({
          swapsBySlot: chairSwapsBySlotForDate,
          benchUid: dentist.id,
        })
      : [];
    const effectiveHasUnavailabilityRecord =
      hasUnavailabilityRecord || (isBenchDentist && benchSwapSlots.length > 0);
    const doesSlotOverlapSwapSlots = (slotLabel, swapSlots = []) => {
      if (!slotLabel || !Array.isArray(swapSlots) || swapSlots.length === 0) {
        return false;
      }
      const parseRange = (label) => {
        if (!label || typeof label !== "string") return null;
        const [startRaw, endRaw] = label.split(" - ").map((part) => part.trim());
        if (!startRaw || !endRaw) return null;
        const start = timeToMinutes(startRaw);
        const end = timeToMinutes(endRaw);
        if (
          typeof start !== "number" ||
          typeof end !== "number" ||
          Number.isNaN(start) ||
          Number.isNaN(end)
        ) {
          return null;
        }
        return { start, end };
      };
      const source = parseRange(slotLabel);
      if (!source) return false;
      const mergedWindows = swapSlots
        .map(parseRange)
        .filter(Boolean)
        .sort((a, b) => a.start - b.start)
        .reduce((acc, next) => {
          if (acc.length === 0) return [next];
          const last = acc[acc.length - 1];
          if (next.start <= last.end) {
            last.end = Math.max(last.end, next.end);
            return acc;
          }
          acc.push({ ...next });
          return acc;
        }, []);
      return mergedWindows.some((target) => {
        // Bench slot must be fully contained in the explicit/merged swap window.
        return source.start >= target.start && source.end <= target.end;
      });
    };
    const inheritedBoundaryMinutes = (() => {
      const parseRange = (label) => {
        if (!label || typeof label !== "string") return null;
        const [startRaw, endRaw] = label.split(" - ").map((part) => part.trim());
        if (!startRaw || !endRaw) return null;
        const start = timeToMinutes(startRaw);
        const end = timeToMinutes(endRaw);
        if (
          typeof start !== "number" ||
          typeof end !== "number" ||
          Number.isNaN(start) ||
          Number.isNaN(end) ||
          end <= start
        ) {
          return null;
        }
        return { start, end };
      };

      const sourceSlots = isBenchDentist
        ? benchSwapSlots
        : Object.values(chairSwapsBySlotForDate || {})
            .filter((entry) => entry?.fromRosterUid === dentist.id && entry?.toBenchUid)
            .map((entry) => entry?.slot)
            .filter(Boolean);

      return Array.from(
        new Set(
          (Array.isArray(sourceSlots) ? sourceSlots : [])
            .map((slotLabel) => parseRange(slotLabel))
            .filter(Boolean)
            .flatMap((range) => [range.start, range.end])
        )
      ).sort((a, b) => a - b);
    })();
    const clampEndToInheritedBoundary = (startMinutes, proposedEndMinutes) => {
      const boundary = inheritedBoundaryMinutes.find(
        (minute) => minute > startMinutes && minute < proposedEndMinutes
      );
      return boundary || proposedEndMinutes;
    };
    const hasBenchSwapInForDate = isBenchDentist && benchSwapSlots.length > 0;
    const linkedRosterUidCandidates = hasBenchSwapInForDate
      ? getLinkedRosterUidsForBenchRange({
          swapsBySlot: chairSwapsBySlotForDate,
          benchUid: dentist.id,
          slotLabel: benchSwapSlots[0],
          timeToMinutes,
        })
      : [];
    const linkedRosterUidForBench =
      linkedRosterUidCandidates.length === 1
        ? linkedRosterUidCandidates[0]
        : hasBenchSwapInForDate
        ? getLinkedRosterForBenchSlot({
            swapsBySlot: chairSwapsBySlotForDate,
            benchUid: dentist.id,
            slot: benchSwapSlots[0],
          })
        : null;
    const linkedBenchUidsForRoster = !isBenchDentist
      ? getRosterPartnerBenchUidsForDay(chairSwapsBySlotForDate, dentist.id)
      : [];
    const hasRosterSwapOutForDate = linkedBenchUidsForRoster.length > 0;
    const inheritedSwapSlotsForRoster = !isBenchDentist
      ? Object.values(chairSwapsBySlotForDate || {})
          .filter((entry) => entry?.fromRosterUid === dentist.id && entry?.toBenchUid)
          .map((entry) => entry?.slot)
          .filter(Boolean)
      : [];

    const dayRowsForCompress = appointments
      .filter((a) => normalizeFirestoreDateKey(a.date) === dateStr)
      .map((a) => withAppointmentDocDate(a, dateStr));
    const { updates: dayCompressUpdates } = computeDayTimelineCompressionShiftUpdates(
      dayRowsForCompress,
      dateStr
    );
    const appointmentsForDay =
      dayCompressUpdates.length > 0
        ? applyTimelineShiftUpdatesToRows(dayRowsForCompress, dayCompressUpdates)
        : dayRowsForCompress;

    // Filter appointments for this date and dentist
    const dentistAppointments = appointmentsForDay.filter((appointment) => {
      if (normalizeFirestoreDateKey(appointment.date) !== dateStr) return false;
      
      // Skip cancelled appointments
      if (appointment.status === "Cancelled" || appointment.status === "cancelled") {
        return false;
      }
      
      // Bench dentists in a swap window should include their own bookings plus
      // linked roster bookings so the generated timeline remains consistent.
      if (hasBenchSwapInForDate && linkedRosterUidForBench) {
        return (
          appointment.dentistId === linkedRosterUidForBench ||
          appointment.dentistId === dentist.id
        );
      }
      if (hasRosterSwapOutForDate) {
        return (
          appointment.dentistId === dentist.id ||
          linkedBenchUidsForRoster.includes(appointment.dentistId)
        );
      }

      // Filter by dentist
      if (appointment.dentistId) {
        return appointment.dentistId === dentist.id;
      } else {
        return appointment.dentist === selectedDentist;
      }
    });
    
    // Sort appointments by start time
    const sortedAppointments = [...dentistAppointments].sort((a, b) => {
      const aStart = timeToMinutes(a.startTime || a.time);
      const bStart = timeToMinutes(b.startTime || b.time);
      return aStart - bStart;
    });
    
    // Create appointment slots with their actual durations
    const appointmentSlots = [];
    sortedAppointments.forEach(appointment => {
      const apptStart = timeToMinutes(appointment.startTime || appointment.time);
      // Use appointment.endTime if available, otherwise calculate from duration
      const apptEnd = appointment.endTime 
        ? timeToMinutes(appointment.endTime)
        : apptStart + (appointment.duration || 30);
      
      // Convert times to display format
      const startHours = Math.floor(apptStart / 60);
      const startMinutes = apptStart % 60;
      const startAmpm = startHours >= 12 ? 'PM' : 'AM';
      const startDisplayHours = startHours % 12 || 12;
      const startTimeStr = `${startDisplayHours}:${startMinutes.toString().padStart(2, '0')} ${startAmpm}`;
      
      const endHours = Math.floor(apptEnd / 60);
      const endMinutes = apptEnd % 60;
      const endAmpm = endHours >= 12 ? 'PM' : 'AM';
      const endDisplayHours = endHours % 12 || 12;
      const endTimeStr = `${endDisplayHours}:${endMinutes.toString().padStart(2, '0')} ${endAmpm}`;
      
      const timeStr = `${startTimeStr} - ${endTimeStr}`;
      
      // Check if this is the current user's appointment
      const isUserAppointment = currentUser && 
        (appointment.uid === currentUser.uid || appointment.patientId === currentUser.uid);
      
      appointmentSlots.push({
        start: apptStart,
        end: apptEnd,
        timeStr,
        appointment,
        isUserAppointment
      });
    });
    
    // Sort appointment slots by start time
    appointmentSlots.sort((a, b) => a.start - b.start);

    const doesRosterSlotOverlapInheritedSwapRange = (slotLabel) => {
      if (
        isBenchDentist ||
        !hasRosterSwapOutForDate ||
        !Array.isArray(inheritedSwapSlotsForRoster) ||
        inheritedSwapSlotsForRoster.length === 0
      ) {
        return false;
      }
      const slotRange = parseSlotLabelToRange(slotLabel, timeToMinutes);
      if (!slotRange) return false;
      return inheritedSwapSlotsForRoster.some((swapSlot) => {
        const swapRange = parseSlotLabelToRange(swapSlot, timeToMinutes);
        return (
          !!swapRange &&
          slotRange.start < swapRange.end &&
          slotRange.end > swapRange.start
        );
      });
    };

    const overlapsRosterBenchExclusiveAfterLinkedAppointment = (slotRange) => {
      if (
        !slotRange ||
        isBenchDentist ||
        !hasRosterSwapOutForDate ||
        !Array.isArray(inheritedSwapSlotsForRoster) ||
        inheritedSwapSlotsForRoster.length === 0
      ) {
        return false;
      }
      return inheritedSwapSlotsForRoster.some((swapSlot) => {
        const swapRange = parseSlotLabelToRange(swapSlot, timeToMinutes);
        return (
          !!swapRange &&
          slotRange.start < swapRange.end &&
          slotRange.end > swapRange.start
        );
      });
    };

    const isBenchSlotPatientBookableForMirror = (slotLabel) => {
      return doesRosterSlotOverlapInheritedSwapRange(slotLabel);
    };

    // Roster + secondary-for-the-day: use the truth-based mergedBlocks path below (not a fixed :00/:30 grid).
    // A fixed grid breaks non–half-hour ends (e.g. 12:05), merges bookings across half hours incorrectly for
    // selection, and diverges from ManageAppointment when swaps are added after bookings exist.

    if (false && hasBenchSwapInForDate) {
      const SLOT_INTERVAL_MINUTES = 30;
      const MIN_VISIBLE_SLOT_MINUTES = 15;
      const parseRange = (label) => {
        if (!label || typeof label !== "string") return null;
        const [startRaw, endRaw] = label.split(" - ").map((part) => part.trim());
        if (!startRaw || !endRaw) return null;
        const start = timeToMinutes(startRaw);
        const end = timeToMinutes(endRaw);
        if (
          typeof start !== "number" ||
          typeof end !== "number" ||
          Number.isNaN(start) ||
          Number.isNaN(end) ||
          end <= start
        ) {
          return null;
        }
        return { start, end };
      };
      const toLabel = (start, end) => {
        const toTime = (minutes) => {
          const hrs24 = Math.floor(minutes / 60);
          const mins = minutes % 60;
          const ampm = hrs24 >= 12 ? "PM" : "AM";
          const hrs12 = hrs24 % 12 || 12;
          return `${hrs12}:${mins.toString().padStart(2, "0")} ${ampm}`;
        };
        return `${toTime(start)} - ${toTime(end)}`;
      };

      const mergedSwapWindows = benchSwapSlots
        .map(parseRange)
        .filter(Boolean)
        .sort((a, b) => a.start - b.start)
        .reduce((acc, next) => {
          if (acc.length === 0) return [next];
          const last = acc[acc.length - 1];
          if (next.start <= last.end) {
            last.end = Math.max(last.end, next.end);
            return acc;
          }
          acc.push({ ...next });
          return acc;
        }, []);
      const benchSlots = [];

      const pushAvailableChunks = (start, end) => {
        let cursor = start;
        while (cursor < end) {
          const chunkEnd = Math.min(cursor + SLOT_INTERVAL_MINUTES, end);
          const availableDuration = chunkEnd - cursor;
          if (availableDuration >= MIN_VISIBLE_SLOT_MINUTES) {
            const isPastTime = isToday && cursor <= currentTimeInMinutes;
            benchSlots.push({
              id: `${cursor}-${chunkEnd}`,
              start: cursor,
              end: chunkEnd,
              timeStr: toLabel(cursor, chunkEnd),
              display: toLabel(cursor, chunkEnd),
              isAvailable: !isPastTime,
              isUnavailable: isPastTime,
              slotStatus: isPastTime ? "past" : "available",
              appointment: null,
            });
          }
          cursor = chunkEnd;
        }
      };

      mergedSwapWindows.forEach((window) => {
        const overlaps = appointmentSlots.filter(
          (appt) => appt.start < window.end && appt.end > window.start
        );
        let cursor = window.start;

        overlaps.forEach((appt) => {
          const overlapStart = Math.max(appt.start, window.start);
          const overlapEnd = Math.min(appt.end, window.end);

          if (overlapStart > cursor) {
            pushAvailableChunks(cursor, overlapStart);
          }

          if (overlapEnd > overlapStart) {
            const isBenchOwnedAppointment = appt.appointment?.dentistId === dentist.id;
            benchSlots.push({
              id: `${overlapStart}-${overlapEnd}`,
              start: overlapStart,
              end: overlapEnd,
              timeStr: toLabel(overlapStart, overlapEnd),
              display: toLabel(overlapStart, overlapEnd),
              isAvailable: false,
              isUnavailable: !isBenchOwnedAppointment,
              slotStatus: isBenchOwnedAppointment
                ? (appt.isUserAppointment ? "your" : "reserved")
                : "unavailable",
              appointment: isBenchOwnedAppointment ? appt.appointment : null,
            });
          }

          cursor = Math.max(cursor, overlapEnd);
        });

        if (window.end > cursor) {
          pushAvailableChunks(cursor, window.end);
        }
      });

      return benchSlots.sort((a, b) => a.start - b.start);
    }
    
    // Step 1: Create individual blocks for each appointment (NO MERGING)
    // Each appointment should be displayed separately, even if consecutive
    const mergedBlocks = [];
    
    appointmentSlots.forEach(slot => {
      // Each appointment gets its own block - no merging
      mergedBlocks.push({
        start: slot.start,
        end: slot.end,
        overallEndTime: slot.end, // Each block's end time is just its own end
        appointments: [slot],
        isUserAppointment: slot.isUserAppointment
      });
    });
    
    // Sort blocks by start time to ensure proper ordering
    mergedBlocks.sort((a, b) => a.start - b.start);

    const configuredChairCount = isPlottingEnabledForDate ? effectiveChairCount : 0;
    const plottedChairNumber =
      isPlottingEnabledForDate && dentist?.id
        ? getDentistScheduleEntry(dentistSchedulesForDate, dentist.id)?.chairNumber ?? null
        : null;
    const dentistChairNumber =
      plottedChairNumber != null &&
      plottedChairNumber >= 1 &&
      plottedChairNumber <= configuredChairCount
        ? plottedChairNumber
        : null;

    // Per-chair occupancy (plotting mode) mirrors the staff chair timetable.
    // Use original booked times so pending compression does not free a chair early.
    const chairTableForBooking =
      configuredChairCount > 0
        ? assignAppointmentsToChairsForBooking({
            appointments: dayRowsForCompress.filter((a) => {
              const s = (a.status || "").toLowerCase();
              return s !== "cancelled" && s !== "canceled";
            }),
            chairCount: configuredChairCount,
            scheduleDocData: isPlottingEnabledForDate
              ? dentistSchedulesForDate
              : null,
          })
        : [];

    // Legacy global sweep when plotting is disabled but chair count exists.
    const computeChairFullRanges = (appts) => {
      if (appts.length < configuredChairCount) return [];
      const evts = [];
      appts.forEach((a) => {
        const s = timeToMinutes(a.startTime || a.time || "");
        const e = a.endTime
          ? timeToMinutes(a.endTime)
          : !isNaN(s)
          ? s + (a.duration || 30)
          : NaN;
        if (!isNaN(s) && !isNaN(e) && e > s) {
          evts.push({ t: s, d: 1 });
          evts.push({ t: e, d: -1 });
        }
      });
      evts.sort((a, b) => (a.t !== b.t ? a.t - b.t : a.d - b.d));
      const ranges = [];
      let cnt = 0;
      let fullStart = null;
      evts.forEach(({ t, d }) => {
        cnt += d;
        if (cnt >= configuredChairCount && fullStart === null) fullStart = t;
        else if (cnt < configuredChairCount && fullStart !== null) {
          ranges.push({ start: fullStart, end: t });
          fullStart = null;
        }
      });
      if (fullStart !== null) ranges.push({ start: fullStart, end: officeEndTime });
      return ranges;
    };

    const chairFullRanges = [];
    if (configuredChairCount > 0 && !isPlottingEnabledForDate) {
      const activeForDayOriginal = dayRowsForCompress.filter((a) => {
        const s = (a.status || "").toLowerCase();
        return s !== "cancelled" && s !== "canceled";
      });
      chairFullRanges.push(...computeChairFullRanges(activeForDayOriginal));
    }

    // For plotting mode, compute chair-full minute ranges from the chair table so that
    // gap slots are split at the exact minute chairs free up (e.g. 10:01 AM) rather than
    // being blocked entirely by the overlap-based areAllChairsOccupiedAtRange check.
    const allChairsFullRangesForGap = (() => {
      if (configuredChairCount <= 0) return [];
      if (isPlottingEnabledForDate && chairTableForBooking.length > 0) {
        const flatAppts = chairTableForBooking.flatMap(({ appointments }) =>
          Array.isArray(appointments) ? appointments : []
        );
        return computeChairFullRanges(flatAppts);
      }
      return chairFullRanges;
    })();

    const pushBookingGapSlot = ({ start, end, timeStr }) => {
      const isPastTime = isToday && start <= currentTimeInMinutes;

      const isWithinPlottedSchedule =
        isPlottingEnabledForDate &&
        dentist?.id &&
        isRangeWithinPlottedSchedule(
          dentistSchedulesForDate,
          dentist.id,
          start,
          end
        );
      const isOutsidePlottedSchedule =
        isPlottingEnabledForDate && dentist?.id && !isWithinPlottedSchedule;

      let chairBlockStatus = null;
      if (!isPastTime && configuredChairCount > 0) {
        if (isPlottingEnabledForDate) {
          const allChairsOccupied = areAllChairsOccupiedAtRange(
            chairTableForBooking,
            start,
            end
          );
          const myChairOccupied =
            dentistChairNumber != null &&
            isChairOccupiedAtRange(
              chairTableForBooking,
              dentistChairNumber,
              start,
              end
            );
          if (allChairsOccupied) {
            chairBlockStatus =
              getChairBlockSeverityAtRange(chairTableForBooking, start, end) ||
              "chair-full";
          } else if (
            myChairOccupied &&
            !isAnyChairFreeAtRange(chairTableForBooking, start, end)
          ) {
            chairBlockStatus = getChairBlockSeverityForChair(
              chairTableForBooking,
              dentistChairNumber,
              start,
              end
            );
          }
        } else if (
          chairFullRanges.some((r) => start < r.end && end > r.start)
        ) {
          chairBlockStatus = "chair-full";
        }
      }

      if (!isPastTime && chairBlockStatus) {
        slots.push({
          id: `${start}-${end}`,
          start,
          end,
          timeStr,
          display: timeStr,
          isAvailable: false,
          isUnavailable: true,
          slotStatus: chairBlockStatus,
          appointment: null,
        });
        return;
      }

      const rosterSlotRange = !isBenchDentist
        ? parseSlotLabelToRange(timeStr, timeToMinutes)
        : null;
      const isDentistUnavailable = isOutsidePlottedSchedule
        ? true
        : isBenchDentist
        ? !doesSlotOverlapSwapSlots(timeStr, benchSwapSlots) ||
          isSlotEffectivelyUnavailableForDentist({
            slot: timeStr,
            dentistUid: dentist.id,
            dentistUnavailableSlots,
            hasUnavailabilityRecord: effectiveHasUnavailabilityRecord,
            settings: chairSettings,
            timeToMinutes,
          })
        : isPlottingEnabledForDate && isWithinPlottedSchedule
        ? isBenchSlotPatientBookableForMirror(timeStr) ||
          (rosterSlotRange &&
            overlapsRosterBenchExclusiveAfterLinkedAppointment(rosterSlotRange))
        : isDentistUnavailableForSlot(timeStr) ||
            isBenchSlotPatientBookableForMirror(timeStr) ||
            (rosterSlotRange &&
              overlapsRosterBenchExclusiveAfterLinkedAppointment(rosterSlotRange));
      const isBenchForcedUnavailable =
        isBenchDentist && !doesSlotOverlapSwapSlots(timeStr, benchSwapSlots);
      slots.push({
        id: `${start}-${end}`,
        start,
        end,
        timeStr,
        display: timeStr,
        isAvailable:
          !isDentistUnavailable && !isPastTime && !isBenchForcedUnavailable,
        isUnavailable:
          isDentistUnavailable || isPastTime || isBenchForcedUnavailable,
        slotStatus: isPastTime
          ? "past"
          : isDentistUnavailable || isBenchForcedUnavailable
          ? "unavailable"
          : "available",
        appointment: null,
      });
    };
    const pushBookingGapBetween = (gapStart, gapEnd) => {
      const emittedGapSlotKeys = new Set();

      const emitGapSlotOnce = ({ start, end, timeStr }) => {
        const key = `${start}-${end}`;
        if (emittedGapSlotKeys.has(key)) return;
        emittedGapSlotKeys.add(key);
        pushBookingGapSlot({ start, end, timeStr });
      };

      const emitGapSlots = (segStart, segEnd) => {
        if (segEnd - segStart < 20) return;
        const effectiveUnavailable =
          allChairsFullRangesForGap.length > 0
            ? mergeMinuteRanges([
                ...unavailableRangesForBooking,
                ...allChairsFullRangesForGap,
              ])
            : unavailableRangesForBooking;
        forEachTimelineGapSlot({
          gapStart: segStart,
          gapEnd: segEnd,
          unavailableRanges: effectiveUnavailable,
          slotInterval,
          officeEndTime,
          minSlotMinutes: 20,
          clampEnd: clampEndToInheritedBoundary,
          onSlot: ({ start, end, timeStr }) =>
            emitGapSlotOnce({ start, end, timeStr }),
        });
      };

      if (isPlottingEnabledForDate && dentist?.id) {
        const plottedRanges = getPlottedScheduleRangesForDentist(
          dentistSchedulesForDate,
          dentist.id
        );

        plottedRanges.forEach(({ start, end, label }) => {
          const segStart = Math.max(gapStart, start);
          const segEnd = Math.min(gapEnd, end);
          if (segEnd - segStart < 20) return;
          if (start >= gapStart && end <= gapEnd) {
            // If a chair-full range overlaps this plotted slot, route through emitGapSlots
            // so the slot is split at the exact chair-free boundary (e.g. 10:01 AM)
            // rather than being blocked entirely by the overlap-based chair check.
            const splitByChairFull = allChairsFullRangesForGap.some(
              (r) => start < r.end && end > r.start
            );
            if (splitByChairFull) {
              emitGapSlots(start, end);
            } else {
              emitGapSlotOnce({ start, end, timeStr: label });
            }
          } else {
            emitGapSlots(segStart, segEnd);
          }
        });

        // Show standard office slots (e.g. 5:30–6:00) as unavailable when staff unselected them.
        for (const label of buildStandardHalfHourSlotLabels({
          officeStartMinutes: officeStartTime,
          officeEndMinutes: officeEndTime,
        })) {
          const range = parseSlotLabelToRange(label, timeToMinutes);
          if (!range) continue;
          const overlapStart = Math.max(gapStart, range.start);
          const overlapEnd = Math.min(gapEnd, range.end);
          if (overlapEnd - overlapStart < 20) continue;
          if (
            isRangeWithinPlottedSchedule(
              dentistSchedulesForDate,
              dentist.id,
              range.start,
              range.end
            )
          ) {
            continue;
          }
          emitGapSlotOnce({
            start: range.start,
            end: range.end,
            timeStr: label,
          });
        }
        return;
      }

      emitGapSlots(gapStart, gapEnd);
    };

    let currentTime = officeStartTime;
    mergedBlocks.forEach((block, blockIndex) => {
      if (currentTime < block.start && currentTime < officeEndTime) {
        pushBookingGapBetween(
          currentTime,
          Math.min(block.start, officeEndTime)
        );
      }
      
      // Add the individual appointment block (each appointment is displayed separately)
      const blockStartHours = Math.floor(block.start / 60);
      const blockStartMinutes = block.start % 60;
      const blockStartAmpm = blockStartHours >= 12 ? 'PM' : 'AM';
      const blockStartDisplayHours = blockStartHours % 12 || 12;
      const blockStartTimeStr = `${blockStartDisplayHours}:${blockStartMinutes.toString().padStart(2, '0')} ${blockStartAmpm}`;
      
      const blockEndHours = Math.floor(block.end / 60);
      const blockEndMinutes = block.end % 60;
      const blockEndAmpm = blockEndHours >= 12 ? 'PM' : 'AM';
      const blockEndDisplayHours = blockEndHours % 12 || 12;
      const blockEndTimeStr = `${blockEndDisplayHours}:${blockEndMinutes.toString().padStart(2, '0')} ${blockEndAmpm}`;
      
      const blockTimeStr = `${blockStartTimeStr} - ${blockEndTimeStr}`;
      
      // Use the appointment in the block for display (each block contains one appointment)
      const displayAppointment = block.appointments[0].appointment;
      const isUserAppointment = block.isUserAppointment;

      /** Roster timeline: mirror non-roster blocks as 30-min strips (legacy). */
      const pushMirroredUnavailableGridSegments = () => {
        const capEnd = Math.min(block.end, officeEndTime);
        let seg = block.start;
        while (seg < capEnd) {
          const segEnd = Math.min(seg + slotInterval, capEnd);
          const segStartHours = Math.floor(seg / 60);
          const segStartMins = seg % 60;
          const segStartAmpm = segStartHours >= 12 ? 'PM' : 'AM';
          const segStartDisp = segStartHours % 12 || 12;
          const segStartStr = `${segStartDisp}:${segStartMins.toString().padStart(2, '0')} ${segStartAmpm}`;
          const segEndHours = Math.floor(segEnd / 60);
          const segEndMins = segEnd % 60;
          const segEndAmpm = segEndHours >= 12 ? 'PM' : 'AM';
          const segEndDisp = segEndHours % 12 || 12;
          const segEndStr = `${segEndDisp}:${segEndMins.toString().padStart(2, '0')} ${segEndAmpm}`;
          const segLabel = `${segStartStr} - ${segEndStr}`;
          slots.push({
            id: `${seg}-${segEnd}`,
            start: seg,
            end: segEnd,
            timeStr: segLabel,
            display: segLabel,
            isAvailable: false,
            isUnavailable: true,
            slotStatus: "unavailable",
            appointment: null,
          });
          seg = segEnd;
        }
      };

      /**
       * Bench view: roster-owned busy time must align to the same :00/:30 grid as ManageAppointment
       * (e.g. 12:00–12:30, 12:30–1:00), not truth-anchored fragments (12:00–12:05…) that then skew
       * following gap fill to 12:05, 12:35, …
       */
      const pushBenchRosterMirrorUnavailableAsStandardHalfHours = () => {
        const blockEndCap = Math.min(block.end, officeEndTime);
        let maxEmittedEnd = block.start;
        for (const label of buildStandardHalfHourSlotLabels({})) {
          const range = parseSlotLabelToRange(label, timeToMinutes);
          if (!range) continue;
          if (range.end <= block.start || range.start >= blockEndCap) continue;
          const isPastTime = isToday && range.start <= currentTimeInMinutes;
          slots.push({
            id: `${range.start}-${range.end}`,
            start: range.start,
            end: range.end,
            timeStr: label,
            display: label,
            isAvailable: false,
            isUnavailable: true,
            slotStatus: isPastTime ? "past" : "unavailable",
            appointment: null,
          });
          maxEmittedEnd = Math.max(maxEmittedEnd, range.end);
        }
        return Math.max(maxEmittedEnd, block.end);
      };

      let mirrorTimelineAdvanceEnd = block.end;

      // For bench dentists in chair mode, only bench-owned appointments should
      // render as reserved/your. Roster-owned mirrored blocks remain unavailable.
      if (hasBenchSwapInForDate) {
        const isBenchOwnedAppointment = displayAppointment?.dentistId === dentist.id;
        if (isBenchOwnedAppointment) {
          slots.push({
            id: `${block.start}-${block.end}`,
            start: block.start,
            end: block.end,
            timeStr: blockTimeStr,
            display: blockTimeStr,
            isAvailable: false,
            isUnavailable: false,
            slotStatus: isUserAppointment ? "your" : "reserved",
            appointment: displayAppointment,
          });
        } else {
          mirrorTimelineAdvanceEnd = pushBenchRosterMirrorUnavailableAsStandardHalfHours();
        }
      } else if (hasRosterSwapOutForDate) {
        const isRosterOwnedAppointment =
          displayAppointment?.dentistId === dentist.id ||
          (!displayAppointment?.dentistId &&
            displayAppointment?.dentist === selectedDentist);
        if (isRosterOwnedAppointment) {
          slots.push({
            id: `${block.start}-${block.end}`,
            start: block.start,
            end: block.end,
            timeStr: blockTimeStr,
            display: blockTimeStr,
            isAvailable: false,
            isUnavailable: false,
            slotStatus: isUserAppointment ? "your" : "reserved",
            appointment: displayAppointment,
          });
        } else {
          pushMirroredUnavailableGridSegments();
        }
      } else {
        slots.push({
          id: `${block.start}-${block.end}`,
          start: block.start,
          end: block.end,
          timeStr: blockTimeStr,
          display: blockTimeStr,
          isAvailable: false,
          isUnavailable: false,
          slotStatus: isUserAppointment ? "your" : "reserved",
          appointment: displayAppointment,
        });
      }
      
      // Move currentTime past this block (and past standard half-hour mirror coverage for bench roster mirrors).
      currentTime = Math.max(currentTime, mirrorTimelineAdvanceEnd);
    });
    
    if (currentTime < officeEndTime) {
      pushBookingGapBetween(currentTime, officeEndTime);
    }

    return mergeContiguousBookedTimelineRows(slots);
  };

  // Fetch closed days from Firebase with real-time updates
  const fetchClosedDays = () => {
    try {
      const closedDaysRef = doc(db, "closedDays", "closedDates");
      let isActive = true; // Track if listener is still active
      
      const unsubscribe = onSnapshot(
        closedDaysRef, 
        (doc) => {
          if (!isActive) return;
          
          if (doc.exists()) {
            setClosedDays(doc.data().dates || []);
          } else {
            setClosedDays([]);
          }
        }, 
        (error) => {
          if (isActive && error?.code !== "permission-denied") {
            console.error("Error listening to closed days:", error);
          }
        }
      );
      
      // Return cleanup function
      return () => {
        isActive = false;
        unsubscribe();
      };
    } catch (error) {
      if (error?.code !== "permission-denied") {
        console.error("Error setting up closed days listener:", error);
      }
      return null;
    }
  };

  // Fetch dentist-specific unavailable time slots
  const fetchDentistUnavailableTimeSlots = async (date, dentistId) => {
    if (!date || !dentistId) return [];
    
    try {
      const dateStr = getLocalDateKey(date);

      const timeSlotsRef = doc(db, "unavailableTimeSlots", dateStr);
      const timeSlotsSnap = await getDoc(timeSlotsRef);
      if (timeSlotsSnap.exists()) {
        const data = timeSlotsSnap.data();
        return data[dentistId] || [];
      } else {
        return [];
      }
    } catch (error) {
      if (error?.code !== "permission-denied") {
        console.error("Error fetching dentist unavailable time slots:", error);
      }
      return [];
    }
  };

  // Function to disable past dates and closed days in calendar
  const tileDisabled = ({ date, view }) => {
    // Only disable tiles in month view, not navigation controls
    if (view !== 'month') {
      return false;
    }
    
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to start of day
    
    // Check if date is in the past
    if (date < today) {
      return true;
    }
    
    // Check if date is marked as closed - use local date formatting
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    return closedDays.includes(dateStr);
  };

  // Function to disable dates in MUI DatePicker (for closed dates)
  const shouldDisableDate = (date) => {
    if (!date) return false;
    
    // Convert dayjs to JavaScript Date for comparison
    const jsDate = date.toDate();
    
    // Check if date is marked as closed - use local date formatting
    const year = jsDate.getFullYear();
    const month = String(jsDate.getMonth() + 1).padStart(2, '0');
    const day = String(jsDate.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    return closedDays.includes(dateStr);
  };

  // Get appointment counts for a specific date (only for current user)
  const getUserAppointmentCountsForDate = (date) => {
    if (!date || !currentUser) return { pending: 0, confirmed: 0, completed: 0 };
    
    const dateStr = getLocalDateKey(date);

    // Filter appointments for this specific date that belong to the current user
    const userDateAppointments = appointments.filter((appointment) => {
      const belongsToUser =
        appointment.uid === currentUser.uid || appointment.patientId === currentUser.uid;
      return normalizeFirestoreDateKey(appointment.date) === dateStr && belongsToUser;
    });
    
    const counts = { pending: 0, confirmed: 0, completed: 0 };
    userDateAppointments.forEach(appointment => {
      if (isPendingLikeStatus(appointment.status)) {
        counts.pending++;
      } else if (appointment.status === "Confirmed" || appointment.status === "confirmed") {
        counts.confirmed++;
      } else if (appointment.status === "Completed" || appointment.status === "completed") {
        counts.completed++;
      }
    });
    
    return counts;
  };


function ServerDay(props) {
  const { day, outsideCurrentMonth, ...other } = props;

  // Get appointment counts for this day
  const appointmentCounts = getUserAppointmentCountsForDate(day.toDate());
  const hasAppointments =
    appointmentCounts.pending > 0 ||
    appointmentCounts.confirmed > 0 ||
    appointmentCounts.completed > 0;

  return (
    <div style={{ position: "relative", display: "inline-flex" }}>
      <PickersDay {...other} outsideCurrentMonth={outsideCurrentMonth} day={day} />

      {hasAppointments && (
        <div
          style={{
            position: "absolute",
            top: "4px",
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            gap: "2px",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 10,
            pointerEvents: "none",
          }}
        >
          {/* Confirmed badge (green) */}
          {appointmentCounts.confirmed > 0 && (
            <div
              style={{
              width: "5px",
              height: "5px",
              borderRadius: "50%",
              backgroundColor: "#80cd97ff",
              border: "1px solid #59544A"
            }}
            />
          )}

          {/* Pending badge (orange) */}
          {appointmentCounts.pending > 0 && (
            <div
              style={{
              width: "6px",
              height: "6px",
              borderRadius: "50%",
              backgroundColor: "#ffd48eff",
              border: "1px solid #59544A"
            }}
            />
          )}
        </div>
      )}
    </div>
  );
}


  // Render calendar tile with appointment badges (only for current user's appointments)
  const renderCalendarTile = (date, view) => {
    if (view !== 'month') {
      return null;
    }
    
    const appointmentCounts = getUserAppointmentCountsForDate(date);
    const hasAppointments = appointmentCounts.pending > 0 || appointmentCounts.confirmed > 0 || appointmentCounts.completed > 0;
    
    
    if (!hasAppointments) {
      return null;
    }
    
    return (
      <div style={{ 
        position: 'absolute', 
        top: '2px', 
        left: '50%', 
        transform: 'translateX(-50%)', 
        display: 'flex', 
        gap: '3px',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
        pointerEvents: 'none'
      }}>
        {appointmentCounts.confirmed > 0 && (
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: '#4CAF50',
            border: '1px solid white',
            boxShadow: '0 1px 3px rgba(0,0,0,0.6)'
          }} />
        )}
        {appointmentCounts.pending > 0 && (
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: '#FFA500',
            border: '1px solid white',
            boxShadow: '0 1px 3px rgba(0,0,0,0.6)'
          }} />
        )}
      </div>
    );
  };

  // Validate appointment details before proceeding to next step
  // This function is called when the 'Review Details' button is clicked
  // It validates all required fields and shows transparent error messages to the patient
  const computeMaxContiguousEndFromSlots = (slots, startMinutes) => {
    if (typeof startMinutes !== "number" || Number.isNaN(startMinutes)) {
      return startMinutes;
    }
    const sorted = (Array.isArray(slots) ? [...slots] : [])
      .filter(
        (slot) =>
          slot &&
          typeof slot.start === "number" &&
          typeof slot.end === "number" &&
          !Number.isNaN(slot.start) &&
          !Number.isNaN(slot.end)
      )
      .sort((a, b) => a.start - b.start);

    const canTraverseSlot = (slot) => {
      if (!slot || slot.isUnavailable === true) return false;
      if (slot.isAvailable === true) return true;
      const appt = slot.appointment;
      if (!appt || !isCompressiblePendingStatus(appt.status)) return false;
      if (!currentUser) return false;
      return appt.uid === currentUser.uid || appt.patientId === currentUser.uid;
    };

    const first = sorted.find(
      (slot) => slot.start === startMinutes && canTraverseSlot(slot)
    );
    if (!first || typeof first.end !== "number") {
      return startMinutes;
    }
    let maxEnd = first.end;
    while (true) {
      const nextAdjacent = sorted.find(
        (slot) => slot.start === maxEnd && canTraverseSlot(slot)
      );
      if (!nextAdjacent) break;
      maxEnd = nextAdjacent.end;
    }
    return maxEnd;
  };

  // Sum visible available minutes from selected start up to the first hard anchored
  // block (confirmed/completed/reserved). Used to cap service combinations in the
  // booking dropdown and review flow to what patients can actually see as remaining time.
  const computeVisibleAvailableMinutesFromStart = (slots, startMinutes) => {
    if (typeof startMinutes !== "number" || Number.isNaN(startMinutes)) return 0;

    const normalized = (Array.isArray(slots) ? slots : [])
      .filter(
        (slot) =>
          slot &&
          typeof slot.start === "number" &&
          typeof slot.end === "number" &&
          !Number.isNaN(slot.start) &&
          !Number.isNaN(slot.end) &&
          slot.end > slot.start &&
          slot.start >= startMinutes
      )
      .sort((a, b) => a.start - b.start);

    if (normalized.length === 0) return 0;

    const firstHardAnchorStart = normalized
      .filter((slot) => {
        const status = ((slot?.appointment?.status || "") + "").trim().toLowerCase();
        return status === "confirmed" || status === "completed" || status === "reserved";
      })
      .reduce((minStart, slot) => Math.min(minStart, slot.start), Number.POSITIVE_INFINITY);

    const capEnd = Number.isFinite(firstHardAnchorStart)
      ? firstHardAnchorStart
      : Number.POSITIVE_INFINITY;

    return normalized.reduce((sum, slot) => {
      if (slot.end > capEnd) return sum;
      if (slot.isAvailable === true && slot.isUnavailable !== true) {
        return sum + (slot.end - slot.start);
      }
      // Compressible pending appointments can be cascade-pushed, so their slots
      // count as usable time for service duration selection.
      if (
        slot.isUnavailable !== true &&
        slot.appointment &&
        isCompressiblePendingStatus(slot.appointment.status)
      ) {
        return sum + (slot.end - slot.start);
      }
      return sum;
    }, 0);
  };

  /** Last minute index of the calendar day (11:59 PM). Minute 1440 is 12:00 AM / next day. */
  const LAST_MINUTE_OF_CALENDAR_DAY = 23 * 60 + 59; // 1439

  /**
   * Normal business-day close on the half-hour grid (6:00 PM). The schedule may extend later for
   * bookings; inherited bench "runs through close" must compare swap segment end to this — not to
   * the last rendered row (e.g. 11:59 PM) — or afternoon swaps incorrectly get a hard 6 PM cap.
   */
  const STANDARD_OFFICE_GRID_CLOSE_MINUTES = 18 * 60;

  /** Max duration (minutes) from slot start so start + duration does not reach 12:00 AM (next day). */
  const maxDurationUntilMidnightFromSlotStart = (startMinutes) => {
    if (typeof startMinutes !== "number" || Number.isNaN(startMinutes)) return 0;
    const cap = LAST_MINUTE_OF_CALENDAR_DAY - startMinutes;
    return cap > 0 ? cap : 0;
  };

  /** Last row of the current schedule grid (e.g. 5:30–6:00). Clinic may run past displayed closing, so duration is not capped to adjacent cells. */
  const isLastDisplayedScheduleSlot = (slot, slots) => {
    if (!slot || typeof slot.end !== "number" || Number.isNaN(slot.end)) return false;
    if (!Array.isArray(slots) || slots.length === 0) return false;
    let maxEnd = -1;
    for (const s of slots) {
      if (s && typeof s.end === "number" && !Number.isNaN(s.end)) {
        maxEnd = Math.max(maxEnd, s.end);
      }
    }
    return maxEnd >= 0 && slot.end === maxEnd;
  };

  const getSelectedDentistLinkedTimelineContext = () => {
    const dentistObj = dentists.find((d) => d.name === selectedDentist);
    if (!dentistObj) {
      return { dentistObj: null, linkedRosterUid: null, usesLinkedTimeline: false };
    }
    const inChairMode = isChairCapacityConfigured(chairSettings);
    const isBenchDentist =
      inChairMode && !isDentistOnRoster(dentistObj.id, chairSettings);
    if (!isBenchDentist) {
      return { dentistObj, linkedRosterUid: null, usesLinkedTimeline: false };
    }
    const benchSwapSlots = getBenchSwapSlots({
      swapsBySlot: chairSwapsBySlotForDate,
      benchUid: dentistObj.id,
    });
    if (benchSwapSlots.length === 0) {
      return { dentistObj, linkedRosterUid: null, usesLinkedTimeline: false };
    }
    const benchPartners = getBenchPartnerRosterUidsForDay(
      chairSwapsBySlotForDate,
      dentistObj.id
    );
    let linkedRosterUid = null;
    if (benchPartners.length === 1) {
      linkedRosterUid = benchPartners[0];
    } else if (benchPartners.length > 1) {
      const linkedRosterCandidates = getLinkedRosterUidsForBenchRange({
        swapsBySlot: chairSwapsBySlotForDate,
        benchUid: dentistObj.id,
        slotLabel: benchSwapSlots[0],
        timeToMinutes,
      });
      linkedRosterUid =
        linkedRosterCandidates.length === 1
          ? linkedRosterCandidates[0]
          : getLinkedRosterForBenchSlot({
              swapsBySlot: chairSwapsBySlotForDate,
              benchUid: dentistObj.id,
              slot: benchSwapSlots[0],
            });
    }
    return {
      dentistObj,
      linkedRosterUid: linkedRosterUid || null,
      usesLinkedTimeline: Boolean(linkedRosterUid),
    };
  };

  const isBlockingStatusForScheduleChecks = (status = "") => {
    const normalized = (status || "").toString().trim().toLowerCase();
    return normalized !== "cancelled" && normalized !== "canceled" && normalized !== "completed";
  };

  const completedPatientOverlapBookingMessage =
    "Booking failed. The total duration of the appointment overlaps with another completed patient appointment.";

  const mergeMinuteRanges = (ranges = []) => {
    const sorted = (Array.isArray(ranges) ? ranges : [])
      .filter(
        (range) =>
          range &&
          typeof range.start === "number" &&
          typeof range.end === "number" &&
          !Number.isNaN(range.start) &&
          !Number.isNaN(range.end) &&
          range.end > range.start
      )
      .sort((a, b) => a.start - b.start);
    return sorted.reduce((acc, next) => {
      if (acc.length === 0) return [{ ...next }];
      const last = acc[acc.length - 1];
      if (next.start <= last.end) {
        last.end = Math.max(last.end, next.end);
        return acc;
      }
      acc.push({ ...next });
      return acc;
    }, []);
  };

  /** Remove merged `subtractors` coverage from `range`; used so booking spans that absorb/shift pending visits are not rejected by mirror "unavailable" geometry. */
  const subtractOneRangeFromMergedUnion = (range, mergedSubtractors) => {
    if (!range || typeof range.start !== "number" || typeof range.end !== "number") {
      return [];
    }
    if (Number.isNaN(range.start) || Number.isNaN(range.end) || range.end <= range.start) {
      return [];
    }
    if (!Array.isArray(mergedSubtractors) || mergedSubtractors.length === 0) {
      return [{ start: range.start, end: range.end }];
    }
    let pieces = [{ start: range.start, end: range.end }];
    for (const sub of mergedSubtractors) {
      if (!sub || sub.end <= sub.start) continue;
      const next = [];
      for (const p of pieces) {
        if (sub.end <= p.start || sub.start >= p.end) {
          next.push(p);
          continue;
        }
        if (sub.start > p.start) {
          next.push({ start: p.start, end: Math.min(p.end, sub.start) });
        }
        if (sub.end < p.end) {
          next.push({ start: Math.max(p.start, sub.end), end: p.end });
        }
      }
      pieces = next.filter((q) => q.end > q.start);
    }
    return pieces;
  };

  const rangeOverlapsAny = (source, targets = []) => {
    if (!source || !Array.isArray(targets) || targets.length === 0) return false;
    return targets.some((target) => source.start < target.end && source.end > target.start);
  };

  /** Tie-break when two obstacles share the same overlap start (prefer unavailable → confirmed → completed). */
  const SCHEDULE_CONFLICT_KIND_ORDER = { unavailable: 0, confirmed: 1, completed: 2 };

  const overlapIntervalStartMinutes = (source, obstacle) => {
    if (
      !source ||
      !obstacle ||
      typeof source.start !== "number" ||
      typeof source.end !== "number" ||
      typeof obstacle.start !== "number" ||
      typeof obstacle.end !== "number"
    ) {
      return null;
    }
    if (
      Number.isNaN(source.start) ||
      Number.isNaN(source.end) ||
      source.end <= source.start ||
      Number.isNaN(obstacle.start) ||
      Number.isNaN(obstacle.end) ||
      obstacle.end <= obstacle.start
    ) {
      return null;
    }
    if (source.start >= obstacle.end || source.end <= obstacle.start) return null;
    return Math.max(source.start, obstacle.start);
  };

  /**
   * Among all overlaps between `source` and layered obstacles, return the conflict that begins
   * earliest on the schedule (then earliest obstacle start, then kind order).
   */
  const findEarliestScheduleConflictForRange = (source, layers) => {
    if (
      !source ||
      typeof source.start !== "number" ||
      typeof source.end !== "number" ||
      Number.isNaN(source.start) ||
      Number.isNaN(source.end) ||
      source.end <= source.start
    ) {
      return null;
    }
    let best = null;
    for (const layer of layers) {
      const ranges = layer.ranges;
      if (!Array.isArray(ranges)) continue;
      const kindOrder = SCHEDULE_CONFLICT_KIND_ORDER[layer.kind] ?? 99;
      for (const r of ranges) {
        const overlapStart = overlapIntervalStartMinutes(source, r);
        if (overlapStart == null) continue;
        const cand = {
          overlapStart,
          obstacleStart: r.start,
          kindOrder,
          message: layer.message,
        };
        if (
          !best ||
          cand.overlapStart < best.overlapStart ||
          (cand.overlapStart === best.overlapStart && cand.obstacleStart < best.obstacleStart) ||
          (cand.overlapStart === best.overlapStart &&
            cand.obstacleStart === best.obstacleStart &&
            cand.kindOrder < best.kindOrder)
        ) {
          best = cand;
        }
      }
    }
    return best;
  };

  const pickEarlierScheduleConflict = (a, b) => {
    if (!a) return b;
    if (!b) return a;
    if (a.overlapStart !== b.overlapStart) return a.overlapStart < b.overlapStart ? a : b;
    if (a.obstacleStart !== b.obstacleStart) return a.obstacleStart < b.obstacleStart ? a : b;
    return a.kindOrder < b.kindOrder ? a : b;
  };

  /** Same compression rules as patient precheck: stack shifted pendings after the new visit in start order. */
  const getCompressionShiftFailureMessage = ({
    newRange,
    pendingRanges,
    fixedConfirmedRanges,
    fixedCompletedRanges = [],
    unavailableRangesForPrecheck,
    isBenchPatientPrecheck,
    benchHardEndCapMinutes,
  }) => {
    const confirmedOverlapMsg =
      "Booking failed. The total duration of the appointment overlaps with another confirmed patient appointment.";
    const unavailableOverlapMsg =
      "Booking failed. The total duration of the appointment overlaps with an unavailable time slot.";

    const pendingMergedForSubtract = mergeMinuteRanges(
      pendingRanges.map((p) => ({ start: p.start, end: p.end }))
    );
    const newRangeSegmentsForUnavailable = subtractOneRangeFromMergedUnion(
      newRange,
      pendingMergedForSubtract
    );

    let earliestNewRangeConflict = findEarliestScheduleConflictForRange(newRange, [
      { ranges: fixedCompletedRanges, message: completedPatientOverlapBookingMessage, kind: "completed" },
      { ranges: fixedConfirmedRanges, message: confirmedOverlapMsg, kind: "confirmed" },
    ]);
    for (const seg of newRangeSegmentsForUnavailable) {
      earliestNewRangeConflict = pickEarlierScheduleConflict(
        earliestNewRangeConflict,
        findEarliestScheduleConflictForRange(seg, [
          { ranges: unavailableRangesForPrecheck, message: unavailableOverlapMsg, kind: "unavailable" },
        ])
      );
    }
    if (earliestNewRangeConflict) {
      return earliestNewRangeConflict.message;
    }

    if (
      typeof benchHardEndCapMinutes === "number" &&
      !Number.isNaN(benchHardEndCapMinutes) &&
      newRange.end > benchHardEndCapMinutes
    ) {
      return unavailableOverlapMsg;
    }

    const unavailableForShiftedPrecheck =
      isBenchPatientPrecheck && pendingMergedForSubtract.length > 0
        ? unavailableRangesForPrecheck.filter(
            (u) => !rangeOverlapsAny(u, pendingMergedForSubtract)
          )
        : unavailableRangesForPrecheck;

    let pushedEnd = newRange.end;
    for (const pendingRange of pendingRanges) {
      if (pendingRange.end <= newRange.start) continue;
      if (pendingRange.start >= pushedEnd) continue;
      const shifted = {
        start: pushedEnd,
        end: pushedEnd + pendingRange.duration,
      };

      // Per-dentist schedule guard: a pushed pending visit cannot move outside
      // that pending appointment's own plotted schedule window.
      const shiftedDentistUid = pendingRange.dentistId || null;
      if (shiftedDentistUid) {
        const shiftedDentistUnavailable =
          getDentistScheduleUnavailableRanges(dentistSchedulesForDate, shiftedDentistUid) || [];
        const exceedsShiftedDentistSchedule = shiftedDentistUnavailable.some(
          (r) => shifted.start < r.end && shifted.end > r.start
        );
        if (exceedsShiftedDentistSchedule) {
          return "Booking failed. The selected services would push another appointment past the dentist's scheduled working hours.";
        }
      }

      if (shifted.end > LAST_MINUTE_OF_CALENDAR_DAY) {
        return "Booking failed. The selected services would push another appointment past 11:59 PM.";
      }
      const shiftedConflict = findEarliestScheduleConflictForRange(shifted, [
        { ranges: fixedCompletedRanges, message: completedPatientOverlapBookingMessage, kind: "completed" },
        { ranges: fixedConfirmedRanges, message: confirmedOverlapMsg, kind: "confirmed" },
        { ranges: unavailableForShiftedPrecheck, message: unavailableOverlapMsg, kind: "unavailable" },
      ]);
      if (shiftedConflict) {
        return shiftedConflict.message;
      }
      pushedEnd = shifted.end;
    }

    if (
      typeof benchHardEndCapMinutes === "number" &&
      !Number.isNaN(benchHardEndCapMinutes) &&
      pushedEnd > benchHardEndCapMinutes
    ) {
      return unavailableOverlapMsg;
    }

    return "";
  };

  const evaluateDurationCompressionPrecheck = ({
    selectedSlotFromCurrentList,
    appointmentDuration,
    selectedDentistContext,
  }) => {
    if (
      !selectedSlotFromCurrentList ||
      typeof selectedSlotFromCurrentList.start !== "number" ||
      typeof appointmentDuration !== "number" ||
      appointmentDuration <= 0
    ) {
      return "";
    }
    const selectedDateObj =
      selectedDateAppointment && typeof selectedDateAppointment.toDate === "function"
        ? selectedDateAppointment.toDate()
        : null;
    if (!selectedDateObj) return "";
    const dateKey = getLocalDateKey(selectedDateObj);
    const dentistUid = selectedDentistContext?.dentistObj?.id;
    if (!dentistUid) return "";

    const maxUntilMidnightPrecheck = maxDurationUntilMidnightFromSlotStart(
      selectedSlotFromCurrentList.start
    );
    if (maxUntilMidnightPrecheck > 0 && appointmentDuration > maxUntilMidnightPrecheck) {
      return "Booking failed. The total duration of the selected services exceeds the available timeslot.";
    }

    const visibleAvailableMinutesCap = computeVisibleAvailableMinutesFromStart(
      timeSlots,
      selectedSlotFromCurrentList.start
    );
    if (visibleAvailableMinutesCap > 0 && appointmentDuration > visibleAvailableMinutesCap) {
      return "Booking failed. The total duration of the selected services exceeds the available timeslot.";
    }

    const timelineDentistIds = new Set([
      dentistUid,
      ...(selectedDentistContext?.linkedRosterUid
        ? [selectedDentistContext.linkedRosterUid]
        : []),
    ]);
    const relevantAppointments = (Array.isArray(appointments) ? appointments : [])
      .filter((appointment) => appointment && appointmentBelongsToDateDoc(appointment, dateKey))
      .filter((appointment) => {
        const apptDentistId = appointment.dentistId || null;
        return apptDentistId && timelineDentistIds.has(apptDentistId);
      })
      .filter((appointment) => {
        const s = ((appointment.status || "") + "").trim().toLowerCase();
        return s !== "cancelled" && s !== "canceled";
      });

    const fixedCompletedRanges = relevantAppointments
      .filter((appointment) => isCompletedLikeStatus(appointment.status))
      .map((appointment) => {
        const start = timeToMinutes(appointment.startTime || appointment.time);
        const end = appointment.endTime
          ? timeToMinutes(appointment.endTime)
          : start + (appointment.duration || 30);
        return { start, end };
      })
      .filter((range) => range.end > range.start);

    const fixedConfirmedRanges = relevantAppointments
      .filter(
        (appointment) =>
          !isCompressiblePendingStatus(appointment.status) &&
          !isCompletedLikeStatus(appointment.status)
      )
      .map((appointment) => {
        const start = timeToMinutes(appointment.startTime || appointment.time);
        const end = appointment.endTime
          ? timeToMinutes(appointment.endTime)
          : start + (appointment.duration || 30);
        return { start, end };
      })
      .filter((range) => range.end > range.start);

    const pendingRanges = relevantAppointments
      .filter((appointment) => isCompressiblePendingStatus(appointment.status))
      .map((appointment) => {
        const start = timeToMinutes(appointment.startTime || appointment.time);
        const end = appointment.endTime
          ? timeToMinutes(appointment.endTime)
          : start + (appointment.duration || 30);
        return {
          start,
          end,
          duration: Math.max(1, end - start),
          dentistId: appointment.dentistId || null,
        };
      })
      .filter((range) => range.end > range.start)
      .sort((a, b) => a.start - b.start);

    const fixedUnavailableFromSlots = mergeMinuteRanges(
      (Array.isArray(timeSlots) ? timeSlots : [])
        .filter(
          (slot) =>
            slot &&
            typeof slot.start === "number" &&
            typeof slot.end === "number" &&
            slot.isUnavailable === true &&
            // Only hard-block statuses prevent service duration extension:
            //   "unavailable" = dentist-marked or structural unavailability.
            //   "chair-full"  = confirmed/completed appointments fill all chairs.
            // "chair-full-soft" (pending-only fills all chairs) is intentionally
            // excluded — pending appointments sit in pendingRanges and can be
            // pushed by compression, so the patient can still select a longer
            // service that overlaps the soft-full window.
            (slot.slotStatus === "unavailable" || slot.slotStatus === "chair-full")
            // chair-full-soft: all chairs busy with pending only — still shown as
            // unavailable to pick, but does not cap service duration (pending can shift).
        )
        .map((slot) => ({ start: slot.start, end: slot.end }))
    );

    if (effectiveChairCount > 0) {
      const dayAppointmentsForChair = (Array.isArray(appointments) ? appointments : [])
        .filter((appointment) => appointment && appointmentBelongsToDateDoc(appointment, dateKey))
        .filter((appointment) => {
          const s = ((appointment.status || "") + "").trim().toLowerCase();
          return s !== "cancelled" && s !== "canceled";
        });
      const chairPlotResult = validatePatientBookingDurationOnChairPlot({
        appointments: dayAppointmentsForChair,
        chairCount: effectiveChairCount,
        scheduleDocData: dentistSchedulesForDate,
        bookingDentistId: dentistUid,
        newStartMin: selectedSlotFromCurrentList.start,
        newEndMin: selectedSlotFromCurrentList.start + appointmentDuration,
      });
      if (!chairPlotResult.ok && chairPlotResult.message) {
        return chairPlotResult.message;
      }
    }

    // Derive the schedule-end cap from the last visible available slot so that
    // services cannot extend the appointment past the dentist's schedule end —
    // even if post-schedule slots lack an explicit isUnavailable marker.
    const scheduleEndMinutes = (Array.isArray(timeSlots) ? timeSlots : [])
      .filter(
        (slot) =>
          slot &&
          typeof slot.end === "number" &&
          slot.isAvailable === true &&
          slot.isUnavailable !== true
      )
      .reduce((max, slot) => Math.max(max, slot.end), 0);
    const fixedUnavailableRanges =
      scheduleEndMinutes > 0
        ? mergeMinuteRanges([
            ...fixedUnavailableFromSlots,
            { start: scheduleEndMinutes, end: 24 * 60 },
          ])
        : fixedUnavailableFromSlots;

    // Chair-capacity hard blocks for service-duration precheck.
    const chairCapacityFullRanges = (() => {
      if (!isChairCapacityConfigured(chairSettings)) return [];
      const cc = isPlottingEnabledForDate
        ? effectiveChairCount
        : Number(chairSettings.chairCount) || 0;
      if (cc <= 0) return [];

      const activeAllDentists = (Array.isArray(appointments) ? appointments : [])
        .filter((a) => a && appointmentBelongsToDateDoc(a, dateKey))
        .filter((a) => {
          const s = ((a.status || "") + "").trim().toLowerCase();
          return s === "confirmed" || s === "completed" || s === "reserved";
        });

      if (isPlottingEnabledForDate) {
        const dentistUid = selectedDentistContext?.dentistObj?.id;
        const plottedChair = dentistUid
          ? getDentistScheduleEntry(dentistSchedulesForDate, dentistUid)?.chairNumber ?? null
          : null;
        const dentistChair =
          plottedChair != null && plottedChair >= 1 && plottedChair <= cc
            ? plottedChair
            : null;
        const chairTable = assignAppointmentsToChairsForBooking({
          appointments: activeAllDentists,
          chairCount: cc,
          scheduleDocData: dentistSchedulesForDate,
        });
        const ranges = [];
        const step = 15;
        for (let t = 0; t < 24 * 60; t += step) {
          const tEnd = t + step;
          const blocked =
            areAllChairsOccupiedAtRange(chairTable, t, tEnd) ||
            (dentistChair != null &&
              isChairOccupiedAtRange(chairTable, dentistChair, t, tEnd));
          if (blocked) ranges.push({ start: t, end: tEnd });
        }
        return mergeMinuteRanges(ranges);
      }

      const minuteRanges = activeAllDentists
        .map((a) => {
          const s = timeToMinutes(a.startTime || a.time);
          const e = a.endTime ? timeToMinutes(a.endTime) : s + (a.duration || 30);
          return { start: s, end: e };
        })
        .filter((r) => r.end > r.start);
      if (minuteRanges.length < cc) return [];
      const evts = [];
      minuteRanges.forEach((r) => {
        evts.push({ t: r.start, d: +1 });
        evts.push({ t: r.end, d: -1 });
      });
      evts.sort((a, b) => (a.t !== b.t ? a.t - b.t : a.d - b.d));
      const full = [];
      let cnt = 0;
      let fStart = null;
      for (const e of evts) {
        cnt += e.d;
        if (cnt >= cc && fStart === null) fStart = e.t;
        else if (cnt < cc && fStart !== null) {
          full.push({ start: fStart, end: e.t });
          fStart = null;
        }
      }
      if (fStart !== null) full.push({ start: fStart, end: 24 * 60 });
      return full;
    })();
    const effectiveUnavailableRanges =
      chairCapacityFullRanges.length > 0
        ? mergeMinuteRanges([...fixedUnavailableRanges, ...chairCapacityFullRanges])
        : fixedUnavailableRanges;

    const newRange = {
      start: selectedSlotFromCurrentList.start,
      end: selectedSlotFromCurrentList.start + appointmentDuration,
    };

    const allowPastClosingForLastSlot = isLastDisplayedScheduleSlot(
      selectedSlotFromCurrentList,
      timeSlots
    );

    let mergedBenchCoverageForPrecheck = [];
    let maxBenchMergedEndForPrecheck = null;
    if (
      isChairCapacityConfigured(chairSettings) &&
      dentistUid &&
      !isDentistOnRoster(dentistUid, chairSettings)
    ) {
      mergedBenchCoverageForPrecheck = getBenchSwapCoverageRanges({
        swapsBySlot: chairSwapsBySlotForDate,
        benchUid: dentistUid,
        timeToMinutes,
      });
      if (mergedBenchCoverageForPrecheck.length > 0) {
        maxBenchMergedEndForPrecheck = Math.max(
          ...mergedBenchCoverageForPrecheck.map((r) => r.end)
        );
      }
    }

    /** Strip post–inherited-close grid unavailability when roster picks last visible row, or when the bench's *current* inherited segment runs through office close (e.g. 1:30–6:00). Morning-only segments (9:00–1:30) cap at that segment's end. */
    const isBenchPatientPrecheck =
      isChairCapacityConfigured(chairSettings) &&
      dentistUid &&
      !isDentistOnRoster(dentistUid, chairSettings);

    const slotStartForBenchPrecheck = selectedSlotFromCurrentList.start;
    const containingBenchRangeForPrecheck =
      mergedBenchCoverageForPrecheck.find(
        (r) =>
          typeof slotStartForBenchPrecheck === "number" &&
          !Number.isNaN(slotStartForBenchPrecheck) &&
          slotStartForBenchPrecheck >= r.start &&
          slotStartForBenchPrecheck < r.end
      ) || null;

    const benchContainingEndMinutes =
      containingBenchRangeForPrecheck != null &&
      typeof containingBenchRangeForPrecheck.end === "number" &&
      !Number.isNaN(containingBenchRangeForPrecheck.end)
        ? containingBenchRangeForPrecheck.end
        : maxBenchMergedEndForPrecheck;

    const benchMayExtendPastInheritedClose =
      isBenchPatientPrecheck &&
      typeof containingBenchRangeForPrecheck?.end === "number" &&
      !Number.isNaN(containingBenchRangeForPrecheck.end) &&
      containingBenchRangeForPrecheck.end >= STANDARD_OFFICE_GRID_CLOSE_MINUTES;

    const stripUnavailableFromInheritedClose =
      typeof maxBenchMergedEndForPrecheck === "number" &&
      !Number.isNaN(maxBenchMergedEndForPrecheck) &&
      (allowPastClosingForLastSlot || benchMayExtendPastInheritedClose);

    // For any dentist with a plotted schedule, treat times outside their scheduled hours
    // as unavailable so services cannot push the appointment past the dentist's schedule
    // end or into schedule gaps (lunch breaks, etc.). This applies to both roster and
    // bench dentists — if a dentist has an explicit schedule entry it must be respected.
    const hasPlottedScheduleForDentist =
      dentistUid && dentistHasPlottedSchedule(dentistSchedulesForDate, dentistUid);
    const scheduleOutsideRanges =
      hasPlottedScheduleForDentist
        ? getDentistScheduleUnavailableRanges(dentistSchedulesForDate, dentistUid) ?? []
        : [];

    const baseUnavailable = stripUnavailableFromInheritedClose
      ? effectiveUnavailableRanges.filter((r) => r.start < maxBenchMergedEndForPrecheck)
      : effectiveUnavailableRanges;
    const unavailableRangesForPrecheck =
      scheduleOutsideRanges.length > 0
        ? mergeMinuteRanges([...baseUnavailable, ...scheduleOutsideRanges])
        : baseUnavailable;

    const benchHardEndCapMinutes =
      isBenchPatientPrecheck &&
      containingBenchRangeForPrecheck != null &&
      typeof benchContainingEndMinutes === "number" &&
      !Number.isNaN(benchContainingEndMinutes) &&
      !benchMayExtendPastInheritedClose
        ? benchContainingEndMinutes
        : null;

    return getCompressionShiftFailureMessage({
      newRange,
      pendingRanges,
      fixedConfirmedRanges,
      fixedCompletedRanges,
      unavailableRangesForPrecheck,
      isBenchPatientPrecheck,
      benchHardEndCapMinutes,
    });
  };

  /** Roster / non-chair: cap by adjacent available cells. Bench with merged swap coverage: precheck authorizes compression; ignore contiguous cap. */
  const shouldApplyContiguousAvailableDurationCap = (dentistUid) => {
    if (!dentistUid) return true;
    if (!isChairCapacityConfigured(chairSettings)) return true;
    if (isDentistOnRoster(dentistUid, chairSettings)) return true;
    const mergedBenchCoverage = getBenchSwapCoverageRanges({
      swapsBySlot: chairSwapsBySlotForDate,
      benchUid: dentistUid,
      timeToMinutes,
    });
    return mergedBenchCoverage.length === 0;
  };

  /**
   * Half-hour grid ends at clinic close (e.g. 6 PM) even when no confirmed/completed/unavailable
   * block exists afterward. If compression already passed for totalMinutes and contiguous availability
   * reached the last row of the rendered schedule, allow duration up to 11:59 PM the same day.
   */
  const durationAllowedVersusContiguousGridCap = ({
    totalMinutes,
    startMinutes,
    maxContiguousEnd,
    timeSlots: slotsForGrid,
    allowPastClosingForLastSlot,
    dentistUidForCap,
  }) => {
    if (typeof startMinutes !== "number" || Number.isNaN(startMinutes)) return true;
    if (allowPastClosingForLastSlot) return true;
    if (!shouldApplyContiguousAvailableDurationCap(dentistUidForCap)) return true;
    const maxBookableDuration = maxContiguousEnd - startMinutes;
    if (!(maxBookableDuration > 0 && totalMinutes > maxBookableDuration)) return true;

    const displayScheduleMaxEnd = (Array.isArray(slotsForGrid) ? slotsForGrid : []).reduce(
      (m, s) =>
        s && typeof s.end === "number" && !Number.isNaN(s.end) ? Math.max(m, s.end) : m,
      0
    );
    const contiguousReachedScheduleEdge =
      typeof maxContiguousEnd === "number" &&
      displayScheduleMaxEnd > 0 &&
      maxContiguousEnd >= displayScheduleMaxEnd;

    const maxUntilDayEnd = maxDurationUntilMidnightFromSlotStart(startMinutes);
    return contiguousReachedScheduleEdge && totalMinutes <= maxUntilDayEnd;
  };

  const handleAppointmentSubmit = async () => {
    const selectedSlotFromCurrentList = selectedTimeSlot
      ? timeSlots.find((slot) => slot.id === selectedTimeSlot.id)
      : null;
    const isSelectedSlotValid =
      !!selectedSlotFromCurrentList &&
      selectedSlotFromCurrentList.isAvailable === true &&
      selectedSlotFromCurrentList.isUnavailable !== true;
    if (selectedTimeSlot && !isSelectedSlotValid) {
      setSelectedTimeSlot(null);
    }

    // Check for missing required fields
    const missingFields = [];
    if (!selectedDateAppointment) missingFields.push("date");
    if (!selectedDentist) missingFields.push("dentist");
    if (selectedServices.length === 0) missingFields.push("service(s)");
    if (!selectedTimeSlot || !isSelectedSlotValid) missingFields.push("time slot");

    if (missingFields.length > 0) {
      // Format: "Please select a date, dentist, service(s), and time slot."
      const fieldsText = missingFields.length === 1 
        ? `a ${missingFields[0]}`
        : missingFields.length === 2
        ? `a ${missingFields[0]} and ${missingFields[1]}`
        : `a ${missingFields.slice(0, -1).join(", ")}, and ${missingFields[missingFields.length - 1]}`;
      setBookingStatus(`Please select ${fieldsText}.`);
      setSnackbarSeverity("error");
      setShowBookingSnackbar(true);
      return false;
    }

    // Note: Service validation is handled by the useEffect hook in real-time
    // If services were removed, the useEffect already filtered them and showed the error

    // Block Review Details when selected services exceed current available slot.
    const totalDuration = selectedServices.reduce((total, serviceName) => {
      const service = services.find((s) => s.name === serviceName);
      const duration = getServiceDurationMinutes(service);
      if (!duration) return total;
      return total + duration;
    }, 0);
    const appointmentDuration = totalDuration > 0 ? totalDuration : 30;
    const selectedDentistContext = getSelectedDentistLinkedTimelineContext();
    const precheckConflictMessage = evaluateDurationCompressionPrecheck({
      selectedSlotFromCurrentList,
      appointmentDuration,
      selectedDentistContext,
    });
    if (precheckConflictMessage) {
      setBookingStatus(precheckConflictMessage);
      setSnackbarSeverity("error");
      setShowBookingSnackbar(true);
      return false;
    }
    // Duration is authoritative once compression/unavailability precheck passes; do not cap again from
    // computeMaxContiguousEndFromSlots — that only walks visually "open" rows and blocks valid bookings
    // that absorb other patients' pending/new visits into later free time (e.g. before 1:30 swap-out).

    // Check if selected date is in the past
    const today = dayjs().startOf('day');
    const selectedDateStart = selectedDateAppointment.startOf('day');
    
    if (selectedDateStart.isBefore(today)) {
      setBookingStatus("Cannot book appointments on past dates.");
      setSnackbarSeverity("error");
      setShowBookingSnackbar(true);
      return false;
    }

    // Check if patient already has an appointment on this date
    if (currentUser) {
      const formattedDate = new Date(
        selectedDateAppointment.toDate().getTime() - selectedDateAppointment.toDate().getTimezoneOffset() * 60000
      )
        .toISOString()
        .split("T")[0];

      try {
        const appointmentsRef = doc(db, "appointments", formattedDate);
        const appointmentsSnap = await getDoc(appointmentsRef);
        
        if (appointmentsSnap.exists()) {
          const appointmentsData = appointmentsSnap.data();
          
          // Find existing appointment for this patient on this date (check by UID or email)
          const existingAppt = Object.entries(appointmentsData).find(([id, appointment]) => {
            if (!appointment) return false;
            const isPatientAppointment = 
              appointment.patientId === currentUser.uid || 
              appointment.uid === currentUser.uid ||
              (appointment.patientEmail && appointment.patientEmail === currentUser.email);
            const isNotCancelled = appointment.status !== "Cancelled" && appointment.status !== "cancelled";
            const isNotCompleted = appointment.status !== "Completed" && appointment.status !== "completed";
            return isPatientAppointment && isNotCancelled && isNotCompleted;
          });

          if (existingAppt) {
            const [appointmentId, appointmentData] = existingAppt;
            setExistingAppointment({
              id: appointmentId,
              ...appointmentData,
              date: formattedDate
            });
            setShowExistingAppointmentModal(true);
            return false; // Don't proceed to booking summary
          }
        }
      } catch (error) {
        console.error("Error checking for existing appointment:", error);
        // Continue with booking if there's an error checking
      }
    }

    return true;
  };

  const confirmInsuranceAndSubmit = async (insuranceStatus) => {
    if (insuranceStatus && !insuranceAgreement) {
      // Don't proceed if they have insurance but haven't agreed
      return;
    }

    setIsProceedButtonLoading(true);
    
    // Add a small delay to show loading state
    await new Promise(resolve => setTimeout(resolve, 500));
    
    setShowInsuranceModal(false);
    // setInsuranceAgreement(false);

    // If they have insurance, show insurance form first
    if (insuranceChoice) {
      // setShowInsuranceForm(true);  q
    } else {
      // If no insurance, show terms and conditions modal directly
      // setShowTermsModal(true);
    }
    
    setIsProceedButtonLoading(false);
  };

  const handleInsuranceChoice = async (choice) => {
    if (choice === true) {
      setIsYesButtonLoading(true);
      // Add a small delay to show loading state
      await new Promise(resolve => setTimeout(resolve, 500));
      setIsYesButtonLoading(false);
    } else {
      setIsNoButtonLoading(true);
      // Add a small delay to show loading state
      await new Promise(resolve => setTimeout(resolve, 500));
      setIsNoButtonLoading(false);
    }
    
    setInsuranceChoice(choice);
    if (choice === false) {
      // If they choose "No", they can proceed immediately
      setInsuranceAgreement(true);
    } else {
      // If they choose "Yes", they need to read and agree
      setInsuranceAgreement(false);
    }
  };

  const handleInsuranceFormSubmit = (insuranceDetails) => {
    setShowInsuranceForm(false);

    // Store insurance details temporarily and show terms modal
    setTempInsuranceDetails(insuranceDetails);
   
    // setShowTermsModal(true);
  };

  const handleTermsAgreement = async () => {
    if (!termsAgreement) {
      return; // Don't proceed without agreement
    }

    // Prevent multiple clicks
    if (isTermsAgreeLoading) {
      return;
    }

    setIsTermsAgreeLoading(true);
    
    // Add a small delay to show loading state
    await new Promise(resolve => setTimeout(resolve, 500));
    
    setShowTermsModal(false);
    setTermsAgreement(false); // Reset for next time

    // Now proceed with the actual appointment submission
    // IMPORTANT: Await the submission to prevent multiple simultaneous submissions
    try {
      // Check if user has insurance or not based on the insurance state
      // User has insurance if insuranceAgreement is true AND tempInsuranceDetails exists
      // User doesn't have insurance if insuranceNone is true
      const hasInsurance = insuranceAgreement && tempInsuranceDetails !== null && !insuranceNone;
      
      if (hasInsurance) {
        await submitAppointment(true, tempInsuranceDetails);
        setTempInsuranceDetails(null); // Clear temporary storage
      } else {
        // User selected "I don't have insurance" or didn't select insurance option
        await submitAppointment(false, null);
      }
      
    } catch (error) {
      // Error is already handled in submitAppointment
      console.error("Error in handleTermsAgreement:", error);
    } finally {
      // Reset insurance choice after submission
      setInsuranceChoice(null);
      setInsuranceAgreement(false);
      setInsuranceNone(false);
      setIsTermsAgreeLoading(false);
    }
  };

  // Cancel the patient's same-day appointment so they can choose a new slot (no Review modal)
  const handleCancelExistingAndProceed = async () => {
    if (!existingAppointment || !currentUser) return;

    try {
      setIsBookingLoading(true);
      const formattedDate = existingAppointment.date;

      // Delete the existing appointment from Firestore
      const appointmentsRef = doc(db, "appointments", formattedDate);
      const updates = {};
      updates[existingAppointment.id] = deleteField();
      await updateDoc(appointmentsRef, updates);

      // Send cancellation notifications using existing system
      try {
        const formattedDateLong = formatDateLong(formattedDate);
        const startTime = existingAppointment.startTime || existingAppointment.time || "N/A";
        const endTime = existingAppointment.endTime || "N/A";
        const appointmentWindow = buildNotificationWindow(formattedDateLong, startTime, endTime);
        const reason = "Rescheduled";
        const reasonSentence = `Reason: ${reason}.`;
        
        // Get patient info
        const patientDoc = await getDoc(doc(db, "users", currentUser.uid));
        let patientName = "Patient";
        let patientEmail = currentUser.email;
        
        if (patientDoc.exists()) {
          const patientData = patientDoc.data();
          patientName = patientData.firstName && patientData.lastName 
            ? `${patientData.firstName} ${patientData.lastName}` 
            : (patientData.name || existingAppointment.patientName || "Patient");
          patientEmail = patientData.email || patientData.authEmail || currentUser.email;
        }

        // Format services
        const servicesRaw = existingAppointment.services || [];
        const services = Array.isArray(servicesRaw) 
          ? servicesRaw.map(service => {
              if (typeof service === 'string') {
                return service;
              } else if (service && typeof service === 'object') {
                return service.name || service.serviceName || JSON.stringify(service);
              }
              return String(service);
            })
          : [];

        // Helper function to send email cancellation notification
        const sendEmailCancellationNotification = async (type, recipientEmail, recipientName = null, recipientRole = null) => {
          try {
            const apiUrl = process.env.REACT_APP_API_URL || '';
            const response = await fetch(`${apiUrl}/api/send-appointment-cancellation`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                type,
                recipientEmail,
                recipientName: recipientName || (type === 'patient' ? patientName : 'Doctor'),
                recipientRole: recipientRole || (type === 'patient' ? null : 'ClinicStaff'),
                patientName,
                appointmentDate: formattedDateLong,
                startTime,
                endTime: endTime !== "N/A" ? endTime : startTime,
                dentistName: existingAppointment.dentist || 'N/A',
                services,
                reason: reason,
                actorName: patientName,
                clinicName: 'VSDiamond Dental Clinic'
              })
            });
            const result = await response.json();
            if (!result.success) {
              console.error(`Failed to send cancellation email notification to ${type}:`, result.message);
            }
          } catch (error) {
            console.error(`Error sending cancellation email notification to ${type}:`, error);
          }
        };

        // Notify the patient
        const patientMessage = `Your appointment on ${appointmentWindow} has been cancelled. ${reasonSentence}`;
        await addNotification(currentUser.uid, patientMessage);
        
        // Send email notification to patient if their email is verified
        const patientData = patientDoc.exists() ? patientDoc.data() : {};
        const patientEmailVerified = patientData.emailVerified === "Yes" || patientData.emailVerified === true;
        if (patientEmailVerified && patientEmail && !patientEmail.includes("@mobileuser.dentalapp.com")) {
          sendEmailCancellationNotification('patient', patientEmail, patientName);
        }

        // Notify the dentist assigned to this appointment
        const dentistUid = existingAppointment.dentistId || null;
        if (dentistUid) {
          const dentistMessage = `Your upcoming patient appointment for ${patientName} on ${appointmentWindow} has been cancelled. ${reasonSentence}`;
          await addNotification(dentistUid, dentistMessage);
          
          // Send email notification to dentist if their email is verified
          const dentistDoc = await getDoc(doc(db, "users", dentistUid));
          if (dentistDoc.exists()) {
            const dentistData = dentistDoc.data();
            const dentistEmailVerified = dentistData.emailVerified === "Yes" || dentistData.emailVerified === true;
            const dentistEmail = dentistData.email || dentistData.authEmail;
            if (dentistEmailVerified && dentistEmail && !dentistEmail.includes("@mobileuser.dentalapp.com")) {
              const dentistDisplayName = dentistData.firstName && dentistData.lastName 
                ? `${dentistData.firstName} ${dentistData.lastName}` 
                : (dentistData.name || "Doctor");
              const dentistRole = dentistData.userType || dentistData.role;
              sendEmailCancellationNotification('dentist', dentistEmail, dentistDisplayName, dentistRole);
            }
          }
        }

        // ClinicStaff only (assigned dentist notified above; other dentists do not get cancellation alerts)
        const usersRef = collection(db, "users");
        const usersSnapshot = await getDocs(usersRef);
        const clinicNotificationMessage = `The appointment for ${patientName} on ${appointmentWindow} has been cancelled. ${reasonSentence}`;
        
        usersSnapshot.forEach((docu) => {
          const data = docu.data();
          const userId = docu.id;
          const userRole = data.userType || data.role;
          
          // Skip the assigned dentist (already notified above)
          if (dentistUid && userId === dentistUid) {
            return;
          }
          
          if (userRole === "ClinicStaff") {
            addNotification(userId, clinicNotificationMessage);
            
            // Send email notification if staff's email is verified
            const staffEmailVerified = data.emailVerified === "Yes" || data.emailVerified === true;
            const staffEmail = data.email || data.authEmail;
            if (staffEmailVerified && staffEmail && !staffEmail.includes("@mobileuser.dentalapp.com")) {
              const staffName = data.firstName && data.lastName 
                ? `${data.firstName} ${data.lastName}` 
                : (data.name || "Staff");
              sendEmailCancellationNotification('dentist', staffEmail, staffName, userRole);
            }
          }
        });
      } catch (notificationError) {
        console.error("Error sending cancellation notifications:", notificationError);
        // Don't block the flow if notification fails
      }

      // Close the existing appointment modal
      setShowExistingAppointmentModal(false);
      setExistingAppointment(null);

      // Stay on the booking screen; patient selects a new time slot (no Review modal)
      setShowBookingSummary(false);
      setActiveStep(0);
      setSelectedTimeSlot(null);
    } catch (error) {
      console.error("Error cancelling existing appointment:", error);
      setBookingStatus("Error cancelling existing appointment. Please try again.");
      setSnackbarSeverity("error");
      setShowBookingSnackbar(true);
    } finally {
      setIsBookingLoading(false);
    }
  };

  // Updated: Assign a random available dentist if none selected
  const submitAppointment = async (hasInsurance, insuranceDetails = null) => {
    
    const formattedDate = new Date(
      selectedDateAppointment.toDate().getTime() - selectedDateAppointment.toDate().getTimezoneOffset() * 60000
    )
      .toISOString()
      .split("T")[0];

    // Use a unique ID for each appointment
    const appointmentId = `${currentUser.uid}_${Date.now()}`;

    let assignedDentist = selectedDentist;
    if (!assignedDentist || assignedDentist === "") {
      setBookingStatus("Please select a dentist.");
        return;
    }

    // Find the correct dentist object to ensure proper name and ID
    const dentistObj = dentists.find(d => d.name === assignedDentist);
    if (!dentistObj) {
      setBookingStatus("Error: Selected dentist not found.");
      return;
    }

    // Note: Service validation is handled by the useEffect hook in real-time
    // If services were removed, the useEffect already filtered them and showed the error
    // Just ensure we have at least one service selected
    if (selectedServices.length === 0) {
      return; // Error already shown by useEffect
    }
    const selectedSlotFromCurrentList = selectedTimeSlot
      ? timeSlots.find((slot) => slot.id === selectedTimeSlot.id)
      : null;
    const isSelectedSlotValid =
      !!selectedSlotFromCurrentList &&
      selectedSlotFromCurrentList.isAvailable === true &&
      selectedSlotFromCurrentList.isUnavailable !== true;
    if (!isSelectedSlotValid) {
      setSelectedTimeSlot(null);
      setBookingStatus("Please select a time slot.");
      setSnackbarSeverity("error");
      setShowBookingSnackbar(true);
      return;
    }

    // Calculate total duration from selected services
    const totalDuration = selectedServices.reduce((total, serviceName) => {
      const service = services.find(s => s.name === serviceName);
      const duration = getServiceDurationMinutes(service);
      if (duration) return total + duration;
      return total;
    }, 0);

    // If no duration found in services, default to 30 minutes
    const appointmentDuration = totalDuration > 0 ? totalDuration : 30;
    const selectedDentistContext = getSelectedDentistLinkedTimelineContext();
    const precheckConflictMessage = evaluateDurationCompressionPrecheck({
      selectedSlotFromCurrentList,
      appointmentDuration,
      selectedDentistContext,
    });
    if (precheckConflictMessage) {
      setBookingStatus(precheckConflictMessage);
      setSnackbarSeverity("error");
      setShowBookingSnackbar(true);
      return;
    }
    // Same as Review-step validation: precheck is sufficient; avoid contiguous-row cap after absorbing pendings.

    const maxContiguousEndForSubmit = computeMaxContiguousEndFromSlots(
      timeSlots,
      selectedSlotFromCurrentList.start
    );
    const maxBookableDurationForSubmit =
      maxContiguousEndForSubmit - selectedSlotFromCurrentList.start;
    const allowPastClosingForLastSlotSubmit = isLastDisplayedScheduleSlot(
      selectedSlotFromCurrentList,
      timeSlots
    );

    const endTimeMinutes = selectedSlotFromCurrentList.start + appointmentDuration;
    const endTime = formatTime(endTimeMinutes);
    const timelineDentistIds = new Set([
      dentistObj.id,
      ...(selectedDentistContext.linkedRosterUid
        ? [selectedDentistContext.linkedRosterUid]
        : []),
    ]);

    // Helper function to convert time strings to minutes for overlap checking in this submit path.
    // Must be declared before maxBenchInheritedEndForTransaction — inner binding must not be used before init (TDZ).
    const submitTimeToMinutes = (timeStr) => {
      if (!timeStr) return 0;
      
      // If in 12-hour format (H:MM AM/PM), convert to 24-hour first
      if (timeStr.match(/^\d{1,2}:\d{2}\s?(AM|PM)$/i)) {
        const [time, period] = timeStr.split(/\s?(AM|PM)$/i);
        const [hours, minutes] = time.split(':').map(Number);
        let hour24 = hours;
        
        if (period.toUpperCase() === 'PM' && hour24 !== 12) {
          hour24 += 12;
        } else if (period.toUpperCase() === 'AM' && hour24 === 12) {
          hour24 = 0;
        }
        
        return hour24 * 60 + minutes;
      }
      
      // If already in 24-hour format (HH:MM), parse directly
      if (timeStr.match(/^\d{1,2}:\d{2}$/)) {
        const [hours, minutes] = timeStr.split(':').map(Number);
        return hours * 60 + minutes;
      }
      
      return 0;
    };

    let mergedBenchCoverageForTransaction = [];
    let maxBenchInheritedEndForTransaction = null;
    if (
      isChairCapacityConfigured(chairSettings) &&
      !isDentistOnRoster(dentistObj.id, chairSettings)
    ) {
      mergedBenchCoverageForTransaction = getBenchSwapCoverageRanges({
        swapsBySlot: chairSwapsBySlotForDate,
        benchUid: dentistObj.id,
        timeToMinutes: submitTimeToMinutes,
      });
      if (mergedBenchCoverageForTransaction.length > 0) {
        maxBenchInheritedEndForTransaction = Math.max(
          ...mergedBenchCoverageForTransaction.map((r) => r.end)
        );
      }
    }

    // Enhanced appointment data with industry best practices
    const appointmentData = {
      // Core appointment details
      appointmentId: appointmentId,
      patientId: currentUser.uid,
      patientEmail: currentUser.email,
      patientName: userDetails ? `${userDetails.firstName} ${userDetails.lastName}` : "Unknown",
      
      // Appointment scheduling
      date: formattedDate,
      duration: appointmentDuration, // Calculated from selected services' durations
      startTime: formatTime(selectedSlotFromCurrentList.start),
      endTime: endTime,
      
      // Services and provider
      services: selectedServices, // Already validated by useEffect hook
      dentist: dentistObj.name, // Use the correct dentist name from the dentist object
      dentistId: dentistObj.id, // Use the correct dentist ID
      
      // Status and metadata
      status: "Pending",
      
      // Insurance information
      hasInsurance: hasInsurance,
      insuranceDetails: hasInsurance ? insuranceDetails : null
    };

    try {
      // Use a transaction to atomically check and save, preventing race conditions
      const appointmentRef = doc(db, "appointments", formattedDate);
      const startTimeToCheck = appointmentData.startTime;
      const endTimeToCheck = appointmentData.endTime;
      
      // Convert times to minutes for overlap checking
      const newApptStartMinutes = submitTimeToMinutes(startTimeToCheck);
      const newApptEndMinutes = submitTimeToMinutes(endTimeToCheck);
      
      await runTransaction(db, async (transaction) => {
        // Read the current state of the appointments document
        const appointmentDoc = await transaction.get(appointmentRef);
        
        let existingAppointments = {};
        if (appointmentDoc.exists()) {
          existingAppointments = appointmentDoc.data() || {};
        }
        
        // First, check if this exact appointment ID already exists (shouldn't happen, but safety check)
        if (existingAppointments[appointmentId]) {
          throw new Error("DIFFERENT_PATIENT"); // Treat as conflict
        }

        for (const [, apt] of Object.entries(existingAppointments)) {
          if (!apt || typeof apt !== "object") continue;
          const st = (apt.status || "").toLowerCase();
          if (st === "cancelled" || st === "canceled") continue;
          const du = apt.dentistId || null;
          if (!du || !timelineDentistIds.has(du) || !appointmentBelongsToDateDoc(apt, formattedDate))
            continue;
          if (apt.patientId !== currentUser.uid) continue;
          const es = submitTimeToMinutes(apt.startTime || apt.time);
          const ee = apt.endTime
            ? submitTimeToMinutes(apt.endTime)
            : es + (apt.duration || 30);
          if (newApptStartMinutes < ee && newApptEndMinutes > es) {
            throw new Error("SAME_PATIENT");
          }
        }

        const snapshotTimeline = Object.values(existingAppointments)
          .map((a) => withAppointmentDocDate(a, formattedDate))
          .filter(
            (a) =>
              a &&
              typeof a === "object" &&
              a.dentistId &&
              timelineDentistIds.has(a.dentistId) &&
              appointmentBelongsToDateDoc(a, formattedDate) &&
              !["cancelled", "canceled"].includes((a.status || "").toLowerCase())
          );

        const fixedCompletedRangesTxn = snapshotTimeline
          .filter((a) => isCompletedLikeStatus(a.status))
          .map((a) => {
            const start = submitTimeToMinutes(a.startTime || a.time);
            const end = a.endTime
              ? submitTimeToMinutes(a.endTime)
              : start + (a.duration || 30);
            return { start, end };
          })
          .filter((r) => r.end > r.start);

        const fixedConfirmedRangesTxn = snapshotTimeline
          .filter(
            (a) =>
              !isCompressiblePendingStatus(a.status) && !isCompletedLikeStatus(a.status)
          )
          .map((a) => {
            const start = submitTimeToMinutes(a.startTime || a.time);
            const end = a.endTime
              ? submitTimeToMinutes(a.endTime)
              : start + (a.duration || 30);
            return { start, end };
          })
          .filter((r) => r.end > r.start);

        const pendingRangesTxn = snapshotTimeline
          .filter((a) => isCompressiblePendingStatus(a.status))
          .map((a) => {
            const start = submitTimeToMinutes(a.startTime || a.time);
            const end = a.endTime
              ? submitTimeToMinutes(a.endTime)
              : start + (a.duration || 30);
            return { start, end, duration: Math.max(1, end - start) };
          })
          .filter((r) => r.end > r.start)
          .sort((a, b) => a.start - b.start);

        const fixedUnavailableRangesTxn = mergeMinuteRanges(
          (Array.isArray(timeSlots) ? timeSlots : [])
            .filter(
              (slot) =>
                slot &&
                typeof slot.start === "number" &&
                typeof slot.end === "number" &&
                slot.isUnavailable === true &&
                // Only hard-block statuses are treated as fixed unavailable in
                // the transaction. "chair-full-soft" (pending-only) is excluded:
                // those pending appointments are already captured in
                // pendingRangesTxn and will be pushed by compression, so they
                // must not also appear as fixed hard blocks.
                (slot.slotStatus === "unavailable" || slot.slotStatus === "chair-full")
            )
            .map((slot) => ({ start: slot.start, end: slot.end }))
        );

        const isBenchPatientPrecheckTxn =
          isChairCapacityConfigured(chairSettings) &&
          dentistObj.id &&
          !isDentistOnRoster(dentistObj.id, chairSettings);

        const containingBenchRangeTxn =
          mergedBenchCoverageForTransaction.find(
            (r) =>
              newApptStartMinutes >= r.start &&
              newApptStartMinutes < r.end
          ) || null;
        const benchContainingEndTxn =
          containingBenchRangeTxn != null &&
          typeof containingBenchRangeTxn.end === "number" &&
          !Number.isNaN(containingBenchRangeTxn.end)
            ? containingBenchRangeTxn.end
            : maxBenchInheritedEndForTransaction;

        const benchMayExtendPastInheritedCloseTxn =
          isBenchPatientPrecheckTxn &&
          typeof containingBenchRangeTxn?.end === "number" &&
          !Number.isNaN(containingBenchRangeTxn.end) &&
          containingBenchRangeTxn.end >= STANDARD_OFFICE_GRID_CLOSE_MINUTES;

        const unavailableRangesForPrecheckTxn =
          typeof maxBenchInheritedEndForTransaction === "number" &&
          !Number.isNaN(maxBenchInheritedEndForTransaction) &&
          (allowPastClosingForLastSlotSubmit || benchMayExtendPastInheritedCloseTxn)
            ? fixedUnavailableRangesTxn.filter(
                (r) => r.start < maxBenchInheritedEndForTransaction
              )
            : fixedUnavailableRangesTxn;

        const benchHardEndCapMinutesTxn =
          isBenchPatientPrecheckTxn &&
          containingBenchRangeTxn != null &&
          typeof benchContainingEndTxn === "number" &&
          !Number.isNaN(benchContainingEndTxn) &&
          !benchMayExtendPastInheritedCloseTxn
            ? benchContainingEndTxn
            : null;

        if (effectiveChairCount > 0) {
          const daySnapForChair = Object.entries(existingAppointments)
            .map(([id, ap]) => withAppointmentDocDate({ ...ap, id }, formattedDate))
            .filter((ap) => {
              const s = ((ap.status || "") + "").trim().toLowerCase();
              return s !== "cancelled" && s !== "canceled";
            });
          const chairPlanTxn = planPatientBookingOnChairPlot({
            appointments: daySnapForChair,
            chairCount: effectiveChairCount,
            scheduleDocData: dentistSchedulesForDate,
            bookingDentistId: dentistObj.id,
            newStartMin: newApptStartMinutes,
            newEndMin: newApptEndMinutes,
          });
          if (!chairPlanTxn.ok && chairPlanTxn.message) {
            throw new Error(`COMPRESSION_PRECHECK:${chairPlanTxn.message}`);
          }
        }

        const compressionFailTxn = getCompressionShiftFailureMessage({
          newRange: { start: newApptStartMinutes, end: newApptEndMinutes },
          pendingRanges: pendingRangesTxn,
          fixedConfirmedRanges: fixedConfirmedRangesTxn,
          fixedCompletedRanges: fixedCompletedRangesTxn,
          unavailableRangesForPrecheck: unavailableRangesForPrecheckTxn,
          isBenchPatientPrecheck: isBenchPatientPrecheckTxn,
          benchHardEndCapMinutes: benchHardEndCapMinutesTxn,
        });
        if (compressionFailTxn) {
          throw new Error(`COMPRESSION_PRECHECK:${compressionFailTxn}`);
        }
        
        // Check if any existing appointment conflicts with this time slot for the same dentist
        for (const [existingAppointmentId, existingAppointment] of Object.entries(existingAppointments)) {
          // Skip if this is not a valid appointment object
          if (!existingAppointment || typeof existingAppointment !== 'object') {
            continue;
          }
          
          // Skip cancelled appointments (they don't block new bookings)
          // Completed appointments should block bookings like confirmed ones
          const status = existingAppointment.status || "";
          const statusLower = status.toLowerCase();
          if (statusLower === "cancelled") {
            continue;
          }
          
          // Check if appointment belongs to the same linked timeline and date
          const existingDentistId = existingAppointment.dentistId || null;
          const isSameDentist = existingDentistId === dentistObj.id;
          const isInLinkedTimeline =
            existingDentistId && timelineDentistIds.has(existingDentistId);
          const isSameDate = appointmentBelongsToDateDoc(existingAppointment, formattedDate);
          
          if (isInLinkedTimeline && isSameDate) {
            // Get existing appointment's time range in minutes
            const existingStartMinutes = submitTimeToMinutes(existingAppointment.startTime || existingAppointment.time);
            const existingEndMinutes = existingAppointment.endTime 
              ? submitTimeToMinutes(existingAppointment.endTime)
              : existingStartMinutes + (existingAppointment.duration || 30);
            
            // Check for exact match first (same start and end time)
            const isExactMatch = existingAppointment.startTime === startTimeToCheck && 
                                 existingAppointment.endTime === endTimeToCheck;
            
            // Check for overlap: appointments overlap when newStart < existingEnd AND newEnd > existingStart
            const overlaps = newApptStartMinutes < existingEndMinutes && newApptEndMinutes > existingStartMinutes;
            
            if (isExactMatch || overlaps) {
              // Check if it's the same patient trying to book again
              if (existingAppointment.patientId === currentUser.uid) {
                throw new Error("SAME_PATIENT");
              } else {
                // Different patient has already booked this slot (exact match or overlapping)
                // Check if the conflicting appointment is pending or confirmed
                const conflictingStatus = (existingAppointment.status || "").toLowerCase();
                
                if (isCompressiblePendingStatus(existingAppointment.status)) {
                  // Stacked pending/new compression vs confirmed/reserved/unavailable/max bench: validated on snapshot above.
                  continue;
                } else {
                  if (existingDentistId && existingDentistId !== dentistObj.id) {
                    throw new Error("LINKED_DENTIST_CONFLICT");
                  }
                  if (conflictingStatus === "completed") {
                    throw new Error("COMPLETED_APPT_OVERLAP");
                  }
                  // Conflicting appointment is confirmed or completed (non-pending status)
                  // Check if the new appointment's duration exceeds the selected time slot
                  if (
                    !allowPastClosingForLastSlotSubmit &&
                    shouldApplyContiguousAvailableDurationCap(dentistObj?.id) &&
                    maxBookableDurationForSubmit > 0 &&
                    appointmentDuration > maxBookableDurationForSubmit &&
                    !durationAllowedVersusContiguousGridCap({
                      totalMinutes: appointmentDuration,
                      startMinutes: selectedSlotFromCurrentList.start,
                      maxContiguousEnd: maxContiguousEndForSubmit,
                      timeSlots,
                      allowPastClosingForLastSlot: allowPastClosingForLastSlotSubmit,
                      dentistUidForCap: dentistObj?.id,
                    })
                  ) {
                    // Appointment duration exceeds the selected time slot
                    // Format the conflicting appointment's time slot for the error message
                    const conflictingStartTime = formatTime(existingStartMinutes);
                    const conflictingEndTime = formatTime(existingEndMinutes);
                    const conflictingTimeSlotStr = `${conflictingStartTime} - ${conflictingEndTime}`;
                    
                    // Calculate hours and minutes for duration display
                    const hours = Math.floor(appointmentDuration / 60);
                    const minutes = appointmentDuration % 60;
                    let durationStr = "";
                    if (hours > 0 && minutes > 0) {
                      durationStr = `${hours} hour${hours > 1 ? 's' : ''} and ${minutes} minute${minutes > 1 ? 's' : ''}`;
                    } else if (hours > 0) {
                      durationStr = `${hours} hour${hours > 1 ? 's' : ''}`;
                    } else {
                      durationStr = `${minutes} minute${minutes > 1 ? 's' : ''}`;
                    }
                    
                    throw new Error(`DURATION_EXCEEDS_SLOT:${selectedServices.length}:${durationStr}|${conflictingTimeSlotStr}`);
                  } else {
                    // Duration doesn't exceed slot, but slot is already reserved
                    throw new Error("DIFFERENT_PATIENT");
                  }
                }
              }
            }
          }
        }
        
        // No conflicts found, add the new appointment to the existing data
        const updatedAppointments = {
          ...existingAppointments,
          [appointmentId]: appointmentData
        };
        
        // Double-check: Verify no conflict was introduced in our update
        // This is a safety check to ensure we didn't miss anything
        for (const [checkAppointmentId, checkAppointment] of Object.entries(updatedAppointments)) {
          if (checkAppointmentId === appointmentId) continue; // Skip the one we're adding
          
          if (!checkAppointment || typeof checkAppointment !== 'object') continue;
          
          const checkStatus = checkAppointment.status || "";
          const checkStatusLower = checkStatus.toLowerCase();
          if (checkStatusLower === "cancelled") continue;
          
          // Check if this existing appointment conflicts with the new one
          if (
            checkAppointment.dentistId &&
            timelineDentistIds.has(checkAppointment.dentistId) &&
            appointmentBelongsToDateDoc(checkAppointment, formattedDate)
          ) {
            // Get existing appointment's time range in minutes
            const checkStartMinutes = submitTimeToMinutes(checkAppointment.startTime || checkAppointment.time);
            const checkEndMinutes = checkAppointment.endTime 
              ? submitTimeToMinutes(checkAppointment.endTime)
              : checkStartMinutes + (checkAppointment.duration || 30);
            
            // Check for exact match
            const isExactMatch = checkAppointment.startTime === startTimeToCheck && 
                                 checkAppointment.endTime === endTimeToCheck;
            
            // Check for overlap
            const overlaps = newApptStartMinutes < checkEndMinutes && newApptEndMinutes > checkStartMinutes;
            
            if (isExactMatch || overlaps) {
              // Conflict detected in our update - this shouldn't happen, but handle it
              if (checkAppointment.patientId === currentUser.uid) {
                throw new Error("SAME_PATIENT");
              } else {
                // Different patient has already booked this slot (exact match or overlapping)
                // Check if the conflicting appointment is pending or confirmed
                const conflictingStatus = (checkAppointment.status || "").toLowerCase();
                
                if (isCompressiblePendingStatus(checkAppointment.status)) {
                  continue;
                } else {
                  if (checkAppointment.dentistId !== dentistObj.id) {
                    throw new Error("LINKED_DENTIST_CONFLICT");
                  }
                  if (conflictingStatus === "completed") {
                    throw new Error("COMPLETED_APPT_OVERLAP");
                  }
                  // Conflicting appointment is confirmed or completed (non-pending status)
                  // Check if the new appointment's duration exceeds the selected time slot
                  if (
                    !allowPastClosingForLastSlotSubmit &&
                    shouldApplyContiguousAvailableDurationCap(dentistObj?.id) &&
                    maxBookableDurationForSubmit > 0 &&
                    appointmentDuration > maxBookableDurationForSubmit &&
                    !durationAllowedVersusContiguousGridCap({
                      totalMinutes: appointmentDuration,
                      startMinutes: selectedSlotFromCurrentList.start,
                      maxContiguousEnd: maxContiguousEndForSubmit,
                      timeSlots,
                      allowPastClosingForLastSlot: allowPastClosingForLastSlotSubmit,
                      dentistUidForCap: dentistObj?.id,
                    })
                  ) {
                    // Appointment duration exceeds the selected time slot
                    // Format the conflicting appointment's time slot for the error message
                    const conflictingStartTime = formatTime(checkStartMinutes);
                    const conflictingEndTime = formatTime(checkEndMinutes);
                    const conflictingTimeSlotStr = `${conflictingStartTime} - ${conflictingEndTime}`;
                    
                    // Calculate hours and minutes for duration display
                    const hours = Math.floor(appointmentDuration / 60);
                    const minutes = appointmentDuration % 60;
                    let durationStr = "";
                    if (hours > 0 && minutes > 0) {
                      durationStr = `${hours} hour${hours > 1 ? 's' : ''} and ${minutes} minute${minutes > 1 ? 's' : ''}`;
                    } else if (hours > 0) {
                      durationStr = `${hours} hour${hours > 1 ? 's' : ''}`;
                    } else {
                      durationStr = `${minutes} minute${minutes > 1 ? 's' : ''}`;
                    }
                    
                    throw new Error(`DURATION_EXCEEDS_SLOT:${selectedServices.length}:${durationStr}|${conflictingTimeSlotStr}`);
                  } else {
                    // Duration doesn't exceed slot, but slot is already reserved
                    throw new Error("DIFFERENT_PATIENT");
                  }
                }
              }
            }
          }
        }
        
        // Set the entire document with the new appointment added
        // Using merge: false ensures we're writing the complete state atomically
        transaction.set(appointmentRef, updatedAppointments, { merge: false });
      });

      // Optimistic compress so the schedule updates before the Firestore round-trip.
      setAppointments((prev) => {
        const newRow = withAppointmentDocDate(
          { ...appointmentData, id: appointmentId },
          formattedDate
        );
        const merged = [
          ...prev.filter(
            (a) =>
              !(
                appointmentBelongsToDateDoc(a, formattedDate) &&
                a.dentistId &&
                timelineDentistIds.has(a.dentistId) &&
                a.id === appointmentId
              )
          ),
          newRow,
        ];
        const dayRows = merged.filter(
          (a) =>
            a?.dentistId &&
            timelineDentistIds.has(a.dentistId) &&
            appointmentBelongsToDateDoc(a, formattedDate) &&
            !["cancelled", "canceled"].includes((a.status || "").toLowerCase())
        );
        const { updates } = computeDayTimelineCompressionShiftUpdates(
          dayRows,
          formattedDate,
          { dentistIdAllowlist: timelineDentistIds }
        );
        return applyTimelineShiftUpdatesToRows(merged, updates);
      });

      // Auto-adjust overlapping Pending appointments
      // Only adjust if both appointments are Pending (not Confirmed)
      let wasNewAppointmentAdjusted = false;
      let adjustedStartTime = appointmentData.startTime;
      let adjustedEndTime = appointmentData.endTime;
      const originalStartTime = appointmentData.startTime; // Store original before any adjustments
      const originalEndTime = appointmentData.endTime; // Store original before any adjustments
      
      try {
        const appointmentDoc = await getDoc(appointmentRef);
        if (appointmentDoc.exists()) {
          const allAppointments = appointmentDoc.data() || {};
          const timelineApps = Object.entries(allAppointments)
            .map(([id, ap]) =>
              withAppointmentDocDate({ ...ap, id }, formattedDate)
            )
            .filter(
              (ap) =>
                ap &&
                ap.dentistId &&
                timelineDentistIds.has(ap.dentistId) &&
                appointmentBelongsToDateDoc(ap, formattedDate) &&
                !["cancelled", "canceled"].includes((ap.status || "").toLowerCase())
            );

          const { updates: shiftUpdates, exceedsDayEnd } =
            computeDayTimelineCompressionShiftUpdates(timelineApps, formattedDate, {
              dentistIdAllowlist: timelineDentistIds,
            });

          if (exceedsDayEnd) {
            console.error(
              "compressible timeline reconcile exceeded 11:59 PM after booking (unexpected)"
            );
          }

          let chairPushUpdates = [];
          let chairSourceAppointments = timelineApps;
          if (isPlottingEnabledForDate && effectiveChairCount > 0) {
            chairSourceAppointments = Object.entries(allAppointments)
              .map(([id, ap]) => withAppointmentDocDate({ ...ap, id }, formattedDate))
              .filter((ap) => {
                const s = ((ap.status || "") + "").trim().toLowerCase();
                return s !== "cancelled" && s !== "canceled";
              });
            const chairPlanAfterSave = planPatientBookingOnChairPlot({
              appointments: chairSourceAppointments,
              chairCount: effectiveChairCount,
              scheduleDocData: dentistSchedulesForDate,
              bookingDentistId: dentistObj.id,
              newStartMin: submitTimeToMinutes(appointmentData.startTime),
              newEndMin: submitTimeToMinutes(appointmentData.endTime),
            });
            chairPushUpdates = chairPlanAfterSave.pushUpdates || [];
          }

          const mergedShiftById = new Map();
          for (const u of shiftUpdates) {
            if (u?.id) mergedShiftById.set(u.id, u);
          }
          for (const u of chairPushUpdates) {
            if (u?.id && !mergedShiftById.has(u.id)) {
              mergedShiftById.set(u.id, u);
            }
          }
          const allShiftUpdates = [...mergedShiftById.values()];

          if (allShiftUpdates.length > 0) {
            const sourceById = new Map(
              [...timelineApps, ...chairSourceAppointments].map((ap) => [ap.id, ap])
            );
            const mergedSource = [...sourceById.values()];
            const appointmentsToAdjust = await applyCompressibleTimelineShiftsAndNotify({
              appointmentRef,
              chainUpdates: allShiftUpdates,
              sourceAppointments: mergedSource,
              formattedDate,
            });

            const selfAdj = appointmentsToAdjust.find((row) => row.id === appointmentId);
            if (selfAdj) {
              wasNewAppointmentAdjusted = true;
              adjustedStartTime = selfAdj.startTime;
              adjustedEndTime = selfAdj.endTime;
              appointmentData.startTime = selfAdj.startTime;
              appointmentData.endTime = selfAdj.endTime;
            }
          }

          if (effectiveChairCount > 0) {
            const dayAppsForChairPush = Object.entries(allAppointments)
              .map(([id, ap]) => withAppointmentDocDate({ ...ap, id }, formattedDate))
              .filter((ap) => {
                const s = ((ap.status || "") + "").trim().toLowerCase();
                return s !== "cancelled" && s !== "canceled";
              });
            const chairPlanAfterSave = planPatientBookingOnChairPlot({
              appointments: dayAppsForChairPush,
              chairCount: effectiveChairCount,
              scheduleDocData: dentistSchedulesForDate,
              bookingDentistId: dentistObj.id,
              newStartMin: submitTimeToMinutes(appointmentData.startTime),
              newEndMin: submitTimeToMinutes(appointmentData.endTime),
            });
            if (chairPlanAfterSave.pushUpdates?.length > 0) {
              await applyCompressibleTimelineShiftsAndNotify({
                appointmentRef,
                chainUpdates: chairPlanAfterSave.pushUpdates,
                sourceAppointments: dayAppsForChairPush,
                formattedDate,
              });
            }
          }
        }
      } catch (adjustError) {
        // Log error but don't fail the booking - auto-adjustment is a convenience feature
        console.error("Error auto-adjusting overlapping appointments:", adjustError);
      }

      // Update patient's user document with insurance information
      const patientRef = doc(db, "users", currentUser.uid);
      
      if (hasInsurance && insuranceDetails) {
        await setDoc(patientRef, {
          hasInsurance: true,
          insuranceDetails: insuranceDetails
        }, { merge: true });
      } else {
        // Update to indicate no insurance
        await setDoc(patientRef, {
          hasInsurance: false,
          insuranceDetails: null
        }, { merge: true });
      }

      // --- Notify assigned dentist and all ClinicStaff ---
      const usersRef = collection(db, "users");
      const usersSnapshot = await getDocs(usersRef);
      const patientName = userDetails ? `${userDetails.firstName} ${userDetails.lastName}` : "A patient";
      
      // Format date as "October 31, 2025"
      const formattedDateLong = formatDateLong(formattedDate);
      
      // Format start and end times separately
      // Use adjusted times if the appointment was auto-adjusted, otherwise use original calculated times
      const startTime = wasNewAppointmentAdjusted ? adjustedStartTime : (selectedTimeSlot ? formatTime(selectedTimeSlot.start) : "N/A");
      const notificationEndTime = wasNewAppointmentAdjusted ? adjustedEndTime : endTime; // Use adjusted endTime if appointment was adjusted
      
      // Format insurance information
      const insuranceText = hasInsurance ? (insuranceDetails?.companyName || "Yes") : "None";
      
      const appointmentWindow = buildNotificationWindow(formattedDateLong, startTime, notificationEndTime);
      const notificationMessage = `A patient ${patientName} booked an appointment on ${appointmentWindow}.\n\nInsurance: ${insuranceText}\nStatus: Pending`;

      // Helper function to send email notification
      const sendEmailNotification = async (type, recipientEmail, recipientName = null, recipientRole = null) => {
        try {
          const apiUrl = process.env.REACT_APP_API_URL || '';
          const response = await fetch(`${apiUrl}/api/send-appointment-notification`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              type,
              recipientEmail,
              recipientName: recipientName || (type === 'patient' ? patientName : 'Doctor'),
              recipientRole: recipientRole || (type === 'patient' ? null : 'ClinicStaff'),
              patientName,
              appointmentDate: formattedDateLong,
              startTime,
              endTime: notificationEndTime,
              dentistName: dentistObj.name,
              services: selectedServices,
              insuranceText,
              clinicName: 'VSDiamond Dental Clinic'
            })
          });
          const result = await response.json();
          if (!result.success) {
            console.error(`Failed to send email notification to ${type}:`, result.message);
          }
        } catch (error) {
          console.error(`Error sending email notification to ${type}:`, error);
        }
      };

      usersSnapshot.forEach((docu) => {
        const data = docu.data();
        const userId = docu.id;
        const userRole = data.userType || data.role;
        
        // Notify the assigned dentist (DentistOwner or AssociateDentist) if their UID matches
        if (
          (userRole === "DentistOwner" || userRole === "AssociateDentist") &&
          userId === dentistObj.id
        ) {
          addNotification(userId, notificationMessage);
          
          // Send email notification if dentist's email is verified
          const dentistEmailVerified = data.emailVerified === "Yes" || data.emailVerified === true;
          const dentistEmail = data.email || data.authEmail;
          if (dentistEmailVerified && dentistEmail && !dentistEmail.includes("@mobileuser.dentalapp.com")) {
            const dentistName = data.firstName && data.lastName 
              ? `${data.firstName} ${data.lastName}` 
              : (data.name || "Doctor");
            sendEmailNotification('dentist', dentistEmail, dentistName, userRole);
          }
        } else if (userRole === "ClinicStaff") {
          // Notify all ClinicStaff only (non-assigned dentist owners / associates do not get booking alerts)
          addNotification(userId, notificationMessage);
          
          // Send email notification if staff's email is verified
          const staffEmailVerified = data.emailVerified === "Yes" || data.emailVerified === true;
          const staffEmail = data.email || data.authEmail;
          if (staffEmailVerified && staffEmail && !staffEmail.includes("@mobileuser.dentalapp.com")) {
            const staffName = data.firstName && data.lastName 
              ? `${data.firstName} ${data.lastName}` 
              : (data.name || "Staff");
            sendEmailNotification('dentist', staffEmail, staffName, userRole);
          }
        }
      });
      // ---------------------------------------------------------------

      setBookingStatus("Appointment is pending. Please wait for your appointment to be confirmed.");
      setSnackbarSeverity("success");
      setShowBookingSnackbar(true);
      
      // Notify the patient about their successful booking
      // Reuse formattedDateLong, startTime, and notificationEndTime that were already declared above
      const insuranceTextPatient = hasInsurance ? (insuranceDetails?.companyName || "Yes") : "None";
      const patientNotificationMessage = `Your appointment on ${appointmentWindow} is currently pending while our clinic team verifies your booking. Please visit the clinic only after your appointment has been confirmed, and check your appointment status from time to time for updates.\n\nInsurance: ${insuranceTextPatient}`;
      addNotification(currentUser.uid, patientNotificationMessage);
      
      // Send email notification to patient if their email is verified
      const patientEmailVerified = userDetails?.emailVerified === "Yes" || userDetails?.emailVerified === true;
      const patientEmail = userDetails?.email || userDetails?.authEmail || currentUser.email;
      if (patientEmailVerified && patientEmail && !patientEmail.includes("@mobileuser.dentalapp.com")) {
        sendEmailNotification('patient', patientEmail, patientName);
      }
      
      setSelectedServices([]);
      setSelectedTimeSlot(null);
      // Keep selectedDentist so user can see their appointment in the time table
      
    } catch (error) {
      console.error("Error booking appointment:", error);
      console.error("Error details:", {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      
      // Handle specific error cases for duplicate bookings
      if (error.message === "SAME_PATIENT") {
        setBookingStatus("Appointment failed. You have already booked this time slot.");
        setSnackbarSeverity("error");
        setShowBookingSnackbar(true);
        setIsTermsAgreeLoading(false);
        return;
      } else if (error.message.startsWith("DURATION_EXCEEDS_SLOT:")) {
        setBookingStatus(
          "Booking failed. The total duration of the appointment overlaps with another confirmed patient appointment."
        );
        setSnackbarSeverity("error");
        setShowBookingSnackbar(true);
        setIsTermsAgreeLoading(false);
        return;
      } else if (error.message === "COMPLETED_APPT_OVERLAP") {
        setBookingStatus(completedPatientOverlapBookingMessage);
        setSnackbarSeverity("error");
        setShowBookingSnackbar(true);
        setIsTermsAgreeLoading(false);
        return;
      } else if (error.message.startsWith("CASCADE_OVERLAP:")) {
        setBookingStatus(
          "Booking failed. The total duration of the appointment overlaps with another confirmed patient appointment."
        );
        setSnackbarSeverity("error");
        setShowBookingSnackbar(true);
        setIsTermsAgreeLoading(false);
        return;
      } else if (error.message.startsWith("COMPRESSION_PRECHECK:")) {
        setBookingStatus(error.message.slice("COMPRESSION_PRECHECK:".length));
        setSnackbarSeverity("error");
        setShowBookingSnackbar(true);
        setIsTermsAgreeLoading(false);
        return;
      } else if (error.message === "DIFFERENT_PATIENT") {
        setBookingStatus("Appointment failed. The time slot is already reserved for another patient.");
        setSnackbarSeverity("error");
        setShowBookingSnackbar(true);
        setIsTermsAgreeLoading(false);
        return;
      } else if (error.message === "BENCH_PENDING_SHIFT_EXCEEDS_SWAP") {
        setBookingStatus(
          "Booking failed. The total duration of the appointment overlaps with an unavailable time slot."
        );
        setSnackbarSeverity("error");
        setShowBookingSnackbar(true);
        setIsTermsAgreeLoading(false);
        return;
      } else if (error.message === "LINKED_DENTIST_CONFLICT") {
        setBookingStatus(
          "Booking failed. The total duration of the appointment overlaps with another confirmed patient appointment."
        );
        setSnackbarSeverity("error");
        setShowBookingSnackbar(true);
        setIsTermsAgreeLoading(false);
        return;
      } else {
        setBookingStatus(`Error booking appointment: ${error.message}`);
        setSnackbarSeverity("error");
        setShowBookingSnackbar(true);
        setIsTermsAgreeLoading(false);
        return;
      }
    }
  };

  const [services, setServices] = useState([]); // Start with empty array - no services until dentist is selected

  // Load services from Firestore for selected dentist with real-time updates
  useEffect(() => {
    if (!selectedDentist) {
      // If no dentist selected, clear services
      setServices([]);
      return;
    }

    const dentist = dentists.find(d => d.name === selectedDentist);
    if (!dentist || !dentist.id) {
      setServices([]);
      return;
    }

    const servicesRef = doc(db, "services", dentist.id);
    
    // Set up real-time listener
    const unsubscribe = onSnapshot(
      servicesRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const firestoreServices = snapshot.data().servicesList;
          if (firestoreServices && firestoreServices.length > 0) {
            setServices(firestoreServices);
            return;
          }
        }
        // If no services found for this dentist, show empty array
        setServices([]);
      },
      (error) => {
        // Silently handle permission errors during sign-out
        if (error.code === 'permission-denied') {
          console.log('Services listener stopped due to sign-out');
          return;
        }
        console.error("Error loading services from Firestore:", error);
        // On error, clear services
        setServices([]);
      }
    );
    
    // Cleanup listener on unmount or when dependencies change
    return () => unsubscribe();
  }, [selectedDentist, dentists]);

  // Validate selectedServices against current services list - remove deleted services
  useEffect(() => {
    if (services.length === 0 || selectedServices.length === 0) {
      // If no services or no selected services, clear selectedServices
      if (services.length === 0 && selectedServices.length > 0) {
        const removedServices = [...selectedServices]; // Store services before clearing
        setSelectedServices([]);
        // Close any open modals
        setShowBookingSummary(false);
        setShowInsuranceModal(false);
        setShowTermsModal(false);
        setBookingStatus(`Selected service's (${removedServices.join(", ")}) are no longer available. Please select different services.`);
        setSnackbarSeverity("error");
        setShowBookingSnackbar(true);
      }
      return;
    }

    // Get list of available service names
    const availableServiceNames = services.map(service => service.name);
    
    // Filter out services that no longer exist
    const validSelectedServices = selectedServices.filter(serviceName => 
      availableServiceNames.includes(serviceName)
    );

    // If some services were removed, update selectedServices and notify user
    if (validSelectedServices.length !== selectedServices.length) {
      const removedServices = selectedServices.filter(serviceName => 
        !availableServiceNames.includes(serviceName)
      );
      setSelectedServices(validSelectedServices);
      // Close any open modals when services are deleted
      setShowBookingSummary(false);
      setShowInsuranceModal(false);
      setShowTermsModal(false);
      setBookingStatus(`Selected service's (${removedServices.join(", ")}) are no longer available. Please select different services.`);
      setSnackbarSeverity("error");
      setShowBookingSnackbar(true);
    }
  }, [services, selectedServices]);

  // Group services by category
  const groupedServices = services.reduce((acc, service) => {
    if (!acc[service.category]) {
      acc[service.category] = [];
    }
    acc[service.category].push(service);
    return acc;
  }, {});

  /** Per-service: if not already selected, can adding it stay within the same rules as Review/submit (compression + contiguous cap)? */
  const serviceDurationFeasibilityByName = useMemo(() => {
    const parseDur = (svc) => {
      if (!svc || !svc.duration) return 30;
      const d = getServiceDurationMinutes(svc);
      return d > 0 ? d : 30;
    };

    const parseDurFromName = (name) => {
      const svc = services.find((s) => s.name === name);
      return parseDur(svc);
    };

    const selectedSlotFromCurrentList = selectedTimeSlot
      ? timeSlots.find((slot) => slot.id === selectedTimeSlot.id)
      : null;
    const selectedSlotForFeasibility =
      selectedSlotFromCurrentList ||
      (selectedTimeSlot && typeof selectedTimeSlot.start === "number"
        ? selectedTimeSlot
        : null);
    const isSelectedSlotValid =
      !!selectedSlotForFeasibility &&
      (selectedSlotForFeasibility.isAvailable !== false) &&
      selectedSlotForFeasibility.isUnavailable !== true;

    const getBlockReason = (totalMinutes) => {
      if (typeof totalMinutes !== "number" || totalMinutes <= 0) {
        return "Invalid duration.";
      }
      if (!isSelectedSlotValid) return null;

      const selectedDentistContext = getSelectedDentistLinkedTimelineContext();
      const dentistId = selectedDentistContext?.dentistObj?.id;

      // Direct schedule-end guard: block any service combination whose total duration
      // would push the appointment end past the dentist's plotted working hours.
      // This is a hard check independent of the compression/shift simulation so it
      // catches all cases — including bench dentists whose shift-precheck strips the
      // schedule cap when pending appointments overlap the schedule boundary.
      const hasPlottedScheduleForDentist =
        dentistId && dentistHasPlottedSchedule(dentistSchedulesForDate, dentistId);
      if (hasPlottedScheduleForDentist) {
        if (dentistId) {
          const schedUnavail = getDentistScheduleUnavailableRanges(
            dentistSchedulesForDate,
            dentistId
          );
          if (schedUnavail && schedUnavail.length > 0) {
            const aptStart = selectedSlotForFeasibility.start;
            const aptEnd = aptStart + totalMinutes;
            for (const r of schedUnavail) {
              if (aptStart < r.end && aptEnd > r.start) {
                return "Booking failed. The selected services would push the appointment past the dentist's scheduled working hours.";
              }
            }
          }
        }
      }

      const compressionMsg = evaluateDurationCompressionPrecheck({
        selectedSlotFromCurrentList: selectedSlotForFeasibility,
        appointmentDuration: totalMinutes,
        selectedDentistContext,
      });
      if (compressionMsg) return compressionMsg;

      // Chair plot check: catches cross-chair same-dentist conflicts that
      // compression pre-check misses (e.g. booking pushes a same-dentist
      // appointment into another of their appointments on a different chair).
      if (effectiveChairCount > 0 && dentistId) {
        const selectedDateObj =
          selectedDateAppointment && typeof selectedDateAppointment.toDate === "function"
            ? selectedDateAppointment.toDate()
            : null;
        if (selectedDateObj) {
          const dateKey = getLocalDateKey(selectedDateObj);
          const dayAppointments = (Array.isArray(appointments) ? appointments : [])
            .filter((ap) => ap && appointmentBelongsToDateDoc(ap, dateKey))
            .filter((ap) => {
              const s = ((ap.status || "") + "").trim().toLowerCase();
              return s !== "cancelled" && s !== "canceled";
            });
          const chairPlotResult = validatePatientBookingDurationOnChairPlot({
            appointments: dayAppointments,
            chairCount: effectiveChairCount,
            scheduleDocData: dentistSchedulesForDate,
            bookingDentistId: dentistId,
            newStartMin: selectedSlotForFeasibility.start,
            newEndMin: selectedSlotForFeasibility.start + totalMinutes,
          });
          if (!chairPlotResult.ok && chairPlotResult.message) {
            return chairPlotResult.message;
          }
        }
      }

      return null;
    };

    const totalSelectedDuration = selectedServices.reduce(
      (sum, n) => sum + parseDurFromName(n),
      0
    );

    const map = new Map();
    for (const service of services) {
      const name = service.name;
      const isSelected = selectedServices.includes(name);
      if (isSelected) {
        map.set(name, { disabled: false, reason: null });
        continue;
      }
      const candidateTotal = totalSelectedDuration + parseDur(service);
      const reason = getBlockReason(candidateTotal);
      map.set(name, { disabled: Boolean(reason), reason });
    }
    return map;
  }, [
    services,
    selectedServices,
    selectedTimeSlot,
    timeSlots,
    selectedDentist,
    dentists,
    chairSettings,
    chairSwapsBySlotForDate,
    selectedDateAppointment,
    appointments,
    dentistSchedulesForDate,
    effectiveChairCount,
  ]);

  const handleServiceChange = (event) => {
    const {
      target: { value },
    } = event;

    const raw =
      typeof value === "string"
        ? value.split(",").filter(Boolean)
        : [...(Array.isArray(value) ? value : [])].filter(Boolean);

    const next = raw.filter((name) => {
      const feas = serviceDurationFeasibilityByName.get(name);
      if (feas?.disabled) {
        return selectedServices.includes(name);
      }
      return true;
    });

    setSelectedServices(next);
  };

  useEffect(() => {
    setSelectedServices((prev) => {
      const filtered = prev.filter((name) => {
        const feas = serviceDurationFeasibilityByName.get(name);
        return !feas?.disabled;
      });
      if (filtered.length === prev.length) return prev;
      return filtered;
    });
  }, [serviceDurationFeasibilityByName]);

const [personName, setPersonName] = React.useState([]);
const [sportsName, setSportsName] = React.useState([]);

  const handleChangeName = (event) => {
    const {
      target: { value },
    } = event;
    setPersonName(
      // On autofill we get a stringified value.
      typeof value === 'string' ? value.split(',') : value,
    );
  };

const fetchHighlightedDays = (date) => {
    const controller = new AbortController();
    fakeFetch(date, {
      signal: controller.signal,
    })
      .then(({ daysToHighlight }) => {
        setHighlightedDays(daysToHighlight);
        setIsLoading(false);
      })
      .catch((error) => {
        // ignore the error if it's caused by `controller.abort`
        if (error.name !== 'AbortError') {
          throw error;
        }
      });

    requestAbortController.current = controller;
  };

  React.useEffect(() => {
    fetchHighlightedDays(initialValue);
    // abort request on unmount
    return () => requestAbortController.current?.abort();
  }, []);

  const handleMonthChange = (date) => {
    if (requestAbortController.current) {
      // make sure that you are aborting useless requests
      // because it is possible to switch between months pretty quickly
      requestAbortController.current.abort();
    }

    setIsLoading(true);
    setHighlightedDays([]);
    fetchHighlightedDays(date);
    
    // If navigating to the current month, set selected date to today
    // Otherwise, use the first day of the month being navigated to
    const today = dayjs();
    const dateMonth = date.month();
    const dateYear = date.year();
    const todayMonth = today.month();
    const todayYear = today.year();
    
    let dateToSelect = date;
    
    // If navigating to the current month, select today's date instead of the 1st
    if (dateMonth === todayMonth && dateYear === todayYear) {
      dateToSelect = today;
    }
    
    // Update selectedDateAppointment to trigger appointment fetching for the new month
    setSelectedDateAppointment(dateToSelect);
    setSelectedTimeSlot(null);
  };

const [open, setOpen] = React.useState(false);

  const toggleDrawer = (newOpen) => () => {
    setOpen(newOpen);
  };
  const [showTooltip, setShowTooltip] = React.useState(true);

  const handleTooltipClose = () => {
    setShowTooltip(false);
  };

  const handleTooltipConfirmClose = () => {
    setShowTooltipConfirm(false);
  };

  const tooltipContentConfirm = (
    <Box
      sx={{
        alignItems: "center",
        gap: 1,
        zIndex: "2"
      }}
    >
      <Typography variant="body3" sx={{
        fontSize: "14px",
        padding: "10px"
      }}>
        Are you sure?
      </Typography>
      <IconButton
        size="small"
        onClick={handleTooltipConfirmClose}
        sx={{
          color: "#FEFEFE",
          padding: "2px",
          "&:hover": { opacity: 0.8 },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );


  const tooltipContent = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
      }}
    >
      <Typography variant="body3">
        Review Details
      </Typography>
      <IconButton
        size="small"
        onClick={handleTooltipClose}
        sx={{
          color: "#FEFEFE",
          padding: "2px",
          "&:hover": { opacity: 0.8 },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );

  const boxStylesContent = {
    width: "100%",
    maxWidth: "1740px",
    margin: "0 auto",
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "calc(100vh - 61px)",
    backgroundColor: {
      xs : "#FEFEFE",
      sm : "#FDF9F3",
      md : "#FDF9F3",
      lg : "#FDF9F3",
      xl : "#FDF9F3"
    },
    // width: "100%"
  }

  const boxStylesWrapper = {  
    // width: "100%",
    backgroundColor: "#FDF9F3",
    justifyContent: "center",
    alignItems: "center",
  }

  // Styling for the text field label
  const styleText = {
    color: '#2C251E',
    fontWeight: {
      xs : '600',
      sm : '600',
      md : '100',
      lg : '100',
      xl : '100',
    },
    fontSize: '14px',
    padding: '5px 0',
  };

  const accordionStylesFaqs = {
    margin: "10px auto",
    width: "100%",
    transition:"0.2s ease-in-out",
    backgroundColor: "transparent",
    "&:hover": {
        // padding: "10px 10px",
        backgroundColor: "transparent",
    },               
  }

  return (
    <div>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <Box
        sx={boxStylesWrapper}
      >

        {/* Header Container */}
        <Header
          count={1}
          page="Appointment Page"
        />
                  
      {/* All Content */}
      <Box 
        sx={boxStylesContent}>

          {/* Main Grids */}
          <Grid
            container
            rowSpacing={4}
            columnSpacing={6}
            sx={{
              // backgroundColor: "mediumaquamarine",
            justifyContent: "end",
            justifyItems: "center",
            alignItems: "center",
            alignContent: "center",
              width: "100%",
              height: "100vh",
              overflow: "auto",
              // backgroundColor: "blue",
              "& .MuiGrid-item": {
                display: "flex",
                justifyContent: "center",
                // backgroundColor: "blue",
              },
            }}>

            {/* Main Content */}
            <Grid 
              size={{ 
                xs: 12, 
                sm: 12, 
                md: 12, 
                lg: 12, 
                xl: 12 
              }} 
              item
            >


              {/* Main Content */}
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  // backgroundColor: "#FDF9F3",
                  // backgroundColor: "#a0844dff",
                }}>

                {/* Grid Main Content */}
                <Grid
                  container
                  sx={{
                    width: "100%",
                    // backgroundColor: "blue",
                    "& .MuiGrid-item": {
                      display: "flex",
                      justifyContent: "center",
                      // backgroundColor: "blue",
                    },
                  }}>


            {/* Main Content */}
            <Grid 
              size={{ 
                xs: 12, 
                sm: 12, 
                md: 12, 
                lg: 12, 
                xl: 12 
              }} 
              item
              sx={{
                display: "flex",
                flexDirection: "column",
              // boxShadow: "-1px 5px 10px rgba(0,0,0,0.25)",
                height: "calc(100vh - 61px)", // 👈 fills height below the header (adjust 80px if header height changes)
                overflow: "auto",
                mt: "0px",
                maxHeight: "calc(100vh - 61px)",
              }}
            >

              <Box
                sx={{
                  background: "#FEFEFE",
                  overflow: "auto",
                  // margin: "30px",
                  boxShadow: {
                    xs : "none",
                    sm : "0 4px 20px rgba(0,0,0,0.30)",
                    md : "0 4px 20px rgba(0,0,0,0.30)",
                    lg : "0 4px 20px rgba(0,0,0,0.30)",
                    xl : "0 4px 20px rgba(0,0,0,0.30)",
                  },
                  // border: "1px solid black",
                  margin: {
                    xs : 0,
                    sm : "40px",
                    md : "40px",
                    lg : "40px",
                    xl : "40px",
                  },
                  borderRadius: {
                    xs : 0,
                    sm : "15px",
                    md : "15px",
                    lg : "15px",
                    xl : "15px",
                  },
                  minHeight: 0,

                  display: "flex",
                  flexDirection: "column",
                  padding:  {
                    xs : "20px",
                    sm : "20px 30px",
                    md : "20px 30px",
                    lg : "20px 30px",
                    xl : "20px 30px",
                  },
                }}
              >
                  {/* Title */}
                  <Box
                    sx={{
                    // background: "#905656ff",
                      padding: {
                        xs : "10px 0",
                        sm : "10px",
                        md : "10px",
                        lg : "10px",
                        xl : "10px",
                      },
                      justifyItems: "center",
                      textAlign: "center"
                    }}>
                      

                  <Text
                    text="Book an Appointment"
                    variant="h2"/>
                    
                  </Box>

                  <Stack
                    direction="row"
                    // spacing={5}
                    justifyContent={{
                      xs: "center",
                      sm: "center",
                      md: "center",
                      lg: "end",
                      xl: "end",
                    }}
                    gap={{
                      xs: 1,
                      sm: 2,
                      md: 1,
                      lg: 3,
                      xl: 3,
                    }}
                    // width="calc(100% - 40px)"
                    display="flex"
                    flexWrap="wrap"
                    margin="10px 0"
                  >

                    {/* Confirmed */}
                    <Stack
                      direction="row"
                      sx={{
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#2E2D2D",
                        borderRadius: "20px"
                      }}>
                        
                        <Icon
                          src={require("../../assets/approve.png")}
                          size="small"/>
                      <Text
                        text="Confirmed"
                        variant="appointmentLabels"/>

                    </Stack>

                    {/* Pending */}
                    <Stack
                      direction="row"
                      sx={{
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#2E2D2D",
                        borderRadius: "20px"
                      }}>
                        
                        <Icon
                          src={require("../../assets/clock_variant2.png")}
                          size="small"/>
                      <Text
                        text="Pending"
                        variant="appointmentLabels"/>

                    </Stack>

                    {/* Reserved */}
                    <Stack
                      direction="row"
                      sx={{
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#2E2D2D",
                        borderRadius: "20px"
                      }}>
                        
                        <Icon
                          src={require("../../assets/icons/removed.png")}
                          size="small"/>
                      <Text
                        text="Reserved"
                        variant="appointmentLabels"/>

                    </Stack>

                    {/* Completed */}
                    <Stack
                      direction="row"
                      sx={{
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#2E2D2D",
                        borderRadius: "20px"
                      }}>
                        
                        <Icon
                          src={require("../../assets/icons/complete.png")}
                          size="small"/>
                      <Text
                        text="Completed"
                        variant="appointmentLabels"/>

                    </Stack>

                    {/* Unavailable */}
                    <Stack
                      direction="row"
                      sx={{
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#2E2D2D",
                        borderRadius: "20px"
                      }}>
                        
                        <Icon
                          src={require("../../assets/icons/cancel_light.png")}
                          size="unavailable"/>
                          
                      <Text
                        text="Unavailable"
                        variant="appointmentLabels"/>

                    </Stack>

                  </Stack>
                
                <Box
                  display={{
                    xs : "block",
                    sm : "block",
                    md : "none",
                    lg : "none",
                    xl : "none"
                  }}>
                <Divider/>

                </Box>
                

          {/* Content Split */}
          <Grid
            container
            rowSpacing={{
              xs: 0,
              sm: 0,
              md: 3,
              lg: 3,
              xl: 3,
            }}
            columnSpacing={3}
            sx={{
              width: "100%",
              // backgroundColor: "mediumaquamarine",
              overflowY: {
                xs: "auto",
              sm: "auto",
              md: "hidden",
              lg: "hidden",
              xl: "hidden",
              },
              justifyContent: "end",
              "& .MuiGrid-item": {
                display: "flex",
                justifyContent: "center",
                // backgroundColor: "blue",
              },
            }}>


            {/* XS SM Size Dentist Split */}
            <Grid 
              size={{ 
                xs: 12,
              }}
              display={{
                xs: "flex",
                sm: "flex",
                md: "none",
                lg: "none",
                xl: "none",
              }} 
              position="sticky"
              item
              sx={{
                  paddingY: 1,
              }}
            ><Stack
                direction="column"
                width="100%">
                  <Typography 
                    sx={{ 
                      fontWeight: "600",
                      fontSize: "17px",
                      color: "#2C251E"
                    }}
                  >
                    Step 1
                  </Typography>
                  <Accordion
                    elevation={0}
                    expanded={accOpen.dentist}
                    onChange={toggleAcc("dentist")}
                    sx={accordionStylesFaqs}
                  >
                <AccordionSummary
                  id="dentist-accordion"
                  aria-controls="accordion-content"
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    borderRadius: "10px",
                    background: "linear-gradient(to right, #8B7355 0%, #C7A76C 100%)",
                    borderBottom:
                      "1px solid rgba(88, 85, 78, 0.4)",
                  }}
                >
                  <Typography 
                    sx={{ 
                      fontWeight: "normal",
                      fontSize: "16px",
                      color: "#FDF9F3"
                    }}
                  >
                    Select a Dentist
                  </Typography>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    padding: "10px 0",
                    ":hover" : {
                      // backgroundColor: "#FEFEFE"
                    }
                  }}>

              <Box textAlign="start" sx={{
                width: "100%",
              }}>
                          <FormControl
                            fullWidth
                                  sx={{
                                    minWidth: "100px",
                                    height: "53px"
                                  }}
                                >
                                  <Select
                                    displayEmpty
                                    maxWidth
                                    labelId="dentistSelect"
                                    id="dentistSelect"
                                    value={selectedDentist || ""}
                                    onChange={(e) => {
                        const next = e.target.value || null;
                        if (next) {
                          const entry = bookingVisibleDentists.find(
                            (d) => d.name === next
                          );
                          if (!entry || !isDentistSelectableForBooking(entry.id)) {
                            return;
                          }
                        }
                        setSelectedDentist(next);
                        setSelectedTimeSlot(null);
                      }}
                                    sx={{
                                      height: "53px",
                                      // maxWidth: "100%",
                                      fontSize: "14px",
                                      "& .MuiSelect-select": {
                                        padding: "10px 15px",
                                        color: "#2C251E",
                                        opacity: selectedDentist ? 1 : .6,
                                        fontSize: selectedDentist ? "14px" : "12px"
                                      },
                                      "& .MuiOutlinedInput-notchedOutline": {
                                        border:
                                          "2px solid rgba(197,165,114,0.4)",
                                        borderRadius: "8px",
                                      },
                                      "&:hover .MuiOutlinedInput-notchedOutline":
                                        {
                                          borderColor: "rgba(197,165,114,0.9)",
                                        },
                                      "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                        {
                                          borderColor: "rgba(197,165,114,1)",
                                        },
                                      "& .MuiSvgIcon-root": {
                                        color: "#2C251E",
                                      },
                                    }}
                                    MenuProps={{
                                      PaperProps: {
                                        sx: {
                                          maxHeight: "200px",
                                          backgroundColor: "#FEFEFE",
                                          color: "#2C251E",
                                          borderRadius: "10px",
                                          marginTop: "10px",
                                          border:
                                            "1.5px solid rgba(197,165,114,0.6)",

                                          "& .MuiMenuItem-root": {
                                            fontSize: "14px",
                                            borderRadius: "6px",
                                            "&:hover": {
                                              backgroundColor:
                                                "rgba(197,165,114,0.2)",
                                            },
                                          },

                                          "& .MuiMenuItem-root.Mui-disabled":
                                            BOOKING_DENTIST_MENU_DISABLED_LIST_SX,

                                          "& .MuiMenuItem-root.Mui-selected, & .MuiMenuItem-root.Mui-selected:hover":
                                            {
                                              backgroundColor:
                                                "rgba(44,37,30,0.9) !important",
                                              color: "#FEFEFE !important",
                                              borderRadius: "0px !important",
                                              "& .MuiListItemButton-root": {
                                                borderRadius: "0px !important",
                                              },
                                            },

                                          "& .MuiList-root .Mui-selected": {
                                            borderRadius: "0px !important",
                                          },

                                          "& .MuiMenuItem-root:first-of-type": {
                                            borderTopLeftRadius: "6px",
                                            borderTopRightRadius: "6px",
                                          },

                                          "& .MuiMenuItem-root:last-of-type": {
                                            borderBottomLeftRadius: "6px",
                                            borderBottomRightRadius: "6px",
                                          },
                                        },
                                      },
                                    }}
                                  >
                                    <MenuItem value="" disabled>
      Select a Dentist
    </MenuItem>
                                    {bookingVisibleDentistsSorted.map((dentist) => {
                                      const isSelectable = isDentistSelectableForBooking(dentist.id);
                                      return (
                                        <MenuItem
                                          key={dentist.id}
                                          value={dentist.name}
                                          disabled={!isSelectable}
                                          sx={
                                            !isSelectable
                                              ? BOOKING_DENTIST_MENU_ITEM_DISABLED_SX
                                              : undefined
                                          }
                                        >
                                          {dentist.name}
                                        </MenuItem>
                                      );
                                    })}
                                  </Select>
                                </FormControl>
            
              </Box>

                      </AccordionDetails>

              </Accordion>
              </Stack>

            </Grid>

            {/* XS SM Size Service Split */}
            <Grid 
              size={{ 
                xs: 12,
              }}
              display={{
                xs: "flex",
                sm: "flex",
                md: "none",
                lg: "none",
                xl: "none",
              }} 
              item
              sx={{
                  paddingY: 1,
              }}
            ><Stack
                direction="column"
                width="100%">
                  <Typography 
                    sx={{ 
                      fontWeight: "600",
                      fontSize: "17px",
                      color: "#2C251E"
                    }}
                  >
                    Step 2
                  </Typography>
                  <Accordion
                    elevation={0}
                    expanded={accOpen.service}
                    onChange={toggleAcc("service")}
                    sx={accordionStylesFaqs}
                  >
                <AccordionSummary
                  id="service-accordion"
                  aria-controls="accordion-content"
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    borderRadius: "10px",
                    background: "linear-gradient(to right, #8B7355 0%, #C7A76C 100%)",
                    borderBottom:
                      "1px solid rgba(88, 85, 78, 0.4)",
                  }}
                >
                  <Typography 
                    sx={{ 
                      fontWeight: "normal",
                      fontSize: "16px",
                      color: "#FDF9F3"
                    }}
                  >
                    Select Service(s)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    padding: "10px 0",
                    ":hover" : {
                      // backgroundColor: "#FEFEFE"
                    }
                  }}>

              <Box textAlign="start" sx={{width: "100%"}}>
                <Stack direction="column">
                  {/* Text */}
                  <Typography variant="h3" sx={styleText}>
                    Select Service(s)
                  </Typography>
<FormControl fullWidth>
  <Select
    multiple
    displayEmpty
    value={selectedServices}
    onChange={handleServiceChange}
    input={<OutlinedInput />}
    renderValue={(selected) => {
      if (selected.length === 0) {
        return "Select Service(s)";
      }
      return `${selected.length} item${selected.length > 1 ? "s" : ""} selected`;
    }}
    sx={{
      height: "53px",
      fontSize: "14px",
      "& .MuiSelect-select": {
        padding: "10px 15px",
        display: "flex",
        flexWrap: "wrap",
        gap: "4px",
        color: selectedServices.length ? "#2C251E" : "rgba(44, 37, 30, 0.6)",
      },
      "& .MuiOutlinedInput-notchedOutline": {
        border: "2px solid rgba(197,165,114,0.4)",
        borderRadius: "8px",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "rgba(197,165,114,0.9)",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "rgba(197,165,114,1)",
      },
      "& .MuiSvgIcon-root": {
        color: "#2C251E",
      },
    }}
    MenuProps={{
      PaperProps: {
        sx: {
          maxHeight: "300px",
          backgroundColor: "#FEFEFE",
          color: "#2C251E",
          borderRadius: "10px",
          marginTop: "10px",
          border: "1.5px solid rgba(197,165,114,0.6)",
          "& .MuiMenuItem-root": {
            fontSize: "14px",
            borderRadius: "6px",
            "&:hover": {
              backgroundColor: "rgba(197,165,114,0.2)",
            },
          },
          "& .MuiMenuItem-root.Mui-disabled": {
            opacity: "1 !important",
            "&:hover": {
              backgroundColor: "rgba(46, 45, 45, 0.2) !important",
            },
          },
          "& .MuiMenuItem-root.Mui-selected, & .MuiMenuItem-root.Mui-selected:hover": {
            backgroundColor: "rgba(44,37,30,0.7) !important",
            color: "#FEFEFE !important",
            borderRadius: "0px !important",
            "& .MuiListItemText-primary": {
              color: "#FEFEFE !important",
            },
            "& .MuiTypography-root": {
              color: "#FEFEFE !important",
            },
            "& .MuiSvgIcon-root": {
              color: "#FEFEFE !important",
            },
          },
          "& .MuiList-root .Mui-selected": {
            borderRadius: "0px !important",
          },
          "& .MuiMenuItem-root:first-of-type": {
            borderTopLeftRadius: "6px",
            borderTopRightRadius: "6px",
          },
          "& .MuiMenuItem-root:last-of-type": {
            borderBottomLeftRadius: "6px",
            borderBottomRightRadius: "6px",
          },
        },
      },
    }}
  >
    <MenuItem value="" disabled>
      Select Service(s)
    </MenuItem>

    {Object.entries(groupedServices).map(([category, categoryServices]) => [
      <ListSubheader
        key={`header-${category}`}
        sx={{
          backgroundColor: "#2E2D2D",
          color: "#FEFEFE",
          fontSize: "14px",
          // fontWeight: "600",
          padding: "10px 16px",
          lineHeight: "1.5",
          pointerEvents: "none",
        }}
      >
        {category}
      </ListSubheader>,
      ...categoryServices.map((service) => {
        const isSelected = selectedServices.includes(service.name);
        const feas = serviceDurationFeasibilityByName.get(service.name) || {
          disabled: false,
          reason: null,
        };
        const isGreyed = feas.disabled && !isSelected;
        const row = (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
              alignItems: "center",
            }}
          >
            <ListItemText
              primary={service.name}
              primaryTypographyProps={{
                fontSize: 14,
                sx: {
                  color: isGreyed
                    ? "#999"
                    : isSelected
                      ? "#FEFEFE"
                      : "#2C251E",
                  fontWeight: 400,
                },
              }}
            />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
              {isSelected && (
                <CheckIcon
                  fontSize="small"
                  sx={{
                    color: "#FEFEFE",
                    ml: 0.5,
                    opacity: 0.95,
                  }}
                />
              )}
            </Box>
          </Box>
        );
        return (
          <MenuItem
            key={service.name}
            value={service.name}
            disabled={isGreyed}
            title={isGreyed && feas.reason ? feas.reason : undefined}
            sx={{
              ...(isGreyed
                ? {
                    opacity: 1,
                    bgcolor: "rgba(46, 45, 45, 0.2)",
                    cursor: "not-allowed !important",
                    "&.Mui-disabled": {
                      opacity: 1,
                    },
                    "& .MuiListItemText-primary": {
                      color: "#999 !important",
                    },
                  }
                : {}),
            }}
          >
            {isGreyed && feas.reason ? (
              <Tooltip
                title={feas.reason}
                placement="left"
                arrow
                slotProps={{
                  popper: {
                    sx: {
                      zIndex: 9999999,
                    },
                  },
                }}
              >
                <Box component="span" sx={{ display: "block", width: "100%" }}>
                  {row}
                </Box>
              </Tooltip>
            ) : (
              row
            )}
          </MenuItem>
        );
      }),
    ])}
  </Select>
</FormControl>

              
              </Stack>


              </Box>

                      </AccordionDetails>

              </Accordion>
              </Stack>

            </Grid>

            {/* XS SM Size Calendar Split */}
            <Grid 
              size={{ 
                xs: 12,
              }}
              display={{
                xs: "flex",
                sm: "flex",
                md: "none",
                lg: "none",
                xl: "none",
              }} 
              item
              sx={{
                  paddingY: 1,
              }}
            >
              <Stack
                direction="column"
                width="100%">
                  <Typography 
                    sx={{ 
                      fontWeight: "600",
                      fontSize: "17px",
                      color: "#2C251E"
                    }}
                  >
                    Step 3
                  </Typography>
                <Accordion
                  elevation={0}
                  expanded={accOpen.date}
                  onChange={toggleAcc("date")}
                  sx={accordionStylesFaqs}
                >
                <AccordionSummary
                  id="date-accordion"
                  aria-controls="accordion-content"
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    borderRadius: "10px",
                    
                    background: "linear-gradient(to right, #8B7355 0%, #C7A76C 100%)",
                    borderBottom:
                      "1px solid rgba(88, 85, 78, 0.4)",
                    "&:hover": {
                    // padding: "10px 10px",
                    // backgroundColor:
                    //   "rgba(190, 52, 52, 1)",
                    },
                  }}
                >
                  <Typography 
                    sx={{ 
                      // fontWeight: expanded === "date" ? "bold" : "normal",
                      fontSize: "16px",
                      color: "#FEFEFE"
                    }}
                  >
                    Select Date
                  </Typography>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    padding: "10px 0",
                    justifyContent: "center",
                    justifyItems: "center",
                    ":hover" : {
                      // backgroundColor: "#FEFEFE"
                    }
                  }}
                >
                  <Box textAlign="start" sx={{
                    // width: "100%",
                  }}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <StaticDatePicker
                        value={selectedDateAppointment}
                        onChange={handleDatePickerChange}
                        minDate={dayjs()}
                        maxDate={null}
                        allowPartialRange={false}
                        shouldDisableDate={shouldDisableDate}
                        tileContent={({ date, view }) => renderCalendarTile(date, view)}
                        loading={isLoading}
                        showDaysOutsideCurrentMonth={true}
                        onMonthChange={handleMonthChange}
                        renderLoading={() => <DayCalendarSkeleton />}
                        slots={{
                          day: ServerDay,
                        }}
                        slotProps={{
                          layout: {
                            sx: {
                              borderRadius: 2,
                              boxShadow: "0px 0.5px 10px .6px rgba(46, 45, 45, .6)",
                              // margin: "20px",
                              p: 2,
                            },
                          },
                          toolbar: {
                            sx: {
                              display: "none",
                            },
                          },
                          actionBar: {
                            actions: [] 
                          },
                          day: {
                            highlightedDays,
                            sx: {
                              '&.Mui-disabled': {
                                backgroundColor: 'rgba(46, 45, 45, .1)', 
                                opacity: 1, 
                                color: 'white', 
                              },
                            },
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Box>


                </AccordionDetails>

              </Accordion>
              </Stack>

            </Grid>

            {/* XS SM Size Time Slot Split */}
            <Grid 
              size={{ 
                xs: 12,
              }}
              display={{
                xs: "flex",
                sm: "flex",
                md: "none",
                lg: "none",
                xl: "none",
              }} 
              item
              sx={{
                  paddingY: 1,
              }}
            >
              <Stack
                direction="column"
                width="100%">
                  <Typography 
                    sx={{ 
                      fontWeight: "600",
                      fontSize: "17px",
                      color: "#2C251E"
                    }}
                  >
                    Step 4
                  </Typography>
                  <Accordion
                    elevation={0}
                    expanded={accOpen.time}
                    onChange={toggleAcc("time")}
                    sx={accordionStylesFaqs}
                  >
                <AccordionSummary
                  id="date-accordion"
                  aria-controls="accordion-content"
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    borderRadius: "10px",
                    
                    background: "linear-gradient(to right, #8B7355 0%, #C7A76C 100%)",
                    borderBottom:
                      "1px solid rgba(88, 85, 78, 0.4)",
                    "&:hover": {
                    // padding: "10px 10px",
                    // backgroundColor:
                    //   "rgba(190, 52, 52, 1)",
                    },
                  }}
                >
                  <Typography 
                    sx={{ 
                      // fontWeight: expanded === "date" ? "bold" : "normal",
                      fontSize: "16px",
                      color: "#FEFEFE"
                    }}
                  >
                    Select Time Slot
                  </Typography>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    padding: "10px 0",
                    justifyContent: "center",
                    justifyItems: "center",
                    ":hover" : {
                      // backgroundColor: "#FEFEFE"
                    }
                  }}>

          <Grid
            container
            // rowSpacing={2}
            // columnSpacing={2}
            sx={{
              width: "100%",
              // height: "100vh",
              // backgroundColor: "blue",
              justifyContent: "end",
              "& .MuiGrid-item": {
                display: "flex",
                justifyContent: "center",
                // backgroundColor: "blue",
              },
            }}>

            {/* Time Slot */}
            <Grid 
              size={{ 
                xs: 12, 
                sm: 12, 
                md: 12, 
                lg: 12, 
                xl: 12 
              }} 
              item
              sx={{
                // backgroundColor: "mediumaquamarine",
              // height: "calc(100% - 50px)",
              }}
            >
              <Box 
                sx={{
                  background: "white",
                  borderRadius: "12px",
                  boxShadow: "0 0.5px 10px rgba(46, 45, 45, 0.6)",
                  overflow: "hidden",
                  height: "550px",
                }}
              >
              
                <Box
                  sx={{
                    background: "#C5A572",
                    color: "white",
                    padding: "20px",
                    justifyContent: "center",
                    textAlign: "center",
                    borderBottom: "2px solid rgba(46, 45, 45, .8)",
                    fontSize: "16px",
                    fontWeight: "600",
                    letterSpacing: 0.4
                  }}
                >
                  {(() => {
                    const dateStr = selectedDateAppointment.toDate().toLocaleDateString('en-US', { 
                      month: 'long', 
                      day: 'numeric', 
                      year: 'numeric' 
                    });
                    const dayStr = selectedDateAppointment.toDate().toLocaleDateString('en-US', { weekday: 'long' });
                    const dentistName = selectedDentist && selectedDentist !== "" ? selectedDentist : "Doctor";
                    return `Date: ${dateStr} (${dayStr}) | Dentist: ${dentistName}`;
                  })()}
                </Box>
              
<Box
  sx={{
    position: "relative",
    maxHeight: "88%",
    overflowY: !selectedDentist || selectedDentist === "" ? "hidden" : "auto",
  }}
>
  {/* Overlay message when no doctor is selected */}
  {!selectedDentist || selectedDentist === "" ? (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(184, 184, 184, .8)",
        zIndex: 2,
        pointerEvents: "none",
      }}
    >
      <Stack
        direction="column"
        justifyContent="center"
        justifyItems="center"
        textAlign="center"
        alignContent="center"
        alignItems="center"
        sx={{
          padding: "30px"
        }}
        spacing={2}>
          <Box
            sx={{
              width: "60px",
              height: "60px",
              backgroundColor: "#C5A572",
              borderRadius: "10px"
            }}>
          <Icon
            src={require("../../assets/icons/masked.png")}
            size="large"/>
            </Box>
        <Text
          text="Select a Dentist First"
          variant="overlayTime"
        />
        <Text
          text="Choose a dentist from the dropdown above to view their available time slots"
          variant="overlayTimeDef"
        />
        </Stack>
    </Box>
  ) : !selectedDentistHasPlottedSchedule ? (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "200px",
        padding: "40px",
        textAlign: "center",
        color: "#333",
        fontSize: "16px",
      }}
    >
      {`There are no available timeslots for ${selectedDentist} on this date.`}
    </Box>
  ) : (
                     timeSlots.map((slot, index) => {
                       // Use appointment information from the slot object
                       const appointmentForSlot = slot.appointment;
  
                       // Determine if this is the current user's appointment
                       const isUserAppointment = appointmentForSlot && currentUser && 
                         (appointmentForSlot.uid === currentUser.uid || appointmentForSlot.patientId === currentUser.uid);
  
                       // Determine appointment status for styling
                       const appointmentStatus = appointmentForSlot ? appointmentForSlot.status : null;
                       const isPending = isUserAppointment && appointmentStatus === "Pending";
                       const isConfirmed = isUserAppointment && appointmentStatus === "Confirmed";
                       const isCompleted = isUserAppointment && appointmentStatus === "Completed";
  
                       // Determine background and border colors
                       let backgroundColor = "#FEFEFE";
                       let borderRadius = "0px";
  
                       if (selectedTimeSlot && selectedTimeSlot.id === slot.id) {
                         backgroundColor = "#DBEAFE";
                       } else if (isCompleted) {
                         backgroundColor = "rgba(44, 103, 133, .9)";
                       } else if (isPending) {
                         backgroundColor = "#b4996cff"
                       } else if (isConfirmed) {
                         backgroundColor = "#43674eff";
                       } else if (appointmentForSlot && !isUserAppointment) {
                         backgroundColor = "#2E2D2D";
                       } else if (slot.isUnavailable) {
                         backgroundColor = "rgba(46, 45, 45, 0.2)";
                       }
  
                       return (
                         <Box 
                           key={index} 
                           sx={{
                             display: "flex",
                             alignItems: "center",
                             borderBottom: "1px solid #585858",
                             background: backgroundColor,
                             borderRadius: borderRadius,
                             cursor: slot.isAvailable && !slot.isUnavailable ? "pointer" : slot.isUnavailable ? "not-allowed" : 
                             "default",
                             transition: "all 0.2s ease",
                             minHeight: "44px",
                             pr: "20px",
                             '&:hover': {
                                backgroundColor:
                                  isCompleted ? "rgba(44, 103, 133, .9)" : 
                                  selectedTimeSlot === slot ? "#DBEAFE" : 
                                  slot.isAvailable && !slot.isUnavailable ? "rgba(89, 84, 74, 0.5)" :
                                  isConfirmed ? "#43674eff" :
                                  isPending ? "#b4996cff" : 
                                  slot.isUnavailable ? "rgba(46, 45, 45, 0.2)" : 
                                  appointmentForSlot ? "#2E2D2D" : 
                                  '#FEFEFE',
                                color: '#FDF9F3',
                              },
                           }}
                           onClick={() => {
                             if (slot.isAvailable && !slot.isUnavailable) {
                               // Toggle selection: if clicking the same slot, deselect it
                               if (selectedTimeSlot && selectedTimeSlot.id === slot.id) {
                                 setSelectedTimeSlot(null);
                               } else {
                                 setSelectedTimeSlot(slot);
                               }
                             }
                           }}
                         >
                          <Box
                            sx={{
                              opacity:
                                slot.isUnavailable ? 0.3 : null
                            }}>
                            <Text
                              text={slot.display}
                              variant={
                                selectedTimeSlot === slot || slot.isUnavailable
                                  ? "historyDateTime"
                                  : appointmentForSlot
                                    ? "timeSlotReserved"
                                    : "historyDateTime"
                              }
                            />
                          </Box>
                            
                           <div style={{ 
                             flex: 1, 
                             fontSize: "14px",
                             color: slot.isUnavailable ? "#999" : "#666",
                             textAlign: "center"
                           }}>
                             {appointmentForSlot ? (
                               isUserAppointment ? (
                                <Text
                                  text={appointmentForSlot.patientName || appointmentForSlot.patientDisplay || "Appointment"}
                                  variant="timeSlotReserved"/>
                               ) : (
                                  <Text
                                    text="Reserved"
                                    variant="timeSlotReserved"/>
                               )
                             ) : (
                               <span style={{ color: "#4CAF50", fontWeight: "bold" }}></span>
                             )}
                           </div>
                          
                           <Box 
                            sx={{ 
                             fontSize: "14px",
                             textAlign: "center",
                             opacity:
                                slot.isUnavailable ? 0.5 : null
                            }}
                           >
                             {isCompleted ? (
                               <Icon
                                src={require("../../assets/icons/complete.png")}
                                size="small"/>
                             ) : isConfirmed ? (
                              <Icon
                                src={require("../../assets/approve.png")}
                                size="small"/>
                             ) : isPending ? (
                               <Icon
                                 src={require("../../assets/icons/clock_filled.png")}
                                 size="small"/>
                             ) : slot.slotStatus === "reserved" ? (
                               <Icon
                                  src={require("../../assets/icons/removed.png")}
                                  size="small"/>
                             ) : slot.isUnavailable ? (
                               <Icon
                                src={require("../../assets/icons/cancel_light.png")}
                                size="unavailable"/>
                             ) : (null)}
                           </Box>
                         </Box>
                       );
                   })
                   )}

  {/* Time Slots (dimmed + non-clickable when no dentist) */}
  <Box
    sx={{
      position: "relative",
      zIndex: 1,
      opacity: !selectedDentist || selectedDentist === "" ? 0.3 : 1,
      pointerEvents: !selectedDentist || selectedDentist === "" ? "none" : "auto",
    }}
  >
    {!selectedDentist || selectedDentist === "" ? (
      timeSlots.map((slot, index) => (
        <Box
          key={index}
          sx={{
            display: "flex",
            alignItems: "center",
            borderBottom: "1px solid #585858",
            background: "white",
            cursor: "default",
            transition: "all 0.2s ease",
            minHeight: "44px",
          }}
        >
          <Text text={slot.display} variant="historyDateTime" />
        </Box>
      ))
    ) : (null)}
  </Box>
</Box>

            </Box>

                  </Grid>

                  </Grid>

                      </AccordionDetails>

              </Accordion>
              </Stack>

            </Grid>


          {/* Content Split */}
          <Grid
            container
            rowSpacing={{
              xs: 0,
              sm: 0,
              md: 3,
              lg: 3,
              xl: 3,
            }}
            columnSpacing={3}
            sx={{
              padding: "0 20px",
              width: "100%",
              // backgroundColor: "mediumaquamarine",
              overflowY: "auto",
              justifyContent: "end",
              "& .MuiGrid-item": {
                display: "flex",
                justifyContent: "center",
                // backgroundColor: "blue",
              },
            }}>


            {/* Left Split */}
            <Grid 
              size={{ 
                xs: 6, 
                sm: 6, 
                md: 6, 
                lg: 6, 
                xl: 6 
              }} 
              item
              sx={{
              }}
            >


              {/* Selects & Calendar & Summary*/}
              <Grid
                container
                rowSpacing={2}
                columnSpacing={2}
                display={{
                  xs: "none",
                  sm: "none",
                  md: "flex",
                  lg: "flex",
                  xl: "flex",
                }} 
                sx={{
                  // mt: "40px",
                  width: "100%",
                  // backgroundColor: "mediumaquamarine",
                  justifyContent: "center",
                  "& .MuiGrid-item": {
                    display: "flex",
                    justifyContent: "center",
                    // backgroundColor: "blue",
                  },
                }}>

              {/* Dentist Select */}
            <Grid 
              size={{ 
                xs: 6, 
                sm: 6, 
                md: 6, 
                lg: 6, 
                xl: 6 
              }} 
              item
              sx={{
                // backgroundColor: "maroon" 
                // padding: "0 20px"
              }}
            >

              <Box textAlign="start" sx={{width: "100%", }}>
                <Stack direction="column">
                  {/* Text */}
                  <Typography variant="h3" sx={styleText}>
                    Select a Dentist
                  </Typography>
              
                          <FormControl
                            fullWidth
                                  sx={{
                                    minWidth: "100px",
                                    height: "53px"
                                  }}
                                >
                                  <Select
                                    displayEmpty
                                    maxWidth
                                    labelId="dentistSelect"
                                    id="dentistSelect"
                                    value={selectedDentist || ""}
                                    onChange={(e) => {
                        const next = e.target.value || null;
                        if (next) {
                          const entry = bookingVisibleDentists.find(
                            (d) => d.name === next
                          );
                          if (!entry || !isDentistSelectableForBooking(entry.id)) {
                            return;
                          }
                        }
                        setSelectedDentist(next);
                        setSelectedTimeSlot(null);
                      }}
                                    sx={{
                                      height: "53px",
                                      // maxWidth: "100%",
                                      fontSize: "14px",
                                      "& .MuiSelect-select": {
                                        padding: "10px 15px",
                                        color: "#2C251E",
                                        opacity: selectedDentist ? 1 : .6,
                                        fontSize: selectedDentist ? "14px" : "12px"
                                      },
                                      "& .MuiOutlinedInput-notchedOutline": {
                                        border:
                                          "2px solid rgba(197,165,114,0.4)",
                                        borderRadius: "8px",
                                      },
                                      "&:hover .MuiOutlinedInput-notchedOutline":
                                        {
                                          borderColor: "rgba(197,165,114,0.9)",
                                        },
                                      "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                        {
                                          borderColor: "rgba(197,165,114,1)",
                                        },
                                      "& .MuiSvgIcon-root": {
                                        color: "#2C251E",
                                      },
                                    }}
                                    MenuProps={{
                                      PaperProps: {
                                        sx: {
                                          maxHeight: "200px",
                                          backgroundColor: "#FEFEFE",
                                          color: "#2C251E",
                                          borderRadius: "10px",
                                          marginTop: "10px",
                                          border:
                                            "1.5px solid rgba(197,165,114,0.6)",

                                          "& .MuiMenuItem-root": {
                                            fontSize: "14px",
                                            borderRadius: "6px",
                                            "&:hover": {
                                              backgroundColor:
                                                "rgba(197,165,114,0.2)",
                                            },
                                          },

                                          "& .MuiMenuItem-root.Mui-disabled":
                                            BOOKING_DENTIST_MENU_DISABLED_LIST_SX,

                                          "& .MuiMenuItem-root.Mui-selected, & .MuiMenuItem-root.Mui-selected:hover":
                                            {
                                              backgroundColor:
                                                "rgba(44,37,30,0.9) !important",
                                              color: "#FEFEFE !important",
                                              borderRadius: "0px !important",
                                              "& .MuiListItemButton-root": {
                                                borderRadius: "0px !important",
                                              },
                                            },

                                          "& .MuiList-root .Mui-selected": {
                                            borderRadius: "0px !important",
                                          },

                                          "& .MuiMenuItem-root:first-of-type": {
                                            borderTopLeftRadius: "6px",
                                            borderTopRightRadius: "6px",
                                          },

                                          "& .MuiMenuItem-root:last-of-type": {
                                            borderBottomLeftRadius: "6px",
                                            borderBottomRightRadius: "6px",
                                          },
                                        },
                                      },
                                    }}
                                  >
                                    <MenuItem value="" disabled>
                                      Select a Dentist
                                    </MenuItem>
                                    {bookingVisibleDentistsSorted.map((dentist) => {
                                      const isSelectable = isDentistSelectableForBooking(dentist.id);
                                      return (
                                        <MenuItem
                                          key={dentist.id}
                                          value={dentist.name}
                                          disabled={!isSelectable}
                                          sx={
                                            !isSelectable
                                              ? BOOKING_DENTIST_MENU_ITEM_DISABLED_SX
                                              : undefined
                                          }
                                        >
                                          {dentist.name}
                                        </MenuItem>
                                      );
                                    })}


                                  </Select>
                                </FormControl>
              
              </Stack>
              </Box>

            </Grid>

            {/* Service Select */}
            <Grid 
              size={{ 
                xs: 6, 
                sm: 6, 
                md: 6, 
                lg: 6, 
                xl: 6 
              }} 
              item
              sx={{ 
                // backgroundColor: "aquamarine" 
                // padding: "0 20px"
              }}
            >

              <Box textAlign="start" sx={{width: "100%"}}>
                <Stack direction="column">
                  {/* Text */}
                  <Typography variant="h3" sx={styleText}>
                    Select Service(s)
                  </Typography>
<FormControl fullWidth>
  <Select
    multiple
    displayEmpty
    value={selectedServices}
    onChange={handleServiceChange}
    input={<OutlinedInput />}
    renderValue={(selected) => {
      if (selected.length === 0) {
        return "Select Service(s)";
      }
      return `${selected.length} item${selected.length > 1 ? "s" : ""} selected`;
    }}
    sx={{
      height: "53px",
      fontSize: "14px",
      "& .MuiSelect-select": {
        padding: "10px 15px",
        color: "#2C251E",
        opacity: selectedServices.length ? 1 : .6,
        fontSize: selectedServices.length ? "14px" : "12px"
      },
      "& .MuiOutlinedInput-notchedOutline": {
        border: "2px solid rgba(197,165,114,0.4)",
        borderRadius: "8px",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "rgba(197,165,114,0.9)",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "rgba(197,165,114,1)",
      },
      "& .MuiSvgIcon-root": {
        color: "#2C251E",
      },
    }}
    MenuProps={{
      PaperProps: {
        sx: {
          maxHeight: "300px",
          backgroundColor: "#FEFEFE",
          color: "#2C251E",
          borderRadius: "10px",
          marginTop: "10px",
          border: "1.5px solid rgba(197,165,114,0.6)",
          "& .MuiMenuItem-root": {
            fontSize: "14px",
            borderRadius: "6px",
            "&:hover": {
              backgroundColor: "rgba(197,165,114,0.2)",
            },
          },
          "& .MuiMenuItem-root.Mui-disabled": {
            opacity: "1 !important",
            "&:hover": {
              backgroundColor: "rgba(46, 45, 45, 0.2) !important",
            },
          },
          "& .MuiMenuItem-root.Mui-selected, & .MuiMenuItem-root.Mui-selected:hover": {
            backgroundColor: "rgba(44,37,30,0.7) !important",
            color: "#FEFEFE !important",
            borderRadius: "0px !important",
            "& .MuiListItemText-primary": {
              color: "#FEFEFE !important",
            },
            "& .MuiTypography-root": {
              color: "#FEFEFE !important",
            },
            "& .MuiSvgIcon-root": {
              color: "#FEFEFE !important",
            },
          },
          "& .MuiList-root .Mui-selected": {
            borderRadius: "0px !important",
          },
          "& .MuiMenuItem-root:first-of-type": {
            borderTopLeftRadius: "6px",
            borderTopRightRadius: "6px",
          },
          "& .MuiMenuItem-root:last-of-type": {
            borderBottomLeftRadius: "6px",
            borderBottomRightRadius: "6px",
          },
        },
      },
    }}
  >
    <MenuItem value="" disabled>
      Select Service(s)
    </MenuItem>

    {Object.entries(groupedServices).map(([category, categoryServices]) => [
      <ListSubheader
        key={`header-${category}`}
        sx={{
          backgroundColor: "#2E2D2D",
          color: "#FEFEFE",
          fontSize: "14px",
          // fontWeight: "600",
          padding: "10px 16px",
          lineHeight: "1.5",
          pointerEvents: "none",
        }}
      >
        {category}
      </ListSubheader>,
      ...categoryServices.map((service) => {
        const isSelected = selectedServices.includes(service.name);
        const feas = serviceDurationFeasibilityByName.get(service.name) || {
          disabled: false,
          reason: null,
        };
        const isGreyed = feas.disabled && !isSelected;
        const row = (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
              alignItems: "center",
            }}
          >
            <ListItemText
              primary={service.name}
              primaryTypographyProps={{
                fontSize: 14,
                sx: {
                  color: isGreyed
                    ? "#999"
                    : isSelected
                      ? "#FEFEFE"
                      : "#2C251E",
                  fontWeight: 400,
                },
              }}
            />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
              {isSelected && (
                <CheckIcon
                  fontSize="small"
                  sx={{
                    color: "#FEFEFE",
                    ml: 0.5,
                    opacity: 0.95,
                  }}
                />
              )}
            </Box>
          </Box>
        );
        return (
          <MenuItem
            key={service.name}
            value={service.name}
            disabled={isGreyed}
            title={isGreyed && feas.reason ? feas.reason : undefined}
            sx={{
              ...(isGreyed
                ? {
                    opacity: 1,
                    bgcolor: "rgba(46, 45, 45, 0.2)",
                    cursor: "not-allowed !important",
                    "&.Mui-disabled": {
                      opacity: 1,
                    },
                    "& .MuiListItemText-primary": {
                      color: "#999 !important",
                    },
                  }
                : {}),
            }}
          >
            {isGreyed && feas.reason ? (
              <Tooltip
                title={feas.reason}
                placement="left"
                arrow
                slotProps={{
                  popper: {
                    sx: {
                      zIndex: 9999999,
                    },
                  },
                }}
              >
                <Box component="span" sx={{ display: "block", width: "100%" }}>
                  {row}
                </Box>
              </Tooltip>
            ) : (
              row
            )}
          </MenuItem>
        );
      }),
    ])}
  </Select>
</FormControl>

              
              </Stack>


              </Box>


            </Grid>

              <Box>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <StaticDatePicker
                        value={selectedDateAppointment}
                        onChange={handleDatePickerChange}
                        minDate={dayjs()}
                        maxDate={null}
                        allowPartialRange={false}
                        shouldDisableDate={shouldDisableDate}
                        tileContent={({ date, view }) => renderCalendarTile(date, view)}
                        loading={isLoading}
                        showDaysOutsideCurrentMonth={true}
                        onMonthChange={handleMonthChange}
                        renderLoading={() => <DayCalendarSkeleton />}
                        slots={{
                          day: ServerDay,
                        }}
                        slotProps={{
                          layout: {
                            sx: {
                              borderRadius: 2,
                              boxShadow: "0px 0.5px 10px .6px rgba(46, 45, 45, .6)",
                              // margin: "20px",
                              p: 2,
                            },
                          },
                          toolbar: {
                            sx: {
                              display: "none",
                            },
                          },
                          actionBar: {
                            actions: [] 
                          },
                          day: {
                            highlightedDays,
                            sx: {
                              '&.Mui-disabled': {
                                backgroundColor: 'rgba(46, 45, 45, .1)', 
                                opacity: 1, 
                                color: 'white', 
                              },
                            },
                          },
                        }}
                      />
                    </LocalizationProvider>
              </Box> 


              {/* Dentist Select */}
            <Grid 
              size={{ 
                xs: 12, 
                sm: 12, 
                md: 12, 
                lg: 12, 
                xl: 12 
              }} 
              item
              sx={{
                justifyItems: "center",
              }}
            >

                  <ButtonComp
                    variant="contained"
                    noElevate={true}
                    noRipple={true}
                    label={
                      isBookingLoading ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <CircularProgress size={20} sx={{ color: "#FDF9F3" }} />
                          <span>Booking Appointment</span>
                        </Box>
                      ) : "Review Details"
                    }
                    color="light"
                    paddings="default"
                    background="default"
                    font="default"
                    weight="default"
                    transform="default"
                    radius="special"
                    selected={true}
                    keys={false}
                    hoverTextColor="default"
                    hoverBackground="default"
                    hoverBorder="none"
                    noRead={isBookingLoading}
                    type="default"
                    justify="default"
                    destination="none"
                    open={true}
                    buttonId="scheduleAppointment"
                    clickPass={async () => {
                      const result = await handleAppointmentSubmit();
                      if (result) {
                        setShowBookingSummary(true);
                      }
                    }}/>
                    
            </Grid>


          </Grid>

        </Grid>

            {/* Right Split */}
            <Grid 
              size={{ 
                xs: 6, 
                sm: 6, 
                md: 6, 
                lg: 6, 
                xl: 6 
              }}
              display={{
                xs: "none",
                sm: "none",
                md: "block",
                lg: "block",
                xl: "block",
              }} 
              item
              sx={{
                height: "600px"
              }}
            >
                {/* Legends & Time Slot*/}
          <Grid
            container
            // rowSpacing={2}
            // columnSpacing={2}
            sx={{
              width: "100%",
              // backgroundColor: "blue",
              justifyContent: "end",
              "& .MuiGrid-item": {
                display: "flex",
                justifyContent: "center",
                // backgroundColor: "blue",
              },
            }}>

            {/* Time Slot */}
            <Grid 
              size={{ 
                xs: 12, 
                sm: 12, 
                md: 12, 
                lg: 12, 
                xl: 12 
              }} 
              item
              sx={{
                // backgroundColor: "mediumaquamarine",
              // height: "calc(100% - 50px)",
              }}
            >
              <Box 
                sx={{
                  background: "white",
                  borderRadius: "12px",
                  boxShadow: "0 0.5px 10px rgba(46, 45, 45, 0.6)",
                  overflow: "hidden",
                  height: "550px",
                }}
              >
              
                <Box
                  sx={{
                    background: "#C5A572",
                    color: "white",
                    padding: "20px",
                    justifyContent: "center",
                    textAlign: "center",
                    borderBottom: "2px solid rgba(46, 45, 45, .8)",
                    fontSize: "16px",
                    fontWeight: "600",
                    letterSpacing: 0.4
                  }}
                >
                  {(() => {
                    const dateStr = selectedDateAppointment.toDate().toLocaleDateString('en-US', { 
                      month: 'long', 
                      day: 'numeric', 
                      year: 'numeric' 
                    });
                    const dayStr = selectedDateAppointment.toDate().toLocaleDateString('en-US', { weekday: 'long' });
                    const dentistName = selectedDentist && selectedDentist !== "" ? selectedDentist : "Doctor";
                    return `Date: ${dateStr} (${dayStr}) | Dentist: ${dentistName}`;
                  })()}
                </Box>
              
<Box
  sx={{
    position: "relative",
    maxHeight: "88%",
    overflowY: !selectedDentist || selectedDentist === "" ? "hidden" : "auto",
  }}
>
  {/* Overlay message when no doctor is selected */}
  {!selectedDentist || selectedDentist === "" ? (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(184, 184, 184, .8)",
        zIndex: 2,
        pointerEvents: "none",
      }}
    >
      <Stack
        direction="column"
        justifyContent="center"
        justifyItems="center"
        textAlign="center"
        alignContent="center"
        alignItems="center"
        sx={{
          padding: "30px"
        }}
        spacing={2}>
          <Box
            sx={{
              width: "60px",
              height: "60px",
              backgroundColor: "#C5A572",
              borderRadius: "10px"
            }}>
          <Icon
            src={require("../../assets/icons/masked.png")}
            size="large"/>
            </Box>
        <Text
          text="Select a Dentist First"
          variant="overlayTime"
        />
        <Text
          text="Choose a dentist from the dropdown above to view their available time slots"
          variant="overlayTimeDef"
        />
        </Stack>
    </Box>
  ) : !selectedDentistHasPlottedSchedule ? (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "200px",
        padding: "40px",
        textAlign: "center",
        color: "#333",
        fontSize: "16px",
      }}
    >
      {`There are no available timeslots for ${selectedDentist} on this date.`}
    </Box>
  ) : (
                     timeSlots.map((slot, index) => {
                       // Use appointment information from the slot object
                       const appointmentForSlot = slot.appointment;
  
                       // Determine if this is the current user's appointment
                       const isUserAppointment = appointmentForSlot && currentUser && 
                         (appointmentForSlot.uid === currentUser.uid || appointmentForSlot.patientId === currentUser.uid);
  
                       // Determine appointment status for styling
                       const appointmentStatus = appointmentForSlot ? appointmentForSlot.status : null;
                       const isPending = isUserAppointment && appointmentStatus === "Pending";
                       const isConfirmed = isUserAppointment && appointmentStatus === "Confirmed";
                       const isCompleted = isUserAppointment && appointmentStatus === "Completed";
  
                       // Determine background and border colors
                       let backgroundColor = "#FEFEFE";
                       let borderRadius = "0px";
  
                       if (selectedTimeSlot && selectedTimeSlot.id === slot.id) {
                         backgroundColor = "#DBEAFE";
                       } else if (isCompleted) {
                         backgroundColor = "rgba(44, 103, 133, .9)";
                       } else if (isPending) {
                         backgroundColor = "#b4996cff"
                       } else if (isConfirmed) {
                         backgroundColor = "#43674eff";
                       } else if (appointmentForSlot && !isUserAppointment) {
                         backgroundColor = "#2E2D2D";
                       } else if (slot.isUnavailable) {
                         backgroundColor = "rgba(46, 45, 45, 0.2)";
                       }
  
                       return (
                         <Box 
                           key={index} 
                           sx={{
                             display: "flex",
                             alignItems: "center",
                             borderBottom: "1px solid #585858",
                             background: backgroundColor,
                             borderRadius: borderRadius,
                             cursor: slot.isAvailable && !slot.isUnavailable ? "pointer" : slot.isUnavailable ? "not-allowed" : 
                             "default",
                             transition: "all 0.2s ease",
                             minHeight: "44px",
                             pr: "20px",
                             '&:hover': {
                                backgroundColor:
                                  isCompleted ? "rgba(44, 103, 133, .9)" : 
                                  selectedTimeSlot === slot ? "#DBEAFE" : 
                                  slot.isAvailable && !slot.isUnavailable ? "rgba(89, 84, 74, 0.5)" :
                                  isConfirmed ? "#43674eff" :
                                  isPending ? "#b4996cff" : 
                                  slot.isUnavailable ? "rgba(46, 45, 45, 0.2)" : 
                                  appointmentForSlot ? "#2E2D2D" : 
                                  '#FEFEFE',
                                color: '#FDF9F3',
                              },
                           }}
                           onClick={() => {
                             if (slot.isAvailable && !slot.isUnavailable) {
                               // Toggle selection: if clicking the same slot, deselect it
                               if (selectedTimeSlot && selectedTimeSlot.id === slot.id) {
                                 setSelectedTimeSlot(null);
                               } else {
                                 setSelectedTimeSlot(slot);
                               }
                             }
                           }}
                         >
                          <Box
                            sx={{
                              opacity:
                                slot.isUnavailable ? 0.3 : null
                            }}>
                            <Text
                              text={slot.display}
                              variant={
                                selectedTimeSlot === slot || slot.isUnavailable
                                  ? "historyDateTime"
                                  : appointmentForSlot
                                    ? "timeSlotReserved"
                                    : "historyDateTime"
                              }
                            />
                          </Box>
                            
                           <div style={{ 
                             flex: 1, 
                             fontSize: "14px",
                             color: slot.isUnavailable ? "#999" : "#666",
                             textAlign: "center"
                           }}>
                             {appointmentForSlot ? (
                               isUserAppointment ? (
                                <Text
                                  text={appointmentForSlot.patientName || appointmentForSlot.patientDisplay || "Appointment"}
                                  variant="timeSlotReserved"/>
                               ) : (
                                  <Text
                                    text="Reserved"
                                    variant="timeSlotReserved"/>
                               )
                             ) : (
                               <span style={{ color: "#4CAF50", fontWeight: "bold" }}></span>
                             )}
                           </div>
                          
                           <Box 
                            sx={{ 
                             fontSize: "14px",
                             textAlign: "center",
                             opacity:
                                slot.isUnavailable ? 0.5 : null
                            }}
                           >
                             {isCompleted ? (
                               <Icon
                                src={require("../../assets/icons/complete.png")}
                                size="small"/>
                             ) : isConfirmed ? (
                              <Icon
                                src={require("../../assets/icons/check_light.png")}
                                size="small"/>
                             ) : isPending ? (
                               <Icon
                                 src={require("../../assets/icons/clock_filled.png")}
                                 size="small"/>
                             ) : slot.slotStatus === "reserved" ? (
                               <Icon
                                  src={require("../../assets/icons/removed.png")}
                                  size="small"/>
                             ) : slot.isUnavailable ? (
                               <Icon
                                src={require("../../assets/icons/cancel_light.png")}
                                size="unavailable"/>
                             ) : (null)}
                           </Box>
                         </Box>
                       );
                   })
                   )}

  {/* Time Slots (dimmed + non-clickable when no dentist) */}
  <Box
    sx={{
      position: "relative",
      zIndex: 1,
      opacity: !selectedDentist || selectedDentist === "" ? 0.3 : 1,
      pointerEvents: !selectedDentist || selectedDentist === "" ? "none" : "auto",
    }}
  >
    {!selectedDentist || selectedDentist === "" ? (
      timeSlots.map((slot, index) => (
        <Box
          key={index}
          sx={{
            display: "flex",
            alignItems: "center",
            borderBottom: "1px solid #585858",
            background: "white",
            cursor: "default",
            transition: "all 0.2s ease",
            minHeight: "44px",
          }}
        >
          <Text text={slot.display} variant="historyDateTime" />
        </Box>
      ))
    ) : (null)}
  </Box>
</Box>

            </Box>

                  </Grid>


                  </Grid>
          </Grid>
            </Grid>

          

</Grid>

      
            {/* XS SM Size Button Split */}
            <Grid 
              size={{ 
                xs: 12,
              }}
              display={{
                xs: "flex",
                sm: "flex",
                md: "none",
                lg: "none",
                xl: "none",
              }} 
              item
              sx={{
                  paddingY: 1,
                  justifyContent: "center"
              }}
            >


                  <ButtonComp
                    variant="contained"
                    noElevate={true}
                    noRipple={true}
                    label={
                      isBookingLoading ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <CircularProgress size={20} sx={{ color: "#FDF9F3" }} />
                          <span>Booking Appointment</span>
                        </Box>
                      ) : "Review Details"
                    }
                    color="light"
                    paddings="default"
                    background="default"
                    font="default"
                    weight="default"
                    transform="default"
                    radius="special"
                    selected={true}
                    keys={false}
                    hoverTextColor="default"
                    hoverBackground="default"
                    hoverBorder="none"
                    noRead={isBookingLoading}
                    type="default"
                    justify="default"
                    destination="none"
                    open={true}
                    buttonId="scheduleAppointment"
                    clickPass={async () => {
                      const result = await handleAppointmentSubmit();
                      if (result) {
                        setShowBookingSummary(true);
                      }
                    }}/>

            </Grid>
        
      
                </Box>

            </Grid>

                  </Grid>


              </Box>

            </Grid>

          </Grid>


      </Box>
      </Box>

      <Tooltip
        title={!showTooltip ? "Review Details" : null}
        placement="left"
        arrow
      >

      <Tooltip
        title={tooltipContent}
        placement="top-end"
        arrow
        open={showTooltip}
        onClose={handleTooltipClose}
        disableFocusListener
        disableHoverListener
        disableTouchListener
      >
      <Button 
        onClick={toggleDrawer(true)}
        sx={{
            display: 
              showBookingSummary ? "none" : "flex"
            ,
            position: 'fixed',
            zIndex: 9999,
            right: 0,
            top: "50%",
            bottom: "auto",
            transform: "translate(0, -50%)",
            backgroundColor: "#2E2D2D",
            boxShadow: "0 0 8px rgba(0, 0, 0, 0.6)",
            borderTopLeftRadius: "50%",
            borderBottomLeftRadius: "50%",
            ":hover": {
              backgroundColor: "rgba(46, 45, 45, 0.9)",
            }
        }}
      >
        <Icon
          src={require("../../assets/icons/details.png")}
          size="large"/>
      </Button>
      </Tooltip>
      </Tooltip>

<Drawer
  anchor="right"
  open={open}
  onClose={toggleDrawer(false)}
  ModalProps={{
    BackdropProps: { sx: { backgroundColor: 'transparent' } },
  }}
  PaperProps={{
    sx: {
      height: 300,
      width: 350,
      top: 'auto',
      bottom: 0,
      borderTopLeftRadius: 12,
      borderTopRightRadius: 12,
      position: 'fixed',
      right: 0,
      boxShadow: '0px 0px 15px rgba(0,0,0,0.3)',
      display: 'flex',              // NEW: make the paper a column flex-box
      flexDirection: 'column',
      overflow: 'hidden',           // NEW: contain inner scrolling to content only
    },
  }}
>
  {/* container for header + content */}
  <Box sx={{ background: 'white', height: '100%', display: 'flex', flexDirection: 'column' }}>
    
    {/* Sticky header */}
    <Box
      sx={{
        color: 'white',
        p: '15px 20px',
        backgroundColor: '#C5A572',
        borderBottom: '1px solid #e9ecef',
        fontSize: '15px',
        fontWeight: 600,
        position: 'sticky',   // NEW
        top: 0,               // NEW
        zIndex: 1,            // NEW: keep above scrolling content
      }}
    >
      <Typography
        sx={{
          fontWeight: 'bold',
          fontSize: '16px',
          color: '#FDF9F3',
          letterSpacing: 0.5,
        }}
      >
        Review Appointment Details
      </Typography>
    </Box>

    {/* Scrollable content */}
    <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
      <Stack direction="column" spacing={2}>
        {/* -- Date row -- */}
        <Stack
          padding="5px 10px"
          border="1px solid rgba(46, 45, 45, .2)"
          borderRadius="8px"
          direction="row"
          justifyContent="space-between"
        >
          <Typography
            gutterBottom
            variant="h3"
            component="div"
            sx={{ fontWeight: 600, padding: '10px 0', fontSize: '14px', marginBottom: 0 }}
          >
            Date
          </Typography>
          <Typography
            gutterBottom
            variant="h3"
            component="div"
            sx={{ fontWeight: 500, padding: '10px 0', fontSize: '14px', marginBottom: 0 }}
          >
            {selectedDateAppointment
              ? selectedDateAppointment.format('ddd, MMM D, YYYY')
              : 'No date selected'}
          </Typography>
        </Stack>

        {/* -- Dentist row -- */}
        <Stack
          padding="5px 10px"
          border="1px solid rgba(46, 45, 45, .2)"
          borderRadius="8px"
          direction="row"
          justifyContent="space-between"
        >
          <Typography
            gutterBottom
            variant="h3"
            component="div"
            sx={{ fontWeight: 600, padding: '10px 0', fontSize: '14px', marginBottom: 0 }}
          >
            Dentist
          </Typography>
          <Typography
            gutterBottom
            variant="h3"
            component="div"
            sx={{
              fontWeight: selectedDentist ? 'normal' : 'bold',
              padding: '10px 0',
              fontSize: '14px',
              color: selectedDentist && selectedDentist !== '' ? '#2C251E' : '#9F4223',
              marginBottom: 0,
            }}
          >
            {selectedDentist && selectedDentist !== '' ? selectedDentist : 'Select a dentist'}
          </Typography>
        </Stack>

        {/* -- Time row -- */}
        <Stack
          padding="5px 10px"
          border="1px solid rgba(46, 45, 45, .2)"
          borderRadius="8px"
          direction="row"
          justifyContent="space-between"
        >
          <Typography
            gutterBottom
            variant="h3"
            component="div"
            sx={{ fontWeight: 600, padding: '10px 0', fontSize: '14px', marginBottom: 0 }}
          >
            Time
          </Typography>
          <Typography
            gutterBottom
            variant="h3"
            component="div"
            sx={{
              fontWeight: selectedTimeSlot ? 'normal' : 'bold',
              padding: '10px 0',
              fontSize: '14px',
              marginBottom: 0,
              color: selectedTimeSlot ? '#2C251E' : '#9F4223',
            }}
          >
            {selectedTimeSlot ? (() => {
              // Calculate total duration from selected services
              const totalDuration = selectedServices.reduce((total, serviceName) => {
                const service = services.find(s => s.name === serviceName);
                const duration = getServiceDurationMinutes(service);
                if (duration) return total + duration;
                return total;
              }, 0);

              // If no duration found in services, default to 30 minutes
              const appointmentDuration = totalDuration > 0 ? totalDuration : 30;
              const startTimeMinutes = selectedTimeSlot.start;
              const endTimeMinutes = startTimeMinutes + appointmentDuration;
              
              // Format time helper
              const formatTimeDisplay = (minutes) => {
                const hours = Math.floor(minutes / 60);
                const mins = minutes % 60;
                const period = hours >= 12 ? "PM" : "AM";
                const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
                return `${displayHours}:${mins.toString().padStart(2, "0")} ${period}`;
              };
              
              const startTime = formatTimeDisplay(startTimeMinutes);
              const endTime = formatTimeDisplay(endTimeMinutes);
              
              return `${startTime} - ${endTime}`;
            })() : 'Select time slot'}
          </Typography>
        </Stack>

        {/* -- Services row -- */}
        <Stack
          padding="5px 10px"
          border="1px solid rgba(46, 45, 45, .2)"
          borderRadius="8px"
          direction={selectedServices.length > 0 ? 'column' : 'row'}
          justifyContent="space-between"
        >
          <Typography
            gutterBottom
            variant="h3"
            component="div"
            sx={{ fontWeight: 600, padding: '10px 0', fontSize: '14px', marginBottom: 0 }}
          >
            Services
          </Typography>

          <Box
            display="flex"
            flexWrap="wrap"
            gap="8px"
            sx={{
              maxWidth: '100%',
              maxHeight: 150,
              overflowY: 'auto',
            }}
          >
            {selectedServices.length > 0 ? (
              selectedServices.map((service, idx) => (
                <span
                  key={`${idx}-${formatServiceForPatientDisplay(service)}`}
                  style={{
                    padding: '6px 10px',
                    backgroundColor: '#F4F1EC',
                    borderRadius: '8px',
                    fontSize: '14px',
                    border: '1px solid rgba(197,165,114,0.4)',
                    color: '#2C251E',
                    display: 'inline-flex',
                    alignItems: 'center',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formatServiceForPatientDisplay(service)}
                </span>
              ))
            ) : (
              <Typography
                gutterBottom
                variant="h3"
                component="div"
                sx={{
                  fontWeight: 'bold',
                  padding: '10px 0',
                  fontSize: '14px',
                  marginBottom: 0,
                  color: '#9F4223',
                }}
              >
                Select a Service(s)
              </Typography>
            )}
          </Box>
        </Stack>
      </Stack>
    </Box>
  </Box>
</Drawer>


                {/* Booking Success Snackbar */}
                {showBookingSnackbar && bookingStatus !== "" && (
                <Snackbar
                  key={bookingStatus}
                  open={true}
                  autoHideDuration={10000}
                  onClose={(event, reason) => {
                    if (reason === 'clickaway') return;
                    setShowBookingSnackbar(false);
                    setBookingStatus("");
                  }}
                  anchorOrigin={{ vertical: "top", horizontal: "center" }}
                  sx={{
                    "& .MuiSnackbar-root": {
                      maxWidth: "none"
                    }
                  }}
                >
                  <Alert
                    onClose={() => {
                      setShowBookingSnackbar(false);
                      setBookingStatus("");
                    }}
                    severity={snackbarSeverity}
                    variant="filled"
                    icon={false}
                    sx={{
                      backgroundColor: snackbarSeverity === "error" 
                        ? (bookingStatus.includes("Appointment failed. The time slot is already reserved for another patient") ||
                           bookingStatus.includes("Appointment failed. You have already booked this time slot") ||
                           bookingStatus.includes("Appointment failed. The selected services") ||
                           bookingStatus.includes("Booking failed. The selected services") ||
                           bookingStatus.includes("The total duration of the selected services exceeds the available timeslot.") ||
                           bookingStatus.includes("Booking failed. The total duration of the appointment overlaps with an unavailable time slot.") ||
                           bookingStatus.includes("Booking failed. The total duration of the appointment overlaps with another confirmed patient appointment.") ||
                           bookingStatus.includes("Booking failed. The total duration of the appointment overlaps with another completed patient appointment.") ||
                           bookingStatus.includes("would result in an overlap") ||
                           bookingStatus.includes("no longer available") ||
                           bookingStatus.includes("Please select"))
                          ? "#9F4223"
                          : "#d32f2f"
                        : "#558363",
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: "14px",
                      letterSpacing: 0.2,
                      borderRadius: "12px",
                      padding: "8px 16px",
                      minWidth: "auto",
                      width: "fit-content",
                      maxWidth: "none",
                      boxShadow: "0 12px 24px rgba(0,0,0,0.18)",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      textAlign: "center",
                      "& .MuiAlert-message": {
                        fontSize: "14px",
                        color: "#fff",
                        fontWeight: 600,
                        padding: 0,
                        whiteSpace: "nowrap",
                        overflow: "visible"
                      }
                    }}
                  >
                    {bookingStatus}
                  </Alert>
                </Snackbar>
                )}


      {/* Insurance Confirmation Dialog */}
      <Dialog
        open={showInsuranceModal}
        onClose={(event, reason) => {
          // Only allow closing with escape key or close button
          if (reason === 'escapeKeyDown') {
            setShowInsuranceModal(false);
            setInsuranceAgreement(false);
            setInsuranceChoice(null);
          }
        }}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '16px',
            padding: '0',
            backgroundColor: '#FEFEFE',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '24px 32px 16px 32px',
            borderBottom: '1px solid #e0e0e0',
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: '600',
              fontSize: '24px',
              color: '#2C251E',
              textAlign: 'center',
              flex: 1,
            }}
          >
            Do you have insurance?
          </Typography>
          <IconButton
            onClick={() => {
              setShowInsuranceModal(false);
              setInsuranceAgreement(false);
              setInsuranceChoice(null);
            }}
            sx={{
              color: '#C5A572',
              border: '2px solid #C5A572',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              '&:hover': {
                backgroundColor: '#C5A572',
                color: '#FDF9F3',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ padding: '0 32px 10px 32px' }}>
          {/* Insurance Choice Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: '20px', marginTop: '20px', marginBottom: '20px' }}>
            <Button
              onClick={() => handleInsuranceChoice(true)}
              disabled={isYesButtonLoading || isNoButtonLoading}
              sx={{
                background: insuranceChoice === true ? '#4CAF50' : '#e8f5e8',
                color: insuranceChoice === true ? 'white' : '#4CAF50',
                border: '2px solid #4CAF50',
                padding: '12px 32px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                minWidth: '120px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                textTransform: 'none',
                '&:hover': {
                  background: insuranceChoice === true ? '#45a049' : '#d4edda',
                },
                '&:disabled': {
                  background: '#e9ecef',
                  color: '#6c757d',
                  border: '2px solid #e9ecef',
                },
              }}
            >
              {isYesButtonLoading ? (
                <>
                  <CircularProgress size={16} sx={{ color: insuranceChoice === true ? 'white' : '#4CAF50' }} />
                  <span>Yes</span>
                </>
              ) : (
                'Yes'
              )}
            </Button>
            
            <Button
              onClick={() => handleInsuranceChoice(false)}
              disabled={isYesButtonLoading || isNoButtonLoading}
              sx={{
                background: insuranceChoice === false ? '#F44336' : '#ffebee',
                color: insuranceChoice === false ? 'white' : '#F44336',
                border: '2px solid #F44336',
                padding: '12px 32px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                minWidth: '120px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                textTransform: 'none',
                '&:hover': {
                  background: insuranceChoice === false ? '#d32f2f' : '#ffcdd2',
                },
                '&:disabled': {
                  background: '#e9ecef',
                  color: '#6c757d',
                  border: '2px solid #e9ecef',
                },
              }}
            >
              {isNoButtonLoading ? (
                <>
                  <CircularProgress size={16} sx={{ color: insuranceChoice === false ? 'white' : '#F44336' }} />
                  <span>No</span>
                </>
              ) : (
                'No'
              )}
            </Button>
          </Box>

          {/* Conditional Content */}
          {insuranceChoice === true && (
            <>
              {/* Important Information Box */}
              <Box
                sx={{
                  marginBottom: '20px',
                  maxHeight: '300px',
                  overflowY: 'auto',
                  background: '#fffbe6',
                  border: '2px solid #c7a76c',
                  borderRadius: '12px',
                  padding: '20px',
                  fontSize: '15px',
                  color: '#23201A',
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 'bold',
                    color: '#d32f2f',
                    fontSize: '16px',
                    marginBottom: '16px',
                    textAlign: 'center',
                    borderBottom: '2px solid #c7a76c',
                    paddingBottom: '8px',
                  }}
                >
                  ⚠️ IMPORTANT: PLEASE READ CAREFULLY ⚠️
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', lineHeight: '1.5' }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <Typography sx={{ color: '#4CAF50', fontSize: '18px' }}>✓</Typography>
                    <Typography>We will first verify your dental coverage with your provider before we can schedule an appointment.</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <Typography sx={{ color: '#4CAF50', fontSize: '18px' }}>✓</Typography>
                    <Typography>Please note that the verification process from your HMO provider <strong>MAY TAKE A WHILE</strong> as we need to coordinate with the insurance hotline.</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <Typography sx={{ color: '#4CAF50', fontSize: '18px' }}>✓</Typography>
                    <Typography>If you have time constraints on the day of your appointment, and/or if you are in a hurry to get your verification, we kindly recommend rescheduling to avoid any inconvenience.</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <Typography sx={{ color: '#4CAF50', fontSize: '18px' }}>✓</Typography>
                    <Typography>Always bring your HMO PHYSICAL/DIGITAL CARD and VALID ID on the day of appointment.</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <Typography sx={{ color: '#4CAF50', fontSize: '18px' }}>✓</Typography>
                    <Typography>Please be advised that extra fees may apply if the procedure is not covered by your health card.</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <Typography sx={{ color: '#4CAF50', fontSize: '18px' }}>✓</Typography>
                    <Typography>ICP Fee (incl. isolation gown): <strong>200 pesos per patient</strong></Typography>
                  </Box>
                </Box>
              </Box>

              {/* Agreement Checkbox */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
            
                  padding: '16px',
                  background: '#f0f0f0',
                  borderRadius: '8px',
                  border: '1px solid #ddd',
                }}
              >
                <input
                  type="checkbox"
                  id="insuranceAgreement"
                  checked={insuranceAgreement}
                  onChange={(e) => setInsuranceAgreement(e.target.checked)}
                  style={{
                    width: '20px',
                    height: '20px',
                    cursor: 'pointer',
                  }}
                />
                <label
                  htmlFor="insuranceAgreement"
                  style={{
                    cursor: 'pointer',
                    fontSize: '15px',
                    color: '#333',
                    fontWeight: '500',
                  }}
                >
                  I have read and understood the insurance verification process and agree to proceed
                </label>
              </Box>
            </>
          )}

          {insuranceChoice === false && (
            <Box
              sx={{
                padding: '20px',
                background: '#f5f5f5',
                border: '1px solid #ddd',
                borderRadius: '8px',
                textAlign: 'center',
                color: '#666',
                fontSize: '15px',
              }}
            >
              Please ignore this if you don't have insurance. You can proceed with your appointment booking.
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ padding: '10px 32px 20px 32px', justifyContent: 'center' }}>
          {insuranceChoice === null ? null : (
            <Button
              onClick={() => confirmInsuranceAndSubmit(insuranceChoice)}
              disabled={(insuranceChoice === true && !insuranceAgreement) || isProceedButtonLoading}
              sx={{
                background: ((insuranceChoice === true && !insuranceAgreement) || isProceedButtonLoading) ? '#ccc' : '#C5A572',
                color: 'white',
                border: 'none',
                padding: '14px 32px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                minWidth: '200px',
                textTransform: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                '&:hover': {
                  backgroundColor: ((insuranceChoice === true && !insuranceAgreement) || isProceedButtonLoading) ? '#ccc' : '#B8955A',
                },
                '&:disabled': {
                  backgroundColor: '#ccc',
                  color: '#999',
                },
              }}
            >
              {isProceedButtonLoading ? (
                <>
                  <CircularProgress size={16} sx={{ color: 'white' }} />
                  <span>{insuranceChoice === true ? 'Proceeding...' : 'Proceeding...'}</span>
                </>
              ) : (
                insuranceChoice === true ? 'Proceed with Insurance' : 'Proceed without Insurance'
              )}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Terms and Conditions Dialog */}
      <Dialog
        open={showTermsModal}
        onClose={(event, reason) => {
          // Only allow closing with escape key or close button
          if (reason === 'escapeKeyDown') {
            setShowTermsModal(false);
            setTermsAgreement(false);
          }
        }}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '16px',
            padding: '0',
            backgroundColor: '#FEFEFE',
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '24px 32px 16px 32px',
            borderBottom: '1px solid #e0e0e0',
          }}
        >
          <Stack
            direction="column"
            width="100%">

          <Box
            sx={{
              justifyItems: "end",
              mb: {
                xs : "10px",
                sm : "10px",
                md : 0,
                lg : 0,
                xl : 0,
              }
            }}>
            <Box
              onClick={() => {
                setShowTermsModal(false);
                setTermsAgreement(false);
              }}
              sx={{
                width: "35px",
                height: "35px",
                border: "2px solid #C5A572",
                borderRadius: "50%",
                cursor: "pointer",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                '&:hover': {
                  backgroundColor: 'rgba(197, 165, 114, 1)',
                },
              }}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              <Icon
                src={
                  isHovered ? require("../../assets/close_white.png") :
                  require("../../assets/close_light.png")
                }
                size="small-no-margin"
                />
            </Box>
          </Box>

          <Box sx={{ flex: 1, textAlign: 'center' }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: '600',
                fontSize: '24px',
                color: '#2C251E',
                marginBottom: '8px',
              }}
            >
              Terms of Service Agreement
            </Typography>
            <Typography
              sx={{
                letterSpacing: .2,
                fontSize: '14px',
                color: '#59544A',
              }}
            >
              By booking an appointment with VSDIAMOND Dental Clinic, you agree to the following:
            </Typography>
          </Box>
          </Stack>


        </DialogTitle>

        <DialogContent sx={{ padding: '20px 32px 20px 32px' }}>
          {/* Terms Content */}
          <Box
            sx={{
              marginTop: '20px',
              marginBottom: '20px',
              maxHeight: '300px',
              overflowY: 'auto',
              background: '#fff',
              border: '2px solid #e0d6c3',
              borderRadius: '12px',
              padding: '24px',
              fontSize: '15px',
              color: '#333',
              lineHeight: '1.6',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

              <Stack
                direction="column"
                spacing={4}>

                {/* 1 */}
                <Stack
                  direction="column"
                  spacing={1}>
                  <Text
                    text="1. Appointments & Cancellations"
                    variant="termsTitle"/>
                  <Text
                    text="Please cancel or reschedule at least 24 hours before your appointment."
                    variant="termsContent"/>
                </Stack>

                {/* 2 */}
                <Stack
                  direction="column"
                  spacing={1}>
                  <Text
                    text="2. Appointment Delays"
                    variant="termsTitle"/>
                    <Stack
                      direction="column"
                      spacing={1}>

                      <Text
                        text="2.1 You agree to wait or reschedule if the previous patient's appointment takes longer than expected due to unforeseen delays or procedures that require additional time."
                        variant="termsContent"/>

                      <Text
                        text="2.2 If you are in a hurry, we recommend rescheduling your appointment on a different day."
                        variant="termsImportant"/>

                    </Stack>
                </Stack>

                {/* 3 */}
                <Stack
                  direction="column"
                  spacing={1}>
                  <Text
                    text="3. Treatment Consent"
                    variant="termsTitle"/>
                  <Text
                    text="You consent to the proposed dental procedures and authorize our dental team to make necessary adjustments during treatment in your best interest."
                    variant="termsContent"/>
                </Stack>

                {/* 4 */}
                <Stack
                  direction="column"
                  spacing={1}>
                  <Text
                    text="4. Payments"
                    variant="termsTitle"/>
                  <Text
                    text="All payments must be settled in the clinic, either in cash or via cashless methods. You are responsible for any fees not covered by your insurance provider."
                    variant="termsContent"/>
                </Stack>

                {/* 5 */}
                <Stack
                  direction="column"
                  spacing={1}>
                  <Text
                    text="5. Post-Treatment Care"
                    variant="termsTitle"/>
                  <Text
                    text="Follow all aftercare instructions provided. The clinic is not liable for complications resulting from failure to follow them."
                    variant="termsContent"/>
                </Stack>

                {/* 6 */}
                <Stack
                  direction="column"
                  spacing={1}>
                  <Text
                    text="6. Limitation of Liability"
                    variant="termsTitle"/>
                  <Text
                    text="The clinic is not responsible for issues arising from undisclosed medical conditions, failure to follow instructions, or factors beyond our control."
                    variant="termsContent"/>
                </Stack>

                {/* 7 */}
                <Stack
                  direction="column"
                  spacing={1}>
                  <Text
                    text="7. Privacy"
                    variant="termsTitle"/>
                  <Text
                    text="Your personal and medical information will be handled in accordance with our Privacy Policy."
                    variant="termsContent"/>
                </Stack>

              </Stack>
            </Box>
          </Box>

          {/* Agreement Checkbox */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '15px 20px',
              background: '#f0f0f0',
              borderRadius: '8px',
              border: '1px solid #ddd',
            }}
          >
            <input
              type="checkbox"
              id="termsAgreement"
              checked={termsAgreement}
              onChange={(e) => setTermsAgreement(e.target.checked)}
              style={{
                width: '20px',
                height: '20px',
                cursor: 'pointer',
              }}
            />
            <label
              htmlFor="termsAgreement"
              style={{
                cursor: 'pointer',
                fontSize: '14px',
                color: '#2C251E',
                fontWeight: '600',
              }}
            >
              I have read, understood, and agree to the Terms of Service Agreement above
            </label>
          </Box>
        </DialogContent>

        <DialogActions sx={{ padding: '0 32px 20px 32px', justifyContent: 'center' }}>

          <Button
            onClick={handleTermsAgreement}
            disabled={!termsAgreement || isTermsAgreeLoading}
            sx={{
              background: (!termsAgreement || isTermsAgreeLoading) ? '#ccc' : '#C5A572',
              color: 'white',
              border: 'none',
              padding: '10px 30px',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              minWidth: '200px',
              textTransform: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              '&:hover': {
                backgroundColor: (!termsAgreement || isTermsAgreeLoading) ? '#ccc' : '#B8955A',
              },
              '&:disabled': {
                backgroundColor: '#ccc',
                color: '#999',
              },
            }}
          >
            {isTermsAgreeLoading ? (
              <>
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid #ffffff',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                <span>Processing...</span>
              </>
            ) : (
              !termsAgreement ? 'Click "Agree"' : 'I Agree & Continue'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Insurance Form Dialog */}
      <Dialog
        open={showInsuranceForm}
        onClose={(event, reason) => {
          // Only allow closing with escape key or close button
          if (reason === 'escapeKeyDown') {
            setShowInsuranceForm(false);
          }
        }}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '16px',
            padding: '0',
            backgroundColor: '#FEFEFE',
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '24px 32px 16px 32px',
            borderBottom: '1px solid #e0e0e0',
          }}
        >
          <Box sx={{ flex: 1, textAlign: 'center' }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: '600',
                fontSize: '28px',
                color: '#2C251E',
                marginBottom: '8px',
              }}
            >
              Insurance Information
            </Typography>
            <Typography
              sx={{
                fontSize: '16px',
                color: '#666',
                fontStyle: 'italic',
              }}
            >
              Please provide your insurance details to help us verify coverage
            </Typography>
          </Box>
          <IconButton
            onClick={() => setShowInsuranceForm(false)}
            sx={{
              color: '#C5A572',
              border: '2px solid #C5A572',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              '&:hover': {
                backgroundColor: '#C5A572',
                color: '#FDF9F3',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ padding: '32px' }}>
          <PatientInsuranceForm
            onSubmit={handleInsuranceFormSubmit}
            onClose={() => {
              setShowInsuranceForm(false);
              setShowInsuranceModal(true);
            }}
            initialData={{
              name: (() => {
                // Build full name with first, middle, and last name
                if (userDetails?.firstName || userDetails?.lastName) {
                  const nameParts = [
                    userDetails?.firstName,
                    userDetails?.middleName,
                    userDetails?.lastName
                  ].filter(Boolean);
                  return nameParts.join(" ").trim();
                }
                return userDetails?.name || "";
              })(),
              contactNumber: userDetails?.contactNumber || "",
              birthdate: (() => {
                // Convert birthdate from YYYY-MM-DD (database format) to MM-DD-YYYY (form format)
                const bdate = userDetails?.birthDate || userDetails?.birthdate || "";
                if (bdate && bdate.match(/^\d{4}-\d{2}-\d{2}$/)) {
                  const [year, month, day] = bdate.split("-");
                  return `${month}-${day}-${year}`;
                }
                // If already in MM-DD-YYYY format, use as is
                return bdate;
              })()
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Existing Appointment Modal */}
      <Modal
        isOpen={showExistingAppointmentModal}
        onRequestClose={() => {
          setShowExistingAppointmentModal(false);
          setExistingAppointment(null);
        }}
        contentLabel="Existing Appointment"
        style={{
          overlay: {
            zIndex: 9999,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "16px",
          },
          content: {
            border: "none",
            borderRadius: "16px",
            background: "#FEFEFE",
            width: "75%",
            maxHeight: "85vh",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
            transition: "all 0.3s ease",
            top: "50%",
            left: "50%",
            right: "auto",
            bottom: "auto",
            transform: "translate(-50%, -50%)",
            padding: "35px",
            maxWidth: "600px",
            minWidth: "180px"
          },
        }}
      >
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            mb: "20px"
          }}
        >
          <Typography
            variant="h3"
            component="div"
            sx={{
              fontWeight: "600",
              fontSize: "24px",
              marginBottom: "0px",
              flex: 1
            }}
          >
            You already have an existing appointment for this date.
          </Typography>
          
          <Tooltip
            title="Close"
            placement="bottom"
            arrow
            slotProps={{
              popper: {
                sx: {
                  zIndex: 9999999,
                }
              }
            }}
          >
            <Box
              onClick={() => {
                setShowExistingAppointmentModal(false);
                setExistingAppointment(null);
              }}
              sx={{
                width: "35px",
                height: "35px",
                border: "2px solid #C5A572",
                borderRadius: "50%",
                cursor: "pointer",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                '&:hover': {
                  backgroundColor: 'rgba(197, 165, 114, 1)',
                },
              }}
            >
              <Icon
                src={require("../../assets/close_light.png")}
                size="small-no-margin"
              />
            </Box>
          </Tooltip>
        </Stack>

        <Divider sx={{ mb: "20px" }} />

        <Box sx={{ padding: "0", mb: "20px" }}>

          {/* Existing Appointment Details */}
          <Stack direction="column" spacing={2}>
            <Stack
              padding="5px 10px"
              border="1px solid rgba(46, 45, 45, .2)"
              borderRadius="8px"
              direction="row"
              justifyContent="space-between"
            >
              <Typography
                variant="h3"
                component="div"
                sx={{
                  fontWeight: "600",
                  padding: "15px 0",
                  fontSize: "17px",
                  marginBottom: "0px",
                }}
              >
                Date
              </Typography>
              <Typography
                variant="h3"
                component="div"
                sx={{
                  fontWeight: "500",
                  padding: "15px 0",
                  fontSize: "17px",
                  marginBottom: "0px",
                }}
              >
                {existingAppointment?.date ? formatDateLong(existingAppointment.date) : "N/A"}
              </Typography>
            </Stack>

            <Stack
              padding="5px 10px"
              border="1px solid rgba(46, 45, 45, .2)"
              borderRadius="8px"
              direction="row"
              justifyContent="space-between"
            >
              <Typography
                variant="h3"
                component="div"
                sx={{
                  fontWeight: "600",
                  padding: "15px 0",
                  fontSize: "17px",
                  marginBottom: "0px",
                }}
              >
                Time
              </Typography>
              <Typography
                variant="h3"
                component="div"
                sx={{
                  fontWeight: "500",
                  padding: "15px 0",
                  fontSize: "17px",
                  marginBottom: "0px",
                }}
              >
                {formatExistingAppointmentModalTime(existingAppointment)}
              </Typography>
            </Stack>

            <Stack
              padding="5px 10px"
              border="1px solid rgba(46, 45, 45, .2)"
              borderRadius="8px"
              direction="row"
              justifyContent="space-between"
            >
              <Typography
                variant="h3"
                component="div"
                sx={{
                  fontWeight: "600",
                  padding: "15px 0",
                  fontSize: "17px",
                  marginBottom: "0px",
                }}
              >
                Dentist
              </Typography>
              <Typography
                variant="h3"
                component="div"
                sx={{
                  fontWeight: "500",
                  padding: "15px 0",
                  fontSize: "17px",
                  marginBottom: "0px",
                }}
              >
                {existingAppointment?.dentist || "N/A"}
              </Typography>
            </Stack>

            <Stack
              padding="5px 10px"
              border="1px solid rgba(46, 45, 45, .2)"
              borderRadius="8px"
              direction="column"
            >
              <Typography
                variant="h3"
                component="div"
                sx={{
                  fontWeight: "600",
                  padding: "15px 0 10px 0",
                  fontSize: "17px",
                  marginBottom: "0px",
                }}
              >
                Services
              </Typography>
              <Box
                display="flex"
                flexWrap="wrap"
                gap="8px"
                sx={{
                  maxWidth: "100%",
                  maxHeight: "100px",
                  overflowY: "auto",
                }}
              >
                {existingAppointment?.services && existingAppointment.services.length > 0 ? (
                  existingAppointment.services.map((service, index) => (
                    <span
                      key={`${index}-${formatServiceForPatientDisplay(service)}`}
                      style={{
                        padding: "6px 10px",
                        backgroundColor: "#F4F1EC",
                        borderRadius: "8px",
                        fontSize: "14px",
                        border: "1px solid rgba(197,165,114,0.4)",
                        color: "#2C251E",
                        display: "inline-flex",
                        alignItems: "center",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {formatServiceForPatientDisplay(service)}
                    </span>
                  ))
                ) : (
                  <span style={{ color: "#adb5bd", fontSize: "13px" }}>No services</span>
                )}
              </Box>
            </Stack>

            <Stack
              padding="5px 10px"
              border="1px solid rgba(46, 45, 45, .2)"
              borderRadius="8px"
              direction="row"
              justifyContent="space-between"
            >
              <Typography
                variant="h3"
                component="div"
                sx={{
                  fontWeight: "600",
                  padding: "15px 0",
                  fontSize: "17px",
                  marginBottom: "0px",
                }}
              >
                Insurance
              </Typography>
              <Typography
                variant="h3"
                component="div"
                sx={{
                  fontWeight: "500",
                  padding: "15px 0",
                  fontSize: "17px",
                  marginBottom: "0px",
                }}
              >
                {existingAppointment?.hasInsurance && existingAppointment?.insuranceDetails
                  ? (existingAppointment.insuranceDetails.companyName || "Yes")
                  : "None"}
              </Typography>
            </Stack>

            <Stack
              padding="5px 10px"
              border="1px solid rgba(46, 45, 45, .2)"
              borderRadius="8px"
              direction="row"
              justifyContent="space-between"
            >
              <Typography
                variant="h3"
                component="div"
                sx={{
                  fontWeight: "600",
                  padding: "15px 0",
                  fontSize: "17px",
                  marginBottom: "0px",
                }}
              >
                Status
              </Typography>
              <Typography
                variant="h3"
                component="div"
                sx={{
                  fontWeight: "500",
                  padding: "15px 0",
                  fontSize: "17px",
                  marginBottom: "0px",
                }}
              >
                {existingAppointment?.status || "Pending"}
              </Typography>
            </Stack>
          </Stack>
        </Box>

        {/* Warning Message */}
        <Box
          sx={{
            padding: "16px",
            backgroundColor: "#FFF3CD",
            border: "1px solid #FFC107",
            borderRadius: "8px",
            mb: "20px"
          }}
        >
          <Typography
            sx={{
              fontSize: "14px",
              color: "#856404",
              lineHeight: "1.5"
            }}
          >
            Clicking "Cancel Previous Appointment" will automatically cancel your existing appointment, after which you may proceed with booking a new appointment.
          </Typography>
        </Box>

        {/* Action Buttons */}
        <Stack direction="row" spacing={2} justifyContent="center">
          <Button
            onClick={() => {
              setShowExistingAppointmentModal(false);
              setExistingAppointment(null);
            }}
            disabled={isBookingLoading}
            sx={{
              background: "#6c757d",
              color: "white",
              border: "none",
              padding: "10px 30px",
              borderRadius: "8px",
              fontSize: "16px",
              fontWeight: "600",
              minWidth: "150px",
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#5a6268",
              },
            }}
          >
            CANCEL
          </Button>
          <Button
            onClick={handleCancelExistingAndProceed}
            disabled={isBookingLoading}
            sx={{
              background: isBookingLoading ? "#ccc" : "#C5A572",
              color: "white",
              border: "none",
              padding: "10px 30px",
              borderRadius: "8px",
              fontSize: "16px",
              fontWeight: "600",
              minWidth: "200px",
              textTransform: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              "&:hover": {
                backgroundColor: isBookingLoading ? "#ccc" : "#B8955A",
              },
              "&:disabled": {
                backgroundColor: "#ccc",
                color: "#999",
              },
            }}
          >
            {isBookingLoading ? (
              <>
                <CircularProgress size={16} sx={{ color: "#ffffff" }} />
                <span>Processing...</span>
              </>
            ) : (
              "CANCEL PREVIOUS APPOINTMENT"
            )}
          </Button>
        </Stack>
      </Modal>

      {/* Appointment Details Modal */}
      <Modal
        isOpen={showBookingSummary}
        onRequestClose={() => {
          setShowBookingSummary(false); 
          setActiveStep(0);
          setIsHovered(false);
          setInsuranceChoice(null);
          setInsuranceNone(false);
          setInsuranceAgreement(false);
          handleTooltipConfirmClose(false);
        }}
        contentLabel="Appointment Details"
                      style={{
                        overlay: {
                          zIndex: 9999,
                          backgroundColor: "rgba(0, 0, 0, 0.7)",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          padding: "16px",
                        },
                        content: {
                          // inset: "unset",
                          border: "none",
                          borderRadius: "16px",
                          background: "#FEFEFE",
                          // padding: "24px",
                          width: "75%",
                          maxHeight: "85vh",
                          // overflowY: "auto",
                          display: "flex",
                          flexDirection: "column",
                          // justifyContent: "center",
                          // alignItems: "center",
                          boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
                          // Responsive adjustments
                          transition: "all 0.3s ease",
                          top: "50%",
                          left: "50%",
                          right: "auto",
                          bottom: "auto",
                          // margin: "20px",
                          // // marginRight: "-50%",
                          transform: "translate(-50%, -50%)",
                          padding: "35px",
                          maxWidth: "600px",
                          minWidth: "180px"
                        },
                      }}
      >
        <Box
          sx={{
            maxHeight: "300px",
          }}>
<Stack
  direction="row"
  sx={{
    justifyContent: "space-between",
    alignItems: "center",
    mb: "10px"
  }}>
  
              <Box 
                sx={{
                  color: "white",
                  padding: "16px 0",
                  // borderBottom: "1px solid #e9ecef",
                  fontSize: "15px",
                  fontWeight: "600",
                  textAlign: "center"
                }}
              >
              <Typography
                gutterBottom
                variant="h3"
                component="div"
                sx={{
                  fontWeight: "600",
                  // padding: "10px 10px",
                  fontSize: "24px",
                  // backgroundColor: "aqua",
                  marginBottom: "0px",
                  textAlign: "center",
                }}
              >
                {steps[activeStep].label}
              </Typography>
              </Box>
              
        {/* Close Button */}
              <Tooltip
        title="Close"
        placement="bottom"
        arrow
        slotProps={{
        popper: {
          sx: {
            zIndex: 9999999, // ensures tooltip appears above modal
          }
        }
      }}
      >
          <Box
            sx={{
              justifyItems: "end",
              mb: {
                xs : "10px",
                sm : "10px",
                md : 0,
                lg : 0,
                xl : 0,
              }
            }}>
            <Box
              onClick={() => {
                setShowBookingSummary(false); 
                setActiveStep(0);
                setIsHovered(false);
                setInsuranceChoice(null);
                setInsuranceNone(false);
                setInsuranceAgreement(false);
                handleTooltipConfirmClose(false);
              }}
              sx={{
                width: "35px",
                height: "35px",
                border: "2px solid #C5A572",
                borderRadius: "50%",
                cursor: "pointer",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                '&:hover': {
                  backgroundColor: 'rgba(197, 165, 114, 1)',
                },
              }}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              <Icon
                src={
                  isHovered ? require("../../assets/close_white.png") :
                  require("../../assets/close_light.png")
                }
                size="small-no-margin"
                />
            </Box>
          </Box>
          </Tooltip>
</Stack>

    <Box sx={{ width: '100%',
            mb: "20px" }}>
      <Stepper activeStep={activeStep} alternativeLabel >
        {label.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
    </Box>

    <Divider/>
    </Box>


<Box sx={{ width: "100%", overflow: "auto"}}>

      <Box
        maxHeight="450px"
        >
      {activeStep === 0 ? (
        <>

              
              <Box sx={{ padding: "24px" }}>
                {/* Summary Items */}
                <Box style={{ marginBottom: "24px", fontSize: "14px", lineHeight: "1.6" }}>
                  <Box style={{ marginBottom: "12px" }}>

                <Stack
                  direction="column"
                  spacing={2}>

                  <Stack
                    padding="5px 10px"
                    border="1px solid rgba(46, 45, 45, .2)"
                    borderRadius="8px"
                    direction="row"
                    justifyContent="space-between">
                  <Typography
                    gutterBottom
                    variant="h3"
                    component="div"
                    sx={{
                      fontWeight: "600",
                      padding: "15px 0",
                      fontSize: "17px",
                      // backgroundColor: "aqua",
                      marginBottom: "0px",
                    }}
                  >
                    Date
                  </Typography>
                  <Typography
                    gutterBottom
                    variant="h3"
                    component="div"
                    sx={{
                      fontWeight: "500",
                      padding: "15px 0",
                      fontSize: "17px",
                      // backgroundColor: "aqua",
                      marginBottom: "0px",
                    }}
                  >
                    {selectedDateAppointment 
                          ? selectedDateAppointment.format("ddd, MMM D, YYYY") 
                          : "No date selected"}
                  </Typography>
                  </Stack>

                  <Stack
                    padding="5px 10px"
                    border="1px solid rgba(46, 45, 45, .2)"
                    borderRadius="8px"
                    direction="row"
                    justifyContent="space-between">
                  <Typography
                    gutterBottom
                    variant="h3"
                    component="div"
                    sx={{
                      fontWeight: "600",
                      padding: "15px 0",
                      fontSize: "17px",
                      // backgroundColor: "aqua",
                      marginBottom: "0px",
                    }}
                  >
                    Dentist
                  </Typography>
                  <Typography
                    gutterBottom
                    variant="h3"
                    component="div"
                    sx={{
                      fontWeight: "500",
                      padding: "15px 0",
                      fontSize: "17px",
                      // backgroundColor: "aqua",
                      marginBottom: "0px",
                    }}
                  >
                    {selectedDentist && selectedDentist !== "" ? selectedDentist : "Select a dentist"}
                  </Typography>
                  </Stack>

                  <Stack
                    padding="5px 10px"
                    border="1px solid rgba(46, 45, 45, .2)"
                    borderRadius="8px"
                    direction="row"
                    justifyContent="space-between">
                  <Typography
                    gutterBottom
                    variant="h3"
                    component="div"
                    sx={{
                      fontWeight: "600",
                      padding: "15px 0",
                      fontSize: "17px",
                      // backgroundColor: "aqua",
                      marginBottom: "0px",
                    }}
                  >
                    Time
                  </Typography>
                  <Typography
                    gutterBottom
                    variant="h3"
                    component="div"
                    sx={{
                      fontWeight: "500",
                      padding: "15px 0",
                      fontSize: "17px",
                      // backgroundColor: "aqua",
                      marginBottom: "0px",
                    }}
                  >
                    {selectedTimeSlot ? (() => {
                      // Calculate total duration from selected services
                      const totalDuration = selectedServices.reduce((total, serviceName) => {
                        const service = services.find(s => s.name === serviceName);
                        const duration = getServiceDurationMinutes(service);
                        if (duration) return total + duration;
                        return total;
                      }, 0);

                      // If no duration found in services, default to 30 minutes
                      const appointmentDuration = totalDuration > 0 ? totalDuration : 30;
                      const startTimeMinutes = selectedTimeSlot.start;
                      const endTimeMinutes = startTimeMinutes + appointmentDuration;
                      
                      // Format time helper
                      const formatTimeDisplay = (minutes) => {
                        const hours = Math.floor(minutes / 60);
                        const mins = minutes % 60;
                        const period = hours >= 12 ? "PM" : "AM";
                        const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
                        return `${displayHours}:${mins.toString().padStart(2, "0")} ${period}`;
                      };
                      
                      const startTime = formatTimeDisplay(startTimeMinutes);
                      const endTime = formatTimeDisplay(endTimeMinutes);
                      
                      return `${startTime} - ${endTime}`;
                    })() : "Select time slot"}
                  </Typography>
                  </Stack>

                  <Stack
                    padding="5px 10px"
                    border="1px solid rgba(46, 45, 45, .2)"
                    borderRadius="8px"
                    // maxHeight="200px"
                    // overflowY="auto"
                    direction="column">
                  <Typography
                    gutterBottom
                    variant="h3"
                    component="div"
                    sx={{
                      fontWeight: "600",
                      padding: "15px 0",
                      fontSize: "17px",
                      // backgroundColor: "aqua",
                      marginBottom: "0px",
                    }}
                  >
                    Services
                  </Typography>

<Box
  display="flex"
  flexWrap="wrap"
  gap="8px"
  sx={{
    maxWidth: "100%",
    maxHeight: "150px",
    overflowY: "auto",
    // paddingTop: "4px",
  }}
>
  {selectedServices.length > 0 ? (
    selectedServices.map((service, idx) => (
      <span
        key={`${idx}-${formatServiceForPatientDisplay(service)}`}
        style={{
          padding: "6px 10px",
          backgroundColor: "#F4F1EC",
          borderRadius: "8px",
          fontSize: "14px",
          border: "1px solid rgba(197,165,114,0.4)",
          color: "#2C251E",
          display: "inline-flex",
          alignItems: "center",
          whiteSpace: "nowrap",
        }}
      >
        {formatServiceForPatientDisplay(service)}
      </span>
    ))
  ) : (
    <span style={{ color: "#adb5bd", fontSize: "13px" }}>
      Select services
    </span>
  )}
</Box>


                  </Stack>
                  
                </Stack>
                  </Box>
                </Box>


              </Box>
        </>
      ) : activeStep === 1 ? (
        <>


            <>
              {/* Important Information Box */}
          <Box
            sx={{
              display : showInsuranceFormModal === false ? "block" : "none",
              marginTop: '20px',
              marginBottom: '20px',
              maxHeight: '300px',
              overflowY: 'auto',
              background: '#fff',
              border: '2px solid #e0d6c3',
              borderRadius: '12px',
              padding: '24px',
              fontSize: '15px',
              color: '#333',
              lineHeight: '1.6',
            }}
          >
                <Typography
                  sx={{
                    fontWeight: 'bold',
                    color: '#d32f2f',
                    fontSize: '16px',
                    marginBottom: '16px',
                    textAlign: 'center',
                    borderBottom: '2px solid #c7a76c',
                    paddingBottom: '8px',
                  }}
                >
                  ⚠️ IMPORTANT: PLEASE READ CAREFULLY ⚠️
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', lineHeight: '1.5' }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <Typography sx={{ color: '#4CAF50', fontSize: '18px' }}>✓</Typography>
                    <Typography>We will first verify your dental coverage with your provider before we can schedule an appointment.</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <Typography sx={{ color: '#4CAF50', fontSize: '18px' }}>✓</Typography>
                    <Typography>Please note that the verification process from your HMO provider <strong>MAY TAKE A WHILE</strong> as we need to coordinate with the insurance hotline.</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <Typography sx={{ color: '#4CAF50', fontSize: '18px' }}>✓</Typography>
                    <Typography>If you have time constraints on the day of your appointment, and/or if you are in a hurry to get your verification, we kindly recommend rescheduling to avoid any inconvenience.</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <Typography sx={{ color: '#4CAF50', fontSize: '18px' }}>✓</Typography>
                    <Typography>Always bring your HMO PHYSICAL/DIGITAL CARD and VALID ID on the day of appointment.</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <Typography sx={{ color: '#4CAF50', fontSize: '18px' }}>✓</Typography>
                    <Typography>Please be advised that extra fees may apply if the procedure is not covered by your health card.</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <Typography sx={{ color: '#4CAF50', fontSize: '18px' }}>✓</Typography>
                    <Typography>ICP Fee (incl. isolation gown): <strong>200 pesos per patient</strong></Typography>
                  </Box>
                </Box>
              </Box>

          <Box
            sx={{
              display : showInsuranceFormModal === false ? "none" : "flex",
              marginTop: '20px',
              marginBottom: '20px',
              // width: "100%",
              maxHeight: '300px',
              overflowY: 'auto',
              background: '#FDF9F3',
              border: '2px solid #e0d6c3',
              borderRadius: '12px',
              padding: '0 0px',
              fontSize: '15px',
              color: '#333',
              lineHeight: '1.6',
            }}
          >
        {/*Grids */}
        <Grid
          container
          rowSpacing={2}
          // columnSpacing={2}
          sx={{
            width: "100%",
            paddingTop: "20px",
            "& .MuiGrid-item": {
              display: "flex",
              justifyContent: "center",
              // backgroundColor: "blue",
            },
          }}
        >

          {/* Patient Name */}
          <Grid
            size={{
              xs: 12,
              sm: 12,
              md: 12,
              lg: 12,
              xl: 12,
            }}
            item
            sx={{
              padding: "0 10px",
              // backgroundColor: "cadetblue"
            }}
          >


        {/* Patient Name */}
        <Stack
          direction="column"
          width="100%">
          <label style={{ 
            fontSize: "14px", 
            fontWeight: "600", 
            color: "#333",
            marginBottom: "4px"
          }}>
            Patient Name <span style={{ color : "red"}}>*</span>
          </label>
          <input 
            placeholder="Enter patient name"
            type="text"
            required
            value={name}
            maxLength="100"
            onChange={(e) => handleFieldChange('name', e.target.value)}
            style={{
              padding: "12px 16px",
              border: errors.name && touched.name ? "1px solid #f44336" : "1px solid #ddd",
              borderRadius: "8px",
              fontSize: "14px",
              backgroundColor: "#fff",
              transition: "border-color 0.2s ease",
              outline: "none"
            }}
            onFocus={(e) => e.target.style.borderColor = "#007bff"}
            onBlur={(e) => {
              e.target.style.borderColor = errors.name && touched.name ? "#f44336" : "#ddd";
              handleBlur('name');
            }}
            maxWidth
          />
          {errors.name && touched.name && (
            <div style={{ color: "#f44336", fontSize: 12, marginTop: 4 }}>
              {errors.name}
            </div>
          )}
          </Stack>
        
          </Grid>

          {/* Company Name*/}
          <Grid
            size={{
              xs: 12,
              sm: 12,
              md: 12,
              lg: 12,
              xl: 12,
            }}
            item
            sx={{
              padding: "0 10px",
              // backgroundColor: "cadetblue"
            }}
          >


        {/* Patient Name */}
        <Stack
          direction="column"
          width="100%">
            <label style={{ 
              fontSize: "14px", 
              fontWeight: "600", 
              color: "#333",
              marginBottom: "4px"
            }}>
              Insurance Company <span style={{ color : "red"}}>*</span>
            </label>
            <select
              required
              value={companyName}
              onChange={(e) => handleFieldChange('companyName', e.target.value)}
              style={{
                padding: "12px 16px",
                border: errors.companyName && touched.companyName ? "1px solid #f44336" : "1px solid #ddd",
                borderRadius: "8px",
                fontSize: "14px",
                backgroundColor: "#fff",
                transition: "border-color 0.2s ease",
                outline: "none",
                cursor: "pointer"
              }}
              onFocus={(e) => e.target.style.borderColor = "#007bff"}
              onBlur={(e) => {
                e.target.style.borderColor = errors.companyName && touched.companyName ? "#f44336" : "#ddd";
                handleBlur('companyName');
              }}
            >
              <option value="">Select insurance company</option>
              <option value="Intellicare (Your Intelligent Health Choice)">Intellicare (Your Intelligent Health Choice)</option>
              <option value="MediCard">MediCard</option>
              <option value="MaxiCare (Live Your Best Life)">MaxiCare (Live Your Best Life)</option>
              <option value="AVEGA">AVEGA</option>
              <option value="flexicare">flexicare</option>
              <option value="InLife (Insulare Health Care)">InLife (Insulare Health Care)</option>
              <option value="HMI (Health Maintenance, Inc.)">HMI (Health Maintenance, Inc.)</option>
              <option value="Lacson & Lacson (Insurance Brokers, Inc.)">Lacson & Lacson (Insurance Brokers, Inc.)</option>
              <option value="ValuCare (Healthcare You Can Count On)">ValuCare (Healthcare You Can Count On)</option>
              <option value="PhilCare (Healthcare Made Smarter)">PhilCare (Healthcare Made Smarter)</option>
              <option value="ASIANLIFE & GENERAL ASSURANCE CORPORATION">ASIANLIFE & GENERAL ASSURANCE CORPORATION</option>
              <option value="EastWest Healthcare (Securing a Healthy Horizon)">EastWest Healthcare (Securing a Healthy Horizon)</option>
              <option value="COCOLIFE HEALTHCARE">COCOLIFE HEALTHCARE</option>
              <option value="KAISER INTERNATIONAL (Health Group, Inc.)">KAISER INTERNATIONAL (Health Group, Inc.)</option>
              <option value="eTiQa Insurance">eTiQa Insurance</option>
              <option value="Life & Health (HMP Incorporated)">Life & Health (HMP Incorporated)</option>
              <option value="DNC (Dental Network Company)">DNC (Dental Network Company)</option>
              <option value="Health Partners Dental Access, Inc.">Health Partners Dental Access, Inc.</option>
              <option value="Getwell Health Systems">Getwell Health Systems</option>
              <option value="GENERAL">GENERAL</option>
            </select>
            {errors.companyName && touched.companyName && (
              <div style={{ color: "#f44336", fontSize: 12, marginTop: 4 }}>
                {errors.companyName}
              </div>
            )}
          
          </Stack>
        
          </Grid>

          {/* Mobile Number */}
          <Grid
            size={{
              xs: 12,
              sm: 12,
              md: 12,
              lg: 12,
              xl: 12,
            }}
            item
            sx={{
              padding: "0 10px",
              // backgroundColor: "cadetblue"
            }}
          >


        {/* Patient Name */}
        <Stack
          direction="column"
          width="100%">
            <label style={{ 
              fontSize: "14px", 
              fontWeight: "600", 
              color: "#333",
              marginBottom: "4px"
            }}>
              Mobile Number <span style={{ color : "red"}}>*</span>
            </label>
            <input 
              placeholder="Enter mobile number"
              type="text"
              required
              maxLength="11"
              value={contactNumber}
              onChange={(e) => handleFieldChange('contactNumber', e.target.value)}
              style={{
                padding: "12px 16px",
                border: errors.contactNumber && touched.contactNumber ? "1px solid #f44336" : "1px solid #ddd",
                borderRadius: "8px",
                fontSize: "14px",
                backgroundColor: "#fff",
                transition: "border-color 0.2s ease",
                outline: "none"
              }}
              onFocus={(e) => e.target.style.borderColor = "#007bff"}
              onBlur={(e) => {
                e.target.style.borderColor = errors.contactNumber && touched.contactNumber ? "#f44336" : "#ddd";
                handleBlur('contactNumber');
              }}
            />
            {errors.contactNumber && touched.contactNumber && (
              <div style={{ color: "#f44336", fontSize: 12, marginTop: 4 }}>
                {errors.contactNumber}
              </div>
            )}
          
          </Stack>
        
          </Grid>

          {/* HMO Card */}
          <Grid
            size={{
              xs: 12,
              sm: 12,
              md: 12,
              lg: 12,
              xl: 12,
            }}
            item
            sx={{
              padding: "0 10px",
              // backgroundColor: "cadetblue"
            }}
          >


        {/* Patient Name */}
        <Stack
          direction="column"
          width="100%">
            <label style={{ 
              fontSize: "14px", 
              fontWeight: "600", 
              color: "#333",
              marginBottom: "4px"
            }}>
              HMO Card <span style={{ color : "red"}}>*</span>
            </label>
            <input 
              placeholder="Enter HMO card number"
              type="text"
              required
              value={hmoCard}
              maxLength="50"
              onChange={(e) => handleFieldChange('hmoCard', e.target.value)}
              style={{
                padding: "12px 16px",
                border: errors.hmoCard && touched.hmoCard ? "1px solid #f44336" : "1px solid #ddd",
                borderRadius: "8px",
                fontSize: "14px",
                backgroundColor: "#fff",
                transition: "border-color 0.2s ease",
                outline: "none"
              }}
              onFocus={(e) => e.target.style.borderColor = "#007bff"}
              onBlur={(e) => {
                e.target.style.borderColor = errors.hmoCard && touched.hmoCard ? "#f44336" : "#ddd";
                handleBlur('hmoCard');
              }}
            />
            {errors.hmoCard && touched.hmoCard && (
              <div style={{ color: "#f44336", fontSize: 12, marginTop: 4 }}>
                {errors.hmoCard}
              </div>
            )}
          
          
          </Stack>
        
          </Grid>

          {/* HMO Account Number */}
          <Grid
            size={{
              xs: 12,
              sm: 12,
              md: 12,
              lg: 12,
              xl: 12,
            }}
            item
            sx={{
              padding: "0 10px",
              // backgroundColor: "cadetblue"
            }}
          >


        {/* Patient Name */}
        <Stack
          direction="column"
          width="100%">
            <label style={{ 
              fontSize: "14px", 
              fontWeight: "600", 
              color: "#333",
              marginBottom: "4px"
            }}>
              HMO Account Number <span style={{ color : "red"}}>*</span>
            </label>
            <input 
              placeholder="Enter HMO account number"
              type="text"
              required
              value={hmoAccountNumber}
              maxLength="50"
              onChange={(e) => handleFieldChange('hmoAccountNumber', e.target.value)}
                          style={{
              padding: "12px 16px",
              border: errors.hmoAccountNumber && touched.hmoAccountNumber ? "1px solid #f44336" : "1px solid #ddd",
              borderRadius: "8px",
              fontSize: "14px",
              backgroundColor: "#fff",
              transition: "border-color 0.2s ease",
              outline: "none"
            }}
            onFocus={(e) => e.target.style.borderColor = "#007bff"}
            onBlur={(e) => {
              e.target.style.borderColor = errors.hmoAccountNumber && touched.hmoAccountNumber ? "#f44336" : "#ddd";
              handleBlur('hmoAccountNumber');
            }}
            />
            {errors.hmoAccountNumber && touched.hmoAccountNumber && (
              <div style={{ color: "#f44336", fontSize: 12, marginTop: 4 }}>
                {errors.hmoAccountNumber}
              </div>
            )}
          
          
          
          </Stack>
        
          </Grid>

          {/* Valid Government ID */}
          <Grid
            size={{
              xs: 12,
              sm: 12,
              md: 12,
              lg: 12,
              xl: 12,
            }}
            item
            sx={{
              padding: "0 10px",
              // backgroundColor: "cadetblue"
            }}
          >


        {/* Patient Name */}
        <Stack
          direction="column"
          width="100%">
            <label style={{ 
              fontSize: "14px", 
              fontWeight: "600", 
              color: "#333",
              marginBottom: "4px"
            }}>
              Government ID Type <span style={{ color : "red"}}>*</span>
            </label>
            <select
              required
              value={validGovernmentID}
              onChange={(e) => handleFieldChange('validGovernmentID', e.target.value)}
              style={{
                padding: "12px 16px",
                border: errors.validGovernmentID && touched.validGovernmentID ? "1px solid #f44336" : "1px solid #ddd",
                borderRadius: "8px",
                fontSize: "14px",
                backgroundColor: "#fff",
                transition: "border-color 0.2s ease",
                outline: "none",
                cursor: "pointer"
              }}
              onFocus={(e) => e.target.style.borderColor = "#007bff"}
              onBlur={(e) => {
                e.target.style.borderColor = errors.validGovernmentID && touched.validGovernmentID ? "#f44336" : "#ddd";
                handleBlur('validGovernmentID');
              }}
            >
              <option value="">Select government ID type</option>
              <option value="Philippine Identification Card (PhilSys ID / National ID)">Philippine Identification Card (PhilSys ID / National ID)</option>
              <option value="Philippine Passport">Philippine Passport</option>
              <option value="Driver's License (LTO)">Driver's License (LTO)</option>
              <option value="Unified Multi-Purpose Identification Card (UMID)">Unified Multi-Purpose Identification Card (UMID)</option>
              <option value="Social Security System Identification Card (SSS ID)">Social Security System Identification Card (SSS ID)</option>
              <option value="Government Service Insurance System Electronic Card (GSIS eCard / GSIS ID)">Government Service Insurance System Electronic Card (GSIS eCard / GSIS ID)</option>
              <option value="Professional Regulation Commission Identification Card (PRC ID)">Professional Regulation Commission Identification Card (PRC ID)</option>
              <option value="Philippine Postal Identification Card (Postal ID)">Philippine Postal Identification Card (Postal ID)</option>
              <option value="Voter's Identification Card / Voter's Certification (Commission on Elections or COMELEC)">Voter's Identification Card / Voter's Certification (Commission on Elections or COMELEC)</option>
              <option value="National Bureau of Investigation Clearance (NBI Clearance)">National Bureau of Investigation Clearance (NBI Clearance)</option>
              <option value="Police Clearance Certificate">Police Clearance Certificate</option>
              <option value="Senior Citizen Identification Card (Senior Citizen ID)">Senior Citizen Identification Card (Senior Citizen ID)</option>
              <option value="Person with Disability Identification Card (PWD ID)">Person with Disability Identification Card (PWD ID)</option>
              <option value="Overseas Workers Welfare Administration Identification Card (OWWA ID / OWWA eCard)">Overseas Workers Welfare Administration Identification Card (OWWA ID / OWWA eCard)</option>
              <option value="Overseas Filipino Worker Identification Card (OFW ID)">Overseas Filipino Worker Identification Card (OFW ID)</option>
              <option value="Seafarer's Identification and Record Book (SIRB / Seaman's Book)">Seafarer's Identification and Record Book (SIRB / Seaman's Book)</option>
              <option value="Alien Certificate of Registration Identity Card (ACR I-Card)">Alien Certificate of Registration Identity Card (ACR I-Card)</option>
              <option value="Firearms License Identification Card (Philippine National Police or PNP Firearms ID)">Firearms License Identification Card (Philippine National Police or PNP Firearms ID)</option>
              <option value="Armed Forces of the Philippines Identification Card (AFP ID)">Armed Forces of the Philippines Identification Card (AFP ID)</option>
              <option value="Philippine Veterans Affairs Office Identification Card (PVAO ID)">Philippine Veterans Affairs Office Identification Card (PVAO ID)</option>
              <option value="Barangay Identification Card (Barangay ID)">Barangay Identification Card (Barangay ID)</option>
              <option value="Philippine Health Insurance Corporation Identification Card (PhilHealth ID)">Philippine Health Insurance Corporation Identification Card (PhilHealth ID)</option>
              <option value="Taxpayer Identification Number Identification Card (TIN ID / BIR ID)">Taxpayer Identification Number Identification Card (TIN ID / BIR ID)</option>
              <option value="Student Identification Card (Student ID)">Student Identification Card (Student ID)</option>
              <option value="Employee Identification Card (Employee ID)">Employee Identification Card (Employee ID)</option>
            </select>
            {errors.validGovernmentID && touched.validGovernmentID && (
              <div style={{ color: "#f44336", fontSize: 12, marginTop: 4 }}>
                {errors.validGovernmentID}
              </div>
            )}
          
          
          
          
          </Stack>
        
          </Grid>

          {/* Valid Government ID Number */}
          <Grid
            size={{
              xs: 12,
              sm: 12,
              md: 12,
              lg: 12,
              xl: 12,
            }}
            item
            sx={{
              padding: "0 10px",
              // backgroundColor: "cadetblue"
            }}
          >


        {/* Patient Name */}
        <Stack
          direction="column"
          width="100%">

            <label style={{ 
              fontSize: "14px", 
              fontWeight: "600", 
              color: "#333",
              marginBottom: "4px"
            }}>
              Government ID Number <span style={{ color : "red"}}>*</span>
            </label>
            <input 
              placeholder="Enter government ID number (any format)"
              type="text"
              required
              value={validGovernmentIDNumber}
              maxLength="100"
              onChange={(e) => handleFieldChange('validGovernmentIDNumber', e.target.value)}
              style={{
                padding: "12px 16px",
                border: errors.validGovernmentIDNumber && touched.validGovernmentIDNumber ? "1px solid #f44336" : "1px solid #ddd",
                borderRadius: "8px",
                fontSize: "14px",
                backgroundColor: "#fff",
                transition: "border-color 0.2s ease",
                outline: "none"
              }}
              onFocus={(e) => e.target.style.borderColor = "#007bff"}
              onBlur={(e) => {
                e.target.style.borderColor = errors.validGovernmentIDNumber && touched.validGovernmentIDNumber ? "#f44336" : "#ddd";
                handleBlur('validGovernmentIDNumber');
              }}
            />
            {errors.validGovernmentIDNumber && touched.validGovernmentIDNumber && (
              <div style={{ color: "#f44336", fontSize: 12, marginTop: 4 }}>
                {errors.validGovernmentIDNumber}
              </div>
            )}
          
          
          
          
          
          </Stack>
        
          </Grid>

          {/* Birthdate */}
          <Grid
            size={{
              xs: 12,
              sm: 12,
              md: 12,
              lg: 12,
              xl: 12,
            }}
            item
            sx={{
              padding: "0 10px",
              // backgroundColor: "cadetblue"
            }}
          >


        {/* Patient Name */}
        <Stack
          direction="column"
          width="100%">

            <label style={{ 
              fontSize: "14px", 
              fontWeight: "600", 
              color: "#333",
              marginBottom: "4px"
            }}>
              Birthdate (MM-DD-YYYY) <span style={{ color : "red"}}>*</span>
            </label>
            <input 
              type="text"
              required
              placeholder="MM-DD-YYYY"
              value={birthdate}
              onChange={(e) => {
                let value = e.target.value;
                // Remove all non-digit characters
                value = value.replace(/\D/g, '');
                
                // Format as MM-DD-YYYY
                if (value.length >= 2) {
                  value = value.slice(0, 2) + '-' + value.slice(2);
                }
                if (value.length >= 5) {
                  value = value.slice(0, 5) + '-' + value.slice(5, 9);
                }
                
                handleFieldChange('birthdate', value);
              }}
              maxLength={10}
              style={{
                padding: "12px 16px",
                border: errors.birthdate && touched.birthdate ? "1px solid #f44336" : "1px solid #ddd",
                borderRadius: "8px",
                fontSize: "14px",
                backgroundColor: "#fff",
                transition: "border-color 0.2s ease",
                outline: "none",
                width: "calc(100% - 35px)"
              }}
              onFocus={(e) => e.target.style.borderColor = "#007bff"}
              onBlur={(e) => {
                e.target.style.borderColor = errors.birthdate && touched.birthdate ? "#f44336" : "#ddd";
                handleBlur('birthdate');
              }}
            />
            {errors.birthdate && touched.birthdate && (
              <div style={{ color: "#f44336", fontSize: 12, marginTop: 4 }}>
                {errors.birthdate}
              </div>
            )}
          
          
          
          
          </Stack>
        
          </Grid>

          {/* Birthdate */}
          <Grid
            size={{
              xs: 12,
              sm: 12,
              md: 12,
              lg: 12,
              xl: 12,
            }}
            item
            sx={{
              padding: "0 10px 20px 10px",
              // backgroundColor: "cadetblue"
            }}
          >


        {/* Patient Name */}
        <Stack
          direction="column"
          width="100%">

            <label style={{ 
              fontSize: "14px", 
              fontWeight: "600", 
              color: "#333",
              marginBottom: "4px"
            }}>
              Relationship <span style={{ color : "red"}}>*</span>
            </label>
            <select
              required
              value={relationship}
              onChange={(e) => handleFieldChange('relationship', e.target.value)}
              style={{
                padding: "12px 16px",
                border: errors.relationship && touched.relationship ? "1px solid #f44336" : "1px solid #ddd",
                borderRadius: "8px",
                fontSize: "14px",
                backgroundColor: "#fff",
                transition: "border-color 0.2s ease",
                outline: "none",
                cursor: "pointer"
              }}
              onFocus={(e) => e.target.style.borderColor = "#007bff"}
              onBlur={(e) => {
                e.target.style.borderColor = errors.relationship && touched.relationship ? "#f44336" : "#ddd";
                handleBlur('relationship');
              }}
            >
              <option value="">Select relationship</option>
              <option value="Principal">Principal</option>
              <option value="Dependent">Dependent</option>
            </select>
            {errors.relationship && touched.relationship && (
              <div style={{ color: "#f44336", fontSize: 12, marginTop: 4 }}>
                {errors.relationship}
              </div>
            )}
          
          
          
          
          
          </Stack>
        
          </Grid>
      
        </Grid>

        {/* Action Buttons */}
        {/* <div style={{ 
          display: "flex", 
          justifyContent: "center", 
          gap: "16px", 
          marginTop: "20px",
          paddingTop: "20px",
          borderTop: "1px solid #eee"
        }}> */}
          {/* <button 
            type="submit"
            disabled={isSubmitting || isGoingBack}
            style={{
              padding: "12px 32px",
              backgroundColor: (isSubmitting || isGoingBack) ? "#ccc" : "#C5A572",
              color: "white",
              border: "none",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: "600",
              cursor: (isSubmitting || isGoingBack) ? "not-allowed" : "pointer",
              transition: "background-color 0.2s ease",
              minWidth: "120px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px"
            }}
            onMouseEnter={(e) => {
              if (!isSubmitting && !isGoingBack) {
                e.target.style.backgroundColor = "#B8955A";
              }
            }}
            onMouseLeave={(e) => {
              if (!isSubmitting && !isGoingBack) {
                e.target.style.backgroundColor = "#C5A572";
              }
            }}
          >
            {isSubmitting ? (
              <>
                <div style={{
                  width: "16px",
                  height: "16px",
                  border: "2px solid #ffffff",
                  borderTop: "2px solid transparent",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite"
                }}></div>
                <span>Submitting...</span>
              </>
            ) : (
              "Submit"
            )}
          </button> */}
          {/* <button 
            type="button" 
            onClick={handleGoBack}
            disabled={isSubmitting || isGoingBack}
            style={{
              padding: "12px 32px",
              backgroundColor: (isSubmitting || isGoingBack) ? "#f5f5f5" : "transparent",
              color: (isSubmitting || isGoingBack) ? "#999" : "#C5A572",
              border: `2px solid ${(isSubmitting || isGoingBack) ? "#ddd" : "#C5A572"}`,
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: "600",
              cursor: (isSubmitting || isGoingBack) ? "not-allowed" : "pointer",
              transition: "all 0.2s ease",
              minWidth: "120px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px"
            }}
            onMouseEnter={(e) => {
              if (!isSubmitting && !isGoingBack) {
                e.target.style.backgroundColor = "#C5A572";
                e.target.style.color = "#FDF9F3";
              }
            }}
            onMouseLeave={(e) => {
              if (!isSubmitting && !isGoingBack) {
                e.target.style.backgroundColor = "transparent";
                e.target.style.color = "#C5A572";
              }
            }}
          >
            {isGoingBack ? (
              <>
                <div style={{
                  width: "16px",
                  height: "16px",
                  border: "2px solid #C5A572",
                  borderTop: "2px solid transparent",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite"
                }}></div>
                <span>Going Back...</span>
              </>
            ) : (
              "Go Back"
            )}
          </button> */}
        {/* </div> */}
      
              {/* <PatientInsuranceForm
                onSubmit={handleInsuranceFormSubmit}
                onClose={() => {
                  setShowInsuranceForm(false);
                  setShowInsuranceModal(true);
                }}
              /> */}
              </Box>

              <Stack
                direction="row"
                spacing={2}>

              {/* Agreement Checkbox */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  width: "100%",
                  padding: '16px',
                  background: '#f0f0f0',
                  borderRadius: '8px',
                  border: '1px solid #ddd',
                }}
              >
                <input
                  type="checkbox"
                  id="insuranceAgreement"
                  checked={insuranceAgreement}
                  onChange={(e) => {
                    if (insuranceNone === true) {
                      setInsuranceAgreement(e.target.checked);
                      setInsuranceNone(false);
                      handleTooltipConfirmClose(false);
                    } else {
                      setInsuranceAgreement(e.target.checked);
                    }
                  }}
                  style={{
                    width: '20px',
                    height: '20px',
                    cursor: 'pointer',
                  }}
                />
                <label
                  htmlFor="insuranceAgreement"
                  style={{
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: '#2C251E',
                    fontWeight: '500',
                  }}
                >
                  I have insurance
                </label>
              </Box>

              {/* Agreement Checkbox */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  width: "100%",
                  padding: '16px',
                  background: '#f0f0f0',
                  borderRadius: '8px',
                  border: '1px solid #ddd',
                }}
              >
                <input
                  type="checkbox"
                  id="insuranceNone"
                  checked={insuranceNone}
                  onChange={(e) => {
                    if (insuranceAgreement === true) {
                      setInsuranceNone(e.target.checked);
                      setInsuranceAgreement(false)
                    } else {
                      setInsuranceNone(e.target.checked);
                      handleTooltipConfirmClose(false);
                    }
                  }}
                  style={{
                    width: '20px',
                    height: '20px',
                    cursor: 'pointer',
                  }}
                />
                <label
                  htmlFor="insuranceNone"
                  style={{
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: '#2C251E',
                    fontWeight: '500',
                  }}
                >
                  I don't have insurance.
                </label>
              </Box>
              </Stack>
            </>
          
        </>  
      ) : (
        <>
          {/* Terms Content */}
          <Box
            sx={{
              marginTop: '20px',
              marginBottom: '20px',
              maxHeight: '300px',
              overflowY: 'auto',
              background: '#fff',
              border: '2px solid #e0d6c3',
              borderRadius: '12px',
              padding: '24px',
              fontSize: '15px',
              color: '#333',
              lineHeight: '1.6',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

              <Stack
                direction="column"
                spacing={4}>

                {/* 1 */}
                <Stack
                  direction="column"
                  spacing={1}>
                  <Text
                    text="1. Appointments & Cancellations"
                    variant="termsTitle"/>
                  <Text
                    text="Please cancel or reschedule at least 24 hours before your appointment."
                    variant="termsContent"/>
                </Stack>

                {/* 2 */}
                <Stack
                  direction="column"
                  spacing={1}>
                  <Text
                    text="2. Appointment Delays"
                    variant="termsTitle"/>
                    <Stack
                      direction="column"
                      spacing={1}>

                      <Text
                        text="2.1 You agree to wait or reschedule if the previous patient's appointment takes longer than expected due to unforeseen delays or procedures that require additional time."
                        variant="termsContent"/>

                      <Text
                        text="2.2 If you are in a hurry, we recommend rescheduling your appointment on a different day."
                        variant="termsImportant"/>

                    </Stack>
                </Stack>

                {/* 3 */}
                <Stack
                  direction="column"
                  spacing={1}>
                  <Text
                    text="3. Treatment Consent"
                    variant="termsTitle"/>
                  <Text
                    text="You consent to the proposed dental procedures and authorize our dental team to make necessary adjustments during treatment in your best interest."
                    variant="termsContent"/>
                </Stack>

                {/* 4 */}
                <Stack
                  direction="column"
                  spacing={1}>
                  <Text
                    text="4. Payments"
                    variant="termsTitle"/>
                  <Text
                    text="All payments must be settled in the clinic, either in cash or via cashless methods. You are responsible for any fees not covered by your insurance provider."
                    variant="termsContent"/>
                </Stack>

                {/* 5 */}
                <Stack
                  direction="column"
                  spacing={1}>
                  <Text
                    text="5. Post-Treatment Care"
                    variant="termsTitle"/>
                  <Text
                    text="Follow all aftercare instructions provided. The clinic is not liable for complications resulting from failure to follow them."
                    variant="termsContent"/>
                </Stack>

                {/* 6 */}
                <Stack
                  direction="column"
                  spacing={1}>
                  <Text
                    text="6. Limitation of Liability"
                    variant="termsTitle"/>
                  <Text
                    text="The clinic is not responsible for issues arising from undisclosed medical conditions, failure to follow instructions, or factors beyond our control."
                    variant="termsContent"/>
                </Stack>

                {/* 7 */}
                <Stack
                  direction="column"
                  spacing={1}>
                  <Text
                    text="7. Privacy"
                    variant="termsTitle"/>
                  <Text
                    text="Your personal and medical information will be handled in accordance with our Privacy Policy."
                    variant="termsContent"/>
                </Stack>

              </Stack>
            </Box>
          </Box>

          {/* Agreement Checkbox */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '15px 20px',
              background: '#f0f0f0',
              borderRadius: '8px',
              border: '1px solid #ddd',
            }}
          >
            <input
              type="checkbox"
              id="termsAgreement"
              checked={termsAgreement}
              onChange={(e) => setTermsAgreement(e.target.checked)}
              style={{
                width: '20px',
                height: '20px',
                cursor: 'pointer',
              }}
            />
            <label
              htmlFor="termsAgreement"
              style={{
                cursor: 'pointer',
                fontSize: '14px',
                color: '#2C251E',
                fontWeight: '600',
              }}
            >
              I have read, understood, and agree to the Terms of Service Agreement above
            </label>
          </Box>
        </>
      )}
      </Box>

    </Box>

  
      <MobileStepper
        variant={null}
        // steps={maxSteps}
        position="static"
        sx={{
          mt: "5px"
        }}
        // activeStep={activeStep}
        nextButton={
          <>
            <Tooltip
              title={tooltipContentConfirm}
              placement="top-end"
              arrow
              open={showTooltipConfirm}
              onClose={handleTooltipConfirmClose}
              disableFocusListener
              disableHoverListener
              disableTouchListener
              slotProps={{
              popper: {
                sx: {
                  zIndex: 9999999, // ensures tooltip appears above modal
                }
              }
            }}
            >
          <Button
            size="small"
            sx={{
              width : "100%",
              backgroundColor: "#C5A572",
              opacity: 
                activeStep === 1 && !insuranceAgreement && !insuranceNone ? .5 : 
                activeStep === 2 && !termsAgreement ? .5 :
                1,
              padding: "10px 30px",
              marginLeft: "10px",
              color: "#FDF9F3",
              borderRadius: "8px",
              textTransform: "none",
              ":hover": {
                backgroundColor: "#8F7049",
              }
            }}
            onClick={
              async () => {
                      if (activeStep === 0) {
                        const result = await handleAppointmentSubmit();
                        if (result) {
                          handleNext();
                        }
                      } else if (activeStep === 1) {
                        if (insuranceAgreement) {
                          // If form is already shown, validate before proceeding
                          if (showInsuranceFormModal === true) {
                            const isValid = await handleSubmit();
                            // Only proceed to next step if validation passed
                            if (isValid) {
                              handleNext();
                            }
                          } else {
                            // Show the form first
                            setShowInsuranceFormModal(true);
                          }
                        } else if (insuranceNone) {
                          // User selected "I don't have insurance", proceed to terms
                          handleNext();
                        } else {
                          setShowTooltipConfirm(true);
                          if (showTooltipConfirm) {
                            handleNext();
                            setShowTooltipConfirm(false);
                          }
                        }
                      } else {
                        handleTermsAgreement();
                        setShowBookingSummary(false); 
                        setActiveStep(0);
                      }
                    }}
            disabled={
              activeStep === 1 && !insuranceAgreement && !insuranceNone ? true : 
              activeStep === 2 && !termsAgreement ? true :
              false
            }
          >
            <Typography
              variant="bookModal"
              >
                {
                activeStep === maxSteps - 1 ? "Done" : 
                activeStep === 1 && insuranceAgreement === true ? "I Have Insurance" :
                activeStep === 1 && showTooltipConfirm === true ? "Yes, I am Sure" :
                activeStep === 1 && insuranceNone === true ? "I Don't Have Insurance" :
                "Continue"
                  }
            </Typography>
            <Icon
              src={require("../../assets/icons/next_thick.png")}
              size="small-no-margin"
            />
          </Button>
          </Tooltip>
          </>
        }
        backButton={
          <Button size="small" onClick={() => {
            if (activeStep === 1) {
              handleTooltipConfirmClose()
              handleBack();
              setShowInsuranceFormModal(false);
              setInsuranceNone(false)
              setInsuranceAgreement(false);
            } else {
              handleBack();
            }
          }} disabled={activeStep === 0} 
            sx={{
              display: activeStep === 0 ? "none" : "flex",
              width : "40%",
              backgroundColor: "transparent",
              border: "2px solid #C5A572",
              padding: "8px 30px",
              marginRight: "10px",
              color: "#C5A572",
              borderRadius: "8px",
              textTransform: "none",
              ":hover": {
                backgroundColor: "rgba(143, 112, 73, .2)"
              }
            }}
            >
            <Icon
              src={require("../../assets/icons/arrow.png")}
              size="small-no-margin"
            />
            <Typography
              variant="bookModalLight"
              >
              Back
            </Typography>
          </Button>
        }
      />


      </Modal>

      {showMedicalHistoryModal && userDetails?.id && (
        <Modal
          isOpen={showMedicalHistoryModal}
          onRequestClose={() => 
            setShowMedicalHistoryModal(false)}
          shouldCloseOnOverlayClick
          shouldCloseOnEsc
          style={{
            overlay: {
              zIndex: 1300,
              backgroundColor: "rgba(0,0,0,0.7)",
            },
            content: {
              width: "60%",
              margin: "0 auto",
              position: "relative",
              padding: "0",
              maxHeight: "calc(100vh - 100px)",
              overflow: "hidden",
              top: "20px",
              borderRadius: "16px",
              backgroundColor: "rgb(241, 241, 241)",
              display: "flex",
              flexDirection: "column",
            },
          }}
          ariaHideApp={false}
        >
          {/* Fixed Header */}
          <div
            style={{
              position: "sticky",
              top: 0,
              backgroundColor: "rgb(241, 241, 241)",
              padding: "16px",
              paddingRight: "64px",
              zIndex: 10,
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            {/* Close button - top right, circular */}
            <button
              onClick={() => setShowMedicalHistoryModal(false)}
              style={{
                position: "absolute",
                top: 16,
                right: 16,
                width: 32,
                height: 32,
                borderRadius: "50%",
                border: "2px solid rgb(197, 165, 114)",
                background: "white",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "18px",
                fontWeight: "bold",
                color: "rgb(197, 165, 114)",
                transition: "all 0.2s ease",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#f5f5f5";
                e.target.style.borderColor = "rgb(185, 153, 102)";
                e.target.style.color = "rgb(185, 153, 102)";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "white";
                e.target.style.borderColor = "rgb(197, 165, 114)";
                e.target.style.color = "rgb(197, 165, 114)";
              }}
            >
              ×
            </button>

            {/* Centered title */}
            <h3
              style={{
                textAlign: "center",
                marginBottom: "0px",
                marginTop: "0px",
                fontSize: "24px",
                fontWeight: "bold",
                color: "#3d342b",
              }}
            >
              Medical History for {userDetails?.firstName && userDetails?.lastName 
                ? `${userDetails.firstName} ${userDetails.lastName}` 
                : userDetails?.name || "Patient"}
            </h3>
          </div>

          {/* Scrollable Content */}
          <div
            style={{
              flex: 1,
              overflow: "auto",
              padding: "16px",
              paddingRight: "8px",
              paddingBottom: "16px",
              minHeight: 0,
            }}
          >
            <MedicalHistory
              ref={medicalHistoryRef}
              patientId={userDetails.id}
            />
          </div>

          {/* Fixed Footer with Save Button */}
          <div
            style={{
              flexShrink: 0,
              backgroundColor: "rgb(241, 241, 241)",
              padding: "16px",
              textAlign: "center",
              borderTop: "1px solid #e0e0e0",
            }}
          >
            <button
              onClick={async () => {
                if (medicalHistoryRef.current) {
                  await medicalHistoryRef.current.save();
                  // Close the modal after saving
                  setShowMedicalHistoryModal(false);
                }
              }}
              style={{
                background: "rgb(197, 165, 114)",
                color: "#fff",
                border: "none",
                padding: "10px 28px",
                borderRadius: "6px",
                fontSize: "1.08rem",
                fontWeight: "600",
                cursor: "pointer",
                transition: "background 0.2s",
              }}
              onMouseEnter={(e) => {
                e.target.style.background = "rgb(185, 153, 102)";
              }}
              onMouseLeave={(e) => {
                e.target.style.background = "rgb(197, 165, 114)";
              }}
            >
              Save Medical History
            </button>
          </div>
        </Modal>
      )}

      <SplashScreen
        isOpen={showCustomAlert}
      />

    </div>
  );
};

export default PatientAppointmentBooking;